import { Link } from 'react-router-dom';

/**
 * Public release notes for base.Policy.online.
 *
 * Unauthenticated — sits alongside /insurers and /brokers-directory so brokers,
 * POSPs and partners can see what changed without logging in.
 *
 * This page keeps HISTORY: every release is an entry in `RELEASES` (newest
 * first). The newest drives the hero; all of them, including older ones, render
 * below as a running record. To publish a release, prepend a new `Release`
 * object — never overwrite an existing one, or the history stops being a record.
 *
 * Security items are described by their effect, never by their mechanism. A
 * public page is the wrong place to publish exploit detail, so "records kept
 * apart between brokers" is deliberate phrasing rather than vagueness.
 */

interface Change {
  title: string;
  detail: string;
}

interface Section {
  tag: string;
  heading: string;
  blurb: string;
  changes: Change[];
}

interface Stat {
  value: string;
  label: string;
  note: string;
}

interface Release {
  /** Machine-style tag, e.g. "RELEASE / 2026.07.23". */
  tag: string;
  /** Human date, e.g. "23 July 2026". */
  date: string;
  /** Two-part hero headline: a lead line and an accented line. */
  headline: { lead: string; accent: string };
  /** One-paragraph summary of the release. */
  summary: string;
  stats: Stat[];
  sections: Section[];
  /** Optional closing note — e.g. what was investigated but needed no change. */
  footnote?: string;
}

// Newest first. Prepend new releases here; do not edit published ones.
const RELEASES: Release[] = [
  {
    tag: 'RELEASE / 2026.10.20',
    date: '20 October 2026',
    headline: {
      lead: 'Insurer desks for quotes and claims, online renewals.',
      accent: 'Commission grids and safer identity numbers.',
    },
    summary:
      'Insurers get a Quote Desk, a Claims Desk, product editing with history and an activity log. Customers can pay a renewal online from a link and find a cashless hospital. Brokers can import commission grids and see rates applied automatically. Identity numbers are masked unless someone with the right chooses to reveal them.',
    stats: [
      { value: '4', label: 'insurer tools', note: 'Quote Desk, Claims Desk, products, activity log' },
      { value: '19', label: 'database updates', note: 'existing data kept' },
      { value: '0', label: 'new tools to install', note: 'nothing to change on your side' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Insurers',
        blurb: 'Work quotes and claims in one place.',
        changes: [
          {
            title: 'Quote Desk',
            detail: 'Answer pricing requests from brokers. Overdue requests are flagged and escalated.',
          },
          {
            title: 'Claims Desk',
            detail: 'Update status, add remarks, request documents, and approve, reject or settle a claim, with a full history.',
          },
          {
            title: 'Products and activity log',
            detail: 'Edit, deactivate and revert a product with its history. Every insurer action is recorded, and administrators can export the log.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Customers',
        blurb: 'Renew and claim with less back-and-forth.',
        changes: [
          {
            title: 'Pay a renewal online',
            detail: 'Open the insurer’s payment link, pay, and get a receipt straight away.',
          },
          {
            title: 'Cashless hospitals',
            detail: 'Find a cashless hospital near you and see which documents a health claim needs.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Brokers',
        blurb: 'Commission grids and cleaner records.',
        changes: [
          {
            title: 'Commission grids',
            detail: 'Import a grid, check it in a preview and publish it. Rates are applied for you, and a past month can be recalculated and undone.',
          },
          {
            title: 'Identity numbers',
            detail: 'Aadhaar, PAN, passport and licence numbers show masked. Reveal one when you need it; each reveal is recorded.',
          },
        ],
      },
    ],
  },
  {
    tag: 'RELEASE / 2026.10.10',
    date: '10 October 2026',
    headline: {
      lead: 'Clearer targets, health policy details, broker compliance.',
      accent: 'A new sign-in for channel partners.',
    },
    summary:
      'Targets can now be set by month, quarter or financial year, and only active policies count towards them. Health policies record their insured members and group master policy. Brokers have a compliance section for their licence, key people, bank and indemnity details. Channel partners get their own portal to invite agents and follow their progress.',
    stats: [
      { value: '5', label: 'features', note: 'first phase of each' },
      { value: '11', label: 'database updates', note: 'all additive, existing data kept' },
      { value: '0', label: 'new tools to install', note: 'nothing to change on your side' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Targets',
        blurb: 'Set goals the way your business runs.',
        changes: [
          {
            title: 'Month, quarter and financial-year targets',
            detail:
              'Set a target by premium or number of policies, for one agent, every agent, or a channel, by insurer or product line. Quarters and years follow April to March.',
          },
          {
            title: 'Only live policies count',
            detail:
              'Cancelled, lapsed and expired policies no longer count towards a target, so progress on existing targets may now look lower than before. Your existing targets and their amounts are unchanged.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Health policies',
        blurb: 'The details a health policy needs.',
        changes: [
          {
            title: 'Insured members and group master policy',
            detail:
              'Individual, family floater and group health policies record who is covered, and group policies record the master policy number and name. Existing health policies can still be edited as before.',
          },
          {
            title: 'Nominee on other non-motor policies',
            detail: 'Other non-motor policy types can now record a nominee when you add or edit them.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Brokers',
        blurb: 'Compliance in one place, and a safer way to look at a partner’s view.',
        changes: [
          {
            title: 'Compliance and KYC',
            detail:
              'Broker administrators can record licence categories, key people, financial standing, indemnity cover, bank accounts and documents, and submit them for review. Sensitive details are stored securely and shown masked.',
          },
          {
            title: 'Preview as partner',
            detail:
              'Authorised broker staff can open a read-only view of a channel to see what it sees. Nothing can be changed from it.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Channel partners',
        blurb: 'A portal for the people who bring in agents.',
        changes: [
          {
            title: 'Partner portal',
            detail:
              'A broker can give a partner channel a sign-in. Partners invite prospective agents by link and see their own agents, production and targets. Each invited agent is approved by the broker.',
          },
        ],
      },
    ],
    footnote:
      'Each of these is the first phase of a larger piece of work. Further phases, including screens for reviewing broker compliance submissions, follow in later releases.',
  },
  {
    tag: 'RELEASE / 2026.10.09',
    date: '9 October 2026',
    headline: {
      lead: 'A clearer insurer portal, safer licence checks.',
      accent: 'Eleven items from the development queue.',
    },
    summary:
      'Insurers now have their own sign-in, home page and menus, and no longer see broker-only screens. Brokers whose licence has lapsed cannot start new business until it is renewed. Finding a policy when raising a servicing request is faster and works for any policy, not just the first twenty.',
    stats: [
      { value: '11', label: 'queue items closed', note: 'fixes and small features' },
      { value: '1', label: 'database update', note: 'a small change to the list of channel types' },
      { value: '0', label: 'new tools to install', note: 'nothing to change on your side' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Brokers and quotes',
        blurb: 'Keeping new business on a valid licence.',
        changes: [
          {
            title: 'Expired licences',
            detail:
              'A broker whose licence has expired cannot create new policies, quotes or payouts until the licence date is updated. Existing policies can still be viewed and serviced. Customers on that broker\'s site are asked to contact their adviser.',
          },
          {
            title: 'Finding a policy',
            detail:
              'In a new servicing request, typing in the Policy box now searches every policy, by number, holder name, phone or vehicle, and your choice is kept if you cancel and come back.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Insurer portal',
        blurb: 'A portal that looks and behaves like an insurer\'s.',
        changes: [
          {
            title: 'Own sign-in and home',
            detail:
              'Insurers sign in at their own page, land on an insurer dashboard with their own policy, premium and claim figures, and return there after signing out.',
          },
          {
            title: 'Only what an insurer needs',
            detail:
              'Broker-only screens such as quotes, new policy, the media library, reports and commission desks are no longer shown to insurers.',
          },
          {
            title: 'Channels and team',
            detail:
              'Channels use insurer channel types, the channel form is easier to use with a keyboard or screen reader, and obviously invalid mobile numbers are refused when adding a team member.',
          },
          {
            title: 'Access Denied page',
            detail:
              'When a page is not available to you, the menu and header stay in place and the page explains why and links you back.',
          },
        ],
      },
    ],
  },
  {
    tag: 'RELEASE / 2026.10.08',
    date: '8 October 2026',
    headline: {
      lead: 'Cleaner records, safer go-lives.',
      accent: 'Twenty-eight fixes from the development queue.',
    },
    summary:
      'A broad release from the development queue. Agents can share their profile card for 30 days and quote for themselves, brokers get a warning before a licence lapses, overdue servicing requests are flagged, and a long list of wrong or missing details in leads, payouts, vehicles and locations is put right.',
    stats: [
      { value: '28', label: 'queue items closed', note: 'fixes and small features' },
      { value: '30 days', label: 'life of a card link', note: 'the shared profile card link now lasts a month' },
      { value: '7', label: 'database updates', note: 'including tidy-ups of vehicle and location lists' },
      { value: '0', label: 'new tools to install', note: 'nothing to change on your side' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Agents and quoting',
        blurb: 'What an agent sees and does when quoting for a customer.',
        changes: [
          {
            title: 'A profile card link that lasts 30 days',
            detail:
              'An agent can share their profile card with a customer, and the link now works for 30 days instead of five minutes.',
          },
          {
            title: 'Quote for yourself',
            detail:
              'An agent who is quoting for themselves can now open their own quote.',
          },
          {
            title: 'Share a customer link',
            detail:
              'A “Share customer link” button now sits beside the internal proposal page, so a quote can be sent to the customer in one step.',
          },
          {
            title: 'Get a Quote buttons go to the right place',
            detail:
              'On the public site, the Get a Quote buttons now lead into the matching product journey and capture the lead.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Brokers and policies',
        blurb: 'Fewer surprises when a broker goes live or a policy is entered.',
        changes: [
          {
            title: 'A warning before a licence lapses',
            detail:
              'The broker page now shows when a licence is close to expiry, and staff are told in advance.',
          },
          {
            title: 'No go-live without a verified admin',
            detail:
              'A broker cannot be made live until it has an active, verified Broker Admin.',
          },
          {
            title: 'Motor details on the broker policy form',
            detail:
              'The broker policy form takes optional motor details, so a motor policy can be recorded in full.',
          },
          {
            title: 'An agent’s own policies are credited to them',
            detail:
              'When an agent holds a policy themselves, it is now recorded as sold by that agent and marked as self-sourced.',
          },
          {
            title: 'No grace date before the policy expires',
            detail:
              'A grace date earlier than the policy’s expiry date is now refused.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Servicing, leads and payouts',
        blurb: 'Fewer wrong or missing details in day-to-day records.',
        changes: [
          {
            title: 'Overdue servicing requests are flagged',
            detail:
              'A request past its due date is marked in the list and the staff responsible are notified once.',
          },
          {
            title: 'Leads need a source and an estimated premium',
            detail:
              'A new lead must say where it came from and what the premium is likely to be, and its stage can be changed from the lead.',
          },
          {
            title: 'No payout period in the future',
            detail:
              'A payout period that ends in the future is now refused.',
          },
          {
            title: 'One email check everywhere',
            detail:
              'The same email check now runs wherever an address is entered, including the public lead form.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Lists and menus',
        blurb: 'The reference data behind the dropdowns.',
        changes: [
          {
            title: 'Vehicle lists',
            detail:
              'Hybrid and Petrol + CNG are added as fuel types, and misspelt or duplicated body types are corrected.',
          },
          {
            title: 'Locations',
            detail:
              'Some stray or outdated place names are removed or renamed, and Leh and Kargil now sit under Ladakh.',
          },
          {
            title: 'Menus',
            detail:
              'Country admins get a Reports group, and Support Categories moves into the Settings hub.',
          },
        ],
      },
    ],
  },
  {
    tag: 'RELEASE / 2026.10.01',
    date: '1 October 2026',
    headline: {
      lead: 'Your policies, your servicing, your report.',
      accent: 'More of the agent app, and fewer wrong names.',
    },
    summary:
      'Ten open requests from the development queue, closed in one release. An agent can now see every policy they sold in one list, follow a servicing request from start to finish, pull a sales report for any date range, and get a profile card to share. Switching from one linked account to another no longer shows the first person’s screens. An insurer’s own policy number is shown next to ours.',
    stats: [
      { value: '10', label: 'queue items closed', note: 'plus five of the eight smaller debts' },
      { value: '4', label: 'quick-quote tiles', note: 'Motor, Health, Travel and Life from Home' },
      { value: '5 min', label: 'life of a card link', note: 'the shared profile card link expires' },
      { value: '0', label: 'database changes', note: 'no existing data rewritten' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'The agent app',
        blurb: 'What an agent sees and does in the Telegram app.',
        changes: [
          {
            title: 'Every policy you sold, in one list',
            detail:
              'A Policies tab lists the policies an agent sold, newest first, with search and filters. Policies with no issue date sort last instead of first.',
          },
          {
            title: 'Four quote tiles on Home',
            detail:
              'Motor, Health, Travel and Life start a quote straight from the agent’s Home screen.',
          },
          {
            title: 'Follow a servicing request',
            detail:
              'A tracker lists the servicing requests raised for an agent’s customers, with where each one stands and what was paid.',
          },
          {
            title: 'A sales report for any dates',
            detail:
              'Pick a start and end date and see quotes, policies sold and conversion for that period. Sales are dated by the day the policy was issued, and conversion never shows above 100%.',
          },
          {
            title: 'A profile card to share',
            detail:
              'An agent can generate a card with their name and agent code to send to a customer. The link works for five minutes.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Fewer wrong screens and wrong names',
        blurb: 'Small things that made people doubt what they were looking at.',
        changes: [
          {
            title: 'Switching accounts starts clean',
            detail:
              'When someone unlinks and links a different account, nothing the first account loaded is shown to the second, even if the unlink happens while the app is still refreshing.',
          },
          {
            title: 'The insurer’s policy number, shown',
            detail:
              'Policy lists show the number the insurer issued, with our own reference beside it.',
          },
          {
            title: 'Greetings use the right name',
            detail: 'The app greets a person by the name on their own profile.',
          },
          {
            title: 'Share results per quote',
            detail:
              'When several quotes are sent together, each one reports whether it went, instead of one answer for the lot.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Insurer admins',
        blurb: 'Running an insurer’s team on the platform.',
        changes: [
          {
            title: 'Remove a team member',
            detail:
              'An insurer admin can remove someone from their team. If a login has lost its insurer link, the screen now says so rather than failing silently.',
          },
          {
            title: 'Smaller platform debts',
            detail:
              'Five of eight listed housekeeping items are closed, including one stale one-off data-repair tool that is deleted.',
          },
        ],
      },
    ],
    footnote:
      'Not in this release: the profile card needs a browser on our server to draw it, so it is confirmed only after the first live card is made. Three of the eight housekeeping items are deliberately left: one needs a database change of its own, one needs a decision on who may edit insurance products, and one could not be reproduced. The ticket system for brokers and the quote-status split are still planned for later.',
  },
  {
    tag: 'RELEASE / 2026.09.29 · B',
    date: '29 September 2026',
    headline: {
      lead: 'The agent app grows up.',
      accent: 'Share quotes, see every policy, manage your contacts.',
    },
    summary:
      'Thirteen requests from agents using the Telegram app, all in this release. An agent can now send a quote by WhatsApp, Telegram, a link or a PDF, pick several quotes and send them together, open any policy they sold and download its documents, see their full profile, leads by type, claims, payouts against target, and their renewals. The Back button no longer throws away what you typed. Quotes now stay with the right person, and a registration number in any usual spelling is accepted.',
    stats: [
      { value: '13', label: 'requests from agents', note: 'every one addressed in this release' },
      { value: '4', label: 'ways to send a quote', note: 'WhatsApp, Telegram, a link, or a PDF' },
      { value: '20', label: 'quotes in one message', note: 'pick several, send once' },
      { value: '0', label: 'database changes', note: 'no existing data rewritten' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Quotes',
        blurb: 'Sending them, finding them, and keeping them with the right person.',
        changes: [
          {
            title: 'Send a quote four ways',
            detail:
              'On the prices screen an agent sees Telegram, WhatsApp, Copy link and PDF. The link opens the same quote the customer would get, so no second quote is made and no vehicle lookup is bought again. The PDF lists each insurer’s premium, cover amount and add-ons, with the advisor’s name and Agent Code, and says the prices are indicative.',
          },
          {
            title: 'Pick several quotes and send them together',
            detail:
              'In My quotes an agent can tap Select (or press and hold a row), tick several, and send one message that lists each quote with its cheapest price and link. Quotes that are not theirs are skipped and named, and the rest still go.',
          },
          {
            title: 'Quotes stay with the right person',
            detail:
              'A quote made before logging in now stays with the person who made it. An agent who links their account no longer sees, opens or buys quotes that belonged to a customer or a guest on the same Telegram account.',
          },
          {
            title: 'Issued quotes move to Completed',
            detail:
              'When a policy is issued from a quote, the quote is marked Completed, leaves the active list, and shows “Policy issued” with the policy number and a link. A quote whose policy failed to issue is left as it was.',
          },
          {
            title: 'Back keeps what you typed',
            detail:
              'The Back button in the quote steps now goes one question back with every answer kept. From the prices it returns to the questions, and it does not start a second quote or buy a second lookup.',
          },
          {
            title: 'Registration numbers in any usual spelling',
            detail:
              'MH 12 AB 1234, mh12ab1234 and MH-12-AB-1234 are all accepted, and so are Bharat (BH) series plates, which the app used to let through and then reject. The website, the app and the chat now share one rule.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Book, contacts and policies',
        blurb: 'What an agent sold, and who they know.',
        changes: [
          {
            title: 'Correct policy counts',
            detail:
              'A client with policies showed “0 policies”, on the website’s contacts list as well as in the app. The count is now worked out live, and for an agent it counts only the policies they can open themselves.',
          },
          {
            title: 'Open a policy and its documents',
            detail:
              'Tapping a policy in a client’s page opens it: number, insurer, product, dates, premium, status, vehicle and its documents, each with a download that works inside Telegram.',
          },
          {
            title: 'Contacts from your phone, by file',
            detail:
              'Telegram does not let any app read a phone’s contacts, so an agent exports their contacts from the phone’s Contacts app as a .vcf file and imports it in the app. Numbers already held are not added twice. Contacts can be downloaded back as a .vcf file to import on a phone.',
          },
          {
            title: 'Contacts with a policy shown in green',
            detail:
              'The Contacts screen shows what share of an agent’s contacts hold a policy sold through them, and marks those contacts green with “Policy issued”. Each contact is counted once, however many policies they have.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Profile, leads, claims, reports and renewals',
        blurb: 'The rest of the agent’s day.',
        changes: [
          {
            title: 'A full profile',
            detail:
              'Photo, name, Agent Code, personal details, certificates and documents, bank details (the account number is shown only as its last four digits), and the training journey, all from the same records as the website. Anything not yet added says so.',
          },
          {
            title: 'Leads by insurance type',
            detail:
              'Leads carry a type badge and can be filtered by Motor, Health, Travel, Life or Others, with a count on each. New leads can be of any of the eleven types.',
          },
          {
            title: 'Claims, read only',
            detail:
              'An agent can see the claims on the policies they sold and follow their status and timeline. Agents can no longer raise or change a claim; that stays with the customer and the broker’s team.',
          },
          {
            title: 'Reports: payouts and targets',
            detail:
              'Payouts earned, paid and pending by month, and progress against the agent’s targets, for this month, the last three months or the financial year. If no target has been set, the screen says so instead of showing a made-up one.',
          },
          {
            title: 'Renewals on the home screen',
            detail:
              'Home shows the two nearest renewals due in 30 days and a “View all” link to the full list, with 30 and 60 day tabs. Policies that have lapsed but are still in their grace period are listed separately.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Privacy and safety',
        blurb: 'Tightened while building this release.',
        changes: [
          {
            title: 'Agents’ identity documents',
            detail:
              'Certificates and identity documents that agents upload are now available only to the agent, the broker’s own team and platform staff. One kind of login could reach them before, on the website and now through the app.',
          },
          {
            title: 'Quotes and policies kept apart between brokers',
            detail:
              'A policy can no longer be attached to another broker’s quote, and one broker’s policy can no longer mark another broker’s quote as completed.',
          },
          {
            title: 'Quote history stays honest',
            detail:
              'A quote that reaches a policy before its identity check was approved is no longer marked as approved on the broker’s behalf; it stays in the review queue.',
          },
        ],
      },
    ],
    footnote:
      'Not yet done or not yet proven: the app cannot read a phone’s address book, so contacts move by .vcf file rather than syncing by themselves; sending a .vcf straight to the bot in chat is not built; the “contacts we hold that are not on your phone” file is empty until contacts can be added on the server; agents cannot yet open the documents of a claim; the share sheet, downloads and press-and-hold selection have been tested on screen and with test doubles but not yet on a real phone; and issuing a real policy from a quote through an insurer has been tested with stand-ins, not with a live policy. Prices in the PDF are indicative and shown in whole rupees.',
  },
  {
    tag: 'RELEASE / 2026.09.29',
    date: '29 September 2026',
    headline: {
      lead: 'A full insurance app inside Telegram.',
      accent: 'Tap “Open app” instead of typing to a bot.',
    },
    summary:
      'Each broker’s Telegram bot can now open an app: a proper screen with the broker’s own logo and colours, in Telegram’s light or dark theme. Anyone can get a quote in it without logging in, and buy after one tap to share their phone number. Customers who link their account see their policies, renewals, claims and servicing requests, and can raise a claim with photos. Agents see their XP, renewals due, clients, leads and payouts, and can quote for a client. The chat now sends updates with the broker’s own stickers and an “Open in app” button. The app is being tried on one bot first and will be switched on for other brokers after that.',
    stats: [
      { value: '4', label: 'tabs in the app', note: 'home, quotes, policies (or book, for agents) and profile' },
      { value: '0', label: 'logins needed to get a quote', note: 'buying asks for one tap to share a phone number' },
      { value: '8', label: 'stickers per broker', note: 'drawn from the broker’s own logo' },
      { value: '1', label: 'database change', note: 'adds new settings and records; no existing data rewritten' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'The app',
        blurb: 'Everything the chat did, on a real screen, plus what only the website could do.',
        changes: [
          {
            title: 'Quotes in a few taps',
            detail:
              'Pick motor, health, travel or life. For motor, type the registration number: the vehicle is looked up and shown as a card, you confirm the variant, answer claim and cover questions with a tap, and prices appear as each insurer answers, cheapest first, with any insurer that declined listed and the reason given. Choosing a price opens the details and payment form in the same app.',
          },
          {
            title: 'Your policies, renewals, claims and requests',
            detail:
              'A customer who links their account sees their policies and downloads their documents, sees renewals that are due, raises a claim with photos and follows it, and raises a servicing request. Lists load in pages, so a large book is never cut short.',
          },
          {
            title: 'Built for agents too',
            detail:
              'An agent’s home shows their XP and policies sold, renewals due in the next 30 days, and shortcuts to leads, a new lead, payouts, XP history and cover preferences. Agents can quote for one of their own clients from the client’s page.',
          },
          {
            title: 'Linking an account',
            detail:
              'Customers and agents link once, from the Profile tab, with their usual username and password, and their two-step code if they use one. A customer with no password can link with the phone number they shared in Telegram, which opens their policies, renewals, claims and requests only. Anyone can unlink at any time, and a link stops working straight away if the account is suspended or the password is changed.',
          },
          {
            title: 'The broker’s own look',
            detail:
              'The app shows the broker’s logo, name and colours, and follows Telegram’s light or dark setting. Each broker sees only their own customers, even when one person is a customer of two brokers.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'The chat',
        blurb: 'Updates and stickers stay in the chat; the work moves to the app.',
        changes: [
          {
            title: 'An “Open app” button next to the message box',
            detail:
              'When the app is switched on for a bot, the menu button beside the message box opens it, and the welcome message offers it too. Typing to the bot still works.',
          },
          {
            title: 'Stickers from the broker’s logo',
            detail:
              'Welcome, prices ready, payment received, policy issued, claim registered and renewal due now arrive with a sticker drawn from the broker’s own logo or icon, and an “Open in app” button that goes straight to the right screen.',
          },
          {
            title: 'Registration lookups in the chat',
            detail:
              'In the chat, typing a registration number now looks the vehicle up and fills in what is known, then offers one card: no claim, I claimed, or change something. A paid lookup is never bought twice for the same number within a day.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Privacy and safety',
        blurb: 'Tightened while building the app, in the chat as well.',
        changes: [
          {
            title: 'Another owner’s details are not filled in',
            detail:
              'When someone quotes for a registration that is not shown to be theirs, the details form no longer fills in the owner’s name, the previous insurer, the previous policy number or the engine and chassis numbers. The person types them in. This applies in the chat and in the app.',
          },
          {
            title: 'Limits on vehicle lookups',
            detail:
              'Vehicle lookups from the app are limited per person and per bot, as they already were on the website and in the chat.',
          },
          {
            title: 'Renewals still in grace stay on the list',
            detail:
              'A lapsed policy that is still inside its grace period now stays on the renewals list, for brokers and for customers, instead of dropping off after 30 days.',
          },
        ],
      },
    ],
    footnote:
      'The app is switched on only for Base.Policy.Online’s test bot for now; other bots keep the chat as it was until the app is switched on for them by the platform. It has been tested with automated tests and on screen at phone size, not yet on a real phone inside Telegram, so document downloads and the phone-number step will be confirmed on real Android and iPhone first. Brokers cannot yet upload a sticker icon from a screen; it is set by the platform for now.',
  },
  {
    tag: 'RELEASE / 2026.09.27',
    date: '27 September 2026',
    headline: {
      lead: 'Compare insurers side by side, cheapest first.',
      accent: 'And get a quote from a Telegram chat.',
    },
    summary:
      'Customers can now get a motor quote on base.policy.online and on every broker’s own quote page, and see live prices from more than one insurer on a single page. The page opens straight away and fills in as each insurer answers. It lists the cheapest price first and says which insurers declined. Customers can change the IDV, the no-claim bonus and add-ons and see the prices update. Each broker can also connect their own Telegram bot, so customers and agents can get a quote in a chat: prices arrive as a designed card and the details go into a secure form. Agents see XP points in place of commission. AI assistants can now use the same quote journey through a personal key.',
    stats: [
      { value: '2', label: 'insurers pricing live', note: 'GoDigit and IndusInd General (formerly Reliance General); two more are built and waiting' },
      { value: '4', label: 'insurance lines in Telegram', note: 'motor, health, travel and life' },
      { value: '14', label: 'database changes', note: 'all add new records or settings; no existing data rewritten' },
      { value: '1 tap', label: 'to verify a phone number in Telegram', note: 'asked once, only when it is needed' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Online motor quotes',
        blurb: 'Several insurers on one page, and a quote the customer can shape.',
        changes: [
          {
            title: 'A quote page for every broker',
            detail:
              'base.policy.online/quote, each broker’s public quote page and the “Get a quote” button on broker microsites all lead to the same journey. The customer enters a registration number, the vehicle is looked up and matched to the right variant, and prices come back for comprehensive and third-party-only cover.',
          },
          {
            title: 'IndusInd General alongside GoDigit',
            detail:
              'IndusInd General (the insurer formerly called Reliance General) now prices in the same journey as GoDigit. It sits on the existing IndusInd General insurer record, so policies are not split between two names.',
          },
          {
            title: 'Prices appear as they arrive',
            detail:
              'The page no longer waits for the slowest insurer. It opens at once, and each insurer’s price appears as soon as that insurer answers.',
          },
          {
            title: 'Cheapest first, and who declined',
            detail:
              'Prices are sorted from lowest to highest. An insurer that declined the vehicle is listed as declined rather than left out, so a missing insurer is never a mystery.',
          },
          {
            title: 'IDV, no-claim bonus and add-ons',
            detail:
              'Each quote shows the IDV used, and the customer can change it. They can also say whether they claimed last year and pick add-ons, with a grid showing which insurer offers which add-on. The prices update after each change.',
          },
          {
            title: 'Ask first, or quote first: the broker decides',
            detail:
              'In Broker Settings, a broker chooses “Quote first, then let the customer customise” (the default) or “Ask before quoting”, which asks about claims, no-claim bonus, add-ons and IDV before showing the first prices.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Insurers as plug-ins',
        blurb: 'A new insurer plugs in without rebuilding the journey.',
        changes: [
          {
            title: 'An Insurer Plug-ins tab for Country Admin',
            detail:
              'Country Admin → Integrations → Insurer Plug-ins switches each insurer on or off, for each insurance line and for test or live. When an insurer is added, it appears here and joins every journey that sells its lines. There is nothing else to wire up.',
          },
          {
            title: 'Credential forms that match the insurer',
            detail:
              'The form where a broker enters their insurer credentials is now built from what that insurer actually needs, so it asks for the right fields.',
          },
          {
            title: 'HDFC ERGO and Universal Sompo built, not yet switched on',
            detail:
              'Both insurers’ motor quoting is built, with their vehicle and location code lists. They stay switched off until each insurer finishes its side of the setup.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Telegram, a bot for each broker',
        blurb: 'The whole quote journey in a chat, under the broker’s own name.',
        changes: [
          {
            title: 'Connect your own bot',
            detail:
              'Broker Settings → Messaging channels takes the token from Telegram’s BotFather. From then on the bot answers in that broker’s name. WhatsApp has a placeholder for every broker, ready for when it is enabled.',
          },
          {
            title: 'Quotes as pictures',
            detail:
              'Prices arrive as a designed quote card rather than a block of text. Choosing a price opens a secure form inside Telegram for the details, KYC and payment. PAN, Aadhaar, engine and chassis numbers are only ever entered in that form, never typed in the chat.',
          },
          {
            title: 'Start as a guest, verify once',
            detail:
              'Anyone can ask for a quote straight away, without giving a phone number. The bot asks for the number only when it is needed, such as before the details form. One tap on “Share my phone number” verifies it, and Telegram never asks again. An agent’s number opens the agent menu, and a guest’s earlier quotes stay attached to them.',
          },
          {
            title: 'Connect my Telegram, from the portal',
            detail:
              'Agents and customers have a “Connect my Telegram” card on their Profile. It opens the broker’s bot and links the account in one step. The link can be used once and expires after ten minutes.',
          },
          {
            title: 'Health, travel, life, My policies and reminders',
            detail:
              'Health, travel and life requests can be made in the chat and go to the broker’s advisors until an insurer is switched on for that line. Customers can see their policies in the chat and are reminded 30, 7 and 1 days before a renewal. Agents are reminded 30 and 7 days before.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Agents: XP points and cover preference',
        blurb: 'Rewards an agent can see, without showing commission.',
        changes: [
          {
            title: 'XP instead of commission',
            detail:
              'IRDAI rules keep commission off an agent’s quote screens, so agents now see XP points: what a sale would earn, and what they have earned. The broker sets how many rupees of agent payout make one XP in Broker Settings (Rupees of agent payout per 1 XP). XP is added when commission is calculated and taken back if a policy is cancelled. Customers never see XP or commission.',
          },
          {
            title: 'Comprehensive, third-party only, or both',
            detail:
              'An agent chooses which covers they want to see quoted, on their Profile or in Telegram. Their points, sales and renewals are all visible in the Telegram chat as well.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'AI assistants',
        blurb: 'The quote journey for an AI assistant, with a key for each person.',
        changes: [
          {
            title: 'Connect an AI assistant',
            detail:
              'Settings → AI assistants creates a personal key that lets an AI assistant look up vehicles, get and compare quotes, and use the agent tools for an agent’s key. Broker admins can see the keys issued in their brokerage. Every action is logged. The assistant never handles PAN, Aadhaar or payment details. Those still go through the secure form.',
          },
        ],
      },
    ],
    footnote:
      'What is not live yet, so nobody is surprised. IndusInd General prices on the insurer’s test system until its live credentials arrive, and a purchase through it waits for a manual check rather than issuing on its own. Buying a GoDigit motor policy online has been rebuilt to GoDigit’s specification but has not yet been completed end to end with a real customer. HDFC ERGO, Universal Sompo, and GoDigit health and travel are built but switched off while those insurers finish their setup. Past sales have not yet been credited with XP. That is the broker’s decision. In Telegram, the secure form opens after a price is chosen; there is not yet a permanent quote button beside the message box.',
  },
  {
    tag: 'RELEASE / 2026.09.24',
    date: '24 September 2026',
    headline: {
      lead: 'Installments that add up.',
      accent: 'And every admin action where the admin already is.',
    },
    summary:
      'The 24 September list: twenty-two items across the Country Admin, broker and agent portals, and several were not what they looked like. An installment "shown as 500 instead of 5,000" was never a display fault: the policy form had no box for the installment amount, so the premium was being divided by the number of payments. The engine and chassis numbers "missing" from the Vehicle Database were already there; almost no vehicle had ever had them filled in, and a blank record could hide a filled one. Alongside the fixes, broker admins can now issue an agent a new password, insurance company admins can build their own team, and Country Admin manages a broker’s admin without leaving the broker.',
    stats: [
      { value: '22', label: 'of 22 reported items', note: 'plus the planned retirement of the old sign-in fallback' },
      { value: '11,990+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '2', label: 'database changes', note: 'both only widen what is allowed; no data rewritten' },
      { value: '4', label: 'faults found that nobody reported', note: 'three about who can manage whose accounts, one about commission rates' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Policies and installments',
        blurb: 'The amount you mean, the amount you were paid, and a schedule that fits on the page.',
        changes: [
          {
            title: 'An Installment Amount box',
            detail:
              'Health, Life and Term policies paid in installments now have an Installment Amount on the Add and Edit Policy form. It starts as the premium you entered and you can change it. Before, there was no such box, so the schedule divided the premium by the number of payments: a 5,000 premium over ten payments became ten installments of 500. Existing schedules are not rewritten; use Rebuild on a policy to apply the amount you set.',
          },
          {
            title: 'The amount you were paid is on the schedule',
            detail:
              'When you record a payment you can change the amount, and that amount was always saved, but the schedule only ever showed the amount due. It now has a Paid column with the amount and date. Every recorded installment now also writes a receipt to Payment History, so choosing how the customer paid is now required; before, a payment with no mode never reached Payment History.',
          },
          {
            title: 'A compact installment schedule',
            detail:
              'The schedule shows twelve installments at a time with Previous and Next, opens on the page holding the next installment due, and has a Next due button to jump back to it.',
          },
          {
            title: 'More payments allowed',
            detail:
              'No. of Payments was capped at 100, which a monthly plan over nine years already exceeds. The limit is now 1,200 — a hundred years of monthly payments.',
          },
          {
            title: 'Products that match what you picked',
            detail:
              'On Add and Edit Policy, the Product list now follows the Insurance Type, Group, Subgroup and Policy Type you choose. A product that does not name a subgroup or policy type still appears, so general products are not hidden; a product already saved on a policy always stays selectable.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Country Admin',
        blurb: 'A broker’s admin managed from the broker, and insurance companies that manage their own people.',
        changes: [
          {
            title: 'Manage a broker admin on the Broker Admin tab',
            detail:
              '"Manage user" on the Brokers list now opens that broker’s Broker Admin tab instead of a page Country Admin could not return from, which is what produced the Access Denied on Back. The tab has Suspend, Block, Reactivate and Reset Password, and the admin’s name, email and phone can be edited in place. A reset password is shown once, and suspending, blocking or resetting ends that person’s saved sign-ins.',
          },
          {
            title: 'Insurance companies build their own team',
            detail:
              'Country Admin creates an insurance company’s Admin; that Admin now has a Team page to add, suspend and reset the company’s Managers and Staff. Only Country Admin can create an insurance company Admin, and a company Admin can only manage their own company’s people.',
          },
          {
            title: 'Master Mappings: a template, an import and Fuel Type',
            detail:
              'Master Mappings now has Download Template, file import (CSV or Excel, with a check-only run that lists every row it would reject) and Export, using readable codes instead of internal IDs. Fuel Type joins Previous Insurer, State, RTO, City and PIN code as a type an insurer’s own code can be set for. The list now shows the company’s name, not an ID.',
          },
          {
            title: '"Register as POSP" requests belong to the broker',
            detail:
              'Requests from agents asking to join a brokerage have moved out of Country Admin. They now sit under Channels in Broker Admin as "Register as POSP", and only the receiving broker decides them.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Broker Admin',
        blurb: 'Agents, reports, targets and the Vehicle Database.',
        changes: [
          {
            title: 'Issue an agent a new password',
            detail:
              'An agent’s page in Channels now has Issue a new password (or Create login, if they have none). The new password is shown once for you to pass on, the agent must change it at next sign-in, and their saved sign-ins end.',
          },
          {
            title: 'Reports: every product, and charts that read properly',
            detail:
              'Policies → By Product now lists every product with at least one active policy, not just the top five, and never lists a product with none. Policies with no product are shown under their insurance type instead of one "Unknown" row. Charts no longer cut off their value labels, count axes no longer show half-policies, money axes read in rupees, and long company names are shortened with the full name on hover.',
          },
          {
            title: 'Compact targets',
            detail:
              'Targets, and the Performance & Targets tab Country Admin sees on a broker, now fit on one screen: one row of totals and the twelve months in a single compact table.',
          },
          {
            title: 'Engine and chassis numbers',
            detail:
              'The Vehicle Database shows Engine No. and Chassis No. as separate columns. A vehicle whose own record left them blank now shows them from the other records we hold for the same registration, and the numbers a registration lookup returns during a quote are now carried through to the vehicle on the policy, where before they were dropped.',
          },
          {
            title: 'One agent list on the Commissions ledger',
            detail:
              'Both ledger views now have a single agent drop-down listing every agent, instead of a drop-down of the first twenty plus a separate search box.',
          },
          {
            title: 'Insurance company logos everywhere',
            detail:
              'The company’s logo now appears next to its name on policy details, renewals, claims, servicing requests, the Vehicle Database’s linked policies, commissions, reports and API Credentials — where the logo never showed at all. A company’s short description appears on hover once Country Admin has written one.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Agent portal',
        blurb: 'Contacts that stay yours, and Reports in the dark.',
        changes: [
          {
            title: 'Contacts stay with the agent who created them',
            detail:
              'A contact created while an agent adds a policy is now recorded as that agent’s contact, so it stays in their Contacts even if the policy is later reassigned. Twenty-two older agent policies from June and July that had no contact at all now each have one, created from the policy’s own holder details.',
          },
          {
            title: 'Reports in dark mode',
            detail: 'The agent Reports page — cards, targets, policy and payout tables — now follows the dark theme.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Security',
        blurb: 'Who can manage whose accounts.',
        changes: [
          {
            title: 'Accounts are managed only within their own organisation',
            detail:
              'Three older gaps are closed: adding a user could name an organisation other than your own; an admin whose account was not linked directly to their brokerage could see and reset people outside it; and the insurance company user tools could change accounts they had no business touching. Each now refuses anything outside the admin’s own organisation.',
          },
          {
            title: 'The old sign-in fallback is retired',
            detail:
              'Last release moved the token that keeps you signed in into a protected cookie and kept the old method working for one release for pages already open. Nothing has used it since, so it has been removed.',
          },
        ],
      },
    ],
    footnote:
      'Also fixed, unreported: deleting a contact who had their own commission rate would have turned that rate into one for every customer. Such a contact now cannot be deleted until the rate is removed. Commission reconciliation entries uploaded by one account were removed on request; the six ledger credits they had posted were reversed rather than deleted, so every running balance stays true.',
  },
  {
    tag: 'RELEASE / 2026.09.23',
    date: '23 September 2026',
    headline: {
      lead: 'Page 6 exists. So does the audit trail.',
      accent: 'And your sign-in is kept somewhere safer.',
    },
    summary:
      'The 23 September list: twenty items across the broker, customer and agent portals, and three of them were nothing like what they looked like. Every list in the platform shared one pager that only ever drew pages 1 to 5, so page 6 was unreachable by number. Saved Reports went blank for anyone with a schedule, because one missing field stopped the whole page drawing. Motor quotes were refused by the insurer because the PIN code we collect was never passed on. Alongside those: your brokerage now keeps a real audit trail, two-step sign-in is available with an authenticator app, and the token that keeps you signed in has moved somewhere a script on the page cannot read it.',
    stats: [
      { value: '20', label: 'of 20 reported items', note: 'one shipped as a written design instead of code — see the note at the foot' },
      { value: '10,500+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '6', label: 'database changes', note: 'all additive; nothing existing was rewritten' },
      { value: '3', label: 'faults found that nobody reported', note: 'including a way to switch off someone else’s two-step sign-in' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Lists, reports and pages that work',
        blurb: 'The pager, the blank page and the PIN code that never travelled.',
        changes: [
          {
            title: 'Pagination past page 5',
            detail:
              'Every list shared one pager that always drew pages 1 to 5 and never moved. On page 7 of your policies nothing was highlighted, and page 12 meant clicking Next seven times. Lists now show a sliding set of page numbers with the first and last always reachable, the page you are on is marked, and the controls finally appear on a phone. The Vehicle Database, which had no pager at all and showed only the first 50 vehicles, now has one.',
          },
          {
            title: 'Saved Reports no longer goes blank',
            detail:
              'If you had even one saved schedule, the whole Saved Reports page drew nothing: the schedule’s file format was missing from the data the page was given, and the page gave up rather than skip it. Schedules now list with their format, recipients and next run. A page that hits an unexpected error now shows a short message and a retry instead of going white.',
          },
          {
            title: 'Motor quotes are priced on your PIN code again',
            detail:
              'Motor premiums are priced by zone, so an insurer cannot quote without the proposer’s PIN code. We were asking for it, storing it, and then not sending it, so quotes were refused before they left us. The PIN now travels with the request, it is filled in from your profile, and a motor request will not submit without it.',
          },
          {
            title: 'Dark mode reaches Reports, insurance companies and API products',
            detail:
              'Every Reports tab, its filters, the policy drawers and the charts, plus the Country Admin insurance company page and the API Products page, were built for the light theme only and stayed white or near-unreadable. They now follow the theme, including text boxes, tables and pop-ups.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Commission reconciliation',
        blurb: 'Numbers read as sent, cards that filter, and a corrected statement that corrects.',
        changes: [
          {
            title: 'Policy numbers arrive as your spreadsheet meant them',
            detail:
              'A long policy number is often typed with a leading apostrophe or backtick to stop Excel turning it into 3.55E+17. That marker was being stored as part of the number, so those lines could never match a policy. New uploads now have it removed, and a row whose digits Excel has already destroyed is refused with a message naming the row, rather than stored wrong.',
          },
          {
            title: 'The summary cards are buttons',
            detail:
              'Matched, Mismatched, Pending and No Matching Policy now filter the list when you click them, and click again to go back. Each shows its count and its net difference, worked out across the whole statement rather than only the rows you are looking at, so the cards do not collapse to the one you selected.',
          },
          {
            title: 'The insurance company is on the line, and in a filter',
            detail:
              'Every statement line already knew which company it came from, but the table never said so. There is now an Insurance Company column and a filter to match, which combines with the cards and the dates. The company you upload against is a separate control, so changing a filter can never change where the next statement is filed.',
          },
          {
            title: 'A corrected statement corrects the line',
            detail:
              'When a company sends the same month again with a different figure for a policy already on file, the line is now updated in place rather than added a second time: the difference is worked out again (green when the insurer paid more, red when less), only the change reaches your ledger, and each correction is kept as history you can open on the line.',
          },
          {
            title: 'Uploading a statement in one line',
            detail:
              'Insurance company, statement period, file and Submit now sit together on one row, and the period is required, so a statement with no dates of its own is filed under the month you mean.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Security',
        blurb: 'A record of what happened, a second step if you want one, and a safer session.',
        changes: [
          {
            title: 'Audit Log',
            detail:
              'Your brokerage now keeps a record of sign-ins, sign-outs and refused sign-ins, of every policy, contact, rights and commission entry created, changed or deleted, and of every data export — each with the person, the time and a plain-English note of what changed. It is visible to broker admins, shows only your own brokerage, and entries cannot be edited or deleted.',
          },
          {
            title: 'Two-step sign-in, with an authenticator app',
            detail:
              'Settings → Password now offers two-step sign-in using an authenticator app such as Google Authenticator: scan the code, confirm once, and from then on a sign-in asks for the six-digit code. Ten single-use backup codes are issued in case you lose the phone. It is entirely optional. We chose an app rather than a code by SMS or email because message delivery is not yet connected on this platform, and a code that cannot arrive would lock people out.',
          },
          {
            title: 'Your session is kept somewhere a script cannot read it',
            detail:
              'The long-lived token that keeps you signed in used to sit in your browser’s storage, readable by any script on the page. It now travels in a protected cookie your browser only returns to our sign-in addresses, and the short-lived part is held in memory. Everyone is signed out once when this ships; sign in again and it will not happen a second time.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Brokers, agents and customers',
        blurb: 'Rates per customer, new vehicles, agent compliance, and your advisors.',
        changes: [
          {
            title: 'A commission rate for one customer',
            detail:
              'A rate can now name one of your contacts, and that customer’s policies with that insurer earn the percentage you set for them instead of your standard card. A rate written for a single policy still wins over it, and an agent’s share follows automatically.',
          },
          {
            title: 'A brand-new vehicle is not asked for a number plate',
            detail:
              'On Add Policy, choosing "New — first registration" now replaces Vehicle Number and Registration Date with what such a vehicle actually has: State, City, Pin Code and RTO. Typing the PIN fills the state and city, and picks the RTO when there is only one.',
          },
          {
            title: 'Agent training and certification',
            detail:
              'An agent’s page now carries a Training & Certification card: IRDAI registration, certified on and expiry (with "expires in 42 days", or how long ago it lapsed), hours recorded, exam result, and the stage they have reached with the next step named. Agents see the same facts, read-only, under Certifications. Nothing is blocked by it.',
          },
          {
            title: 'An agent who is already your contact can be added as a channel',
            detail:
              'Adding a channel for someone already on your books used to demand a second email address they do not have. It now links the person you already have, and says so — and linking never changes what they are paid.',
          },
          {
            title: 'Duplicate contacts are allowed again',
            detail:
              'Two contacts may share an email address or a phone number again, which families and businesses routinely do. A new switch in Broker Settings turns strict checking back on if you prefer it.',
          },
          {
            title: 'All of your advisors',
            detail:
              'Settings → Profile showed one broker and one agent. It now lists every broker you hold a policy with, the agents who sold you those policies beneath them, and any agent assigned to you — each opening to their name, phone, email and the policies they look after.',
          },
          {
            title: 'One Profile for agents, and quote filters that match reality',
            detail:
              'An agent’s separate "My Agent Profile" page is now part of the Profile tab in Settings, in the order you would read it. The Quotes list offers every status a quote can actually be in, with the older statuses grouped so the list is honest about what is still in use. Insurance company logos now appear beside the company name on policies, contacts and your insurance companies list.',
          },
        ],
      },
    ],
    footnote:
      'Also fixed while we were in there, though nobody had reported them: anyone could rewrite which brokerage their own login belonged to, or reactivate a blocked login, through the ordinary profile save; and changing a person\u2019s role on the Users screen reported success and changed nothing \u2014 that field is now shown as a fact, with an explanation, until a proper way to change it exists. Not in this release, and why. One person signing in to BOTH the agent and customer portals with the same details is NOT built: a login carries exactly one role today, and every permission check on the platform reads it, so a half-built version would have risked one portal seeing the other\u2019s records. It is written up as a design for a decision instead. Thirty-one commission statement rows uploaded on 22 September still carry the spreadsheet marker in their policy number; new uploads are read correctly, but those existing rows are deliberately left untouched because correcting them posts adjustments to your ledger \u2014 that is your call, and there is a command ready to run. Two-step sign-in uses an authenticator app only, because email and SMS delivery are not yet connected on this platform. And a two-column statement re-uploaded under a DIFFERENT month with a changed figure is filed as a second line rather than a correction: a month you typed is not firm enough ground on which to rewrite money.',
  },
  {
    tag: 'RELEASE / 2026.09.22',
    date: '22 September 2026',
    headline: {
      lead: 'The customer quote request really sends now.',
      accent: 'And the things that looked broken, explained.',
    },
    summary:
      'The 22 September list: thirteen items across the customer, broker and agent portals. Before changing anything, we checked each report against what the platform really does. Four turned out to be something other than what they looked like. API credentials were saving; the page just never said so. Saved Reports was not blank; nobody had found the button. Vehicles were not being duplicated; policies were not recording them at all. So those fixes go at the real cause. One correction to our last entry: the customer Request a Quote we announced on 20 September was refused on every submission, and not one ever got through. It works now.',
    stats: [
      { value: '13', label: 'of 13 reported items', note: 'every item on the list, including the four that turned out to be something else' },
      { value: '11,000+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '4', label: 'database changes', note: 'all additive; one fills in which brokerage each statement line belongs to' },
      { value: '5', label: 'unreported faults found', note: 'including one save that reported success and kept nothing' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Customers',
        blurb: 'A quote request that arrives, and one place for everything about you.',
        changes: [
          {
            title: 'Request a Quote works',
            detail:
              'Every request from the customer portal was being turned away, whatever cover was picked, so none ever reached a broker. Requests now arrive in the broker’s Quote Requests list, marked as coming from the customer portal, with the cover type the customer chose and any documents they attached.',
          },
          {
            title: 'Settings has a single Profile section',
            detail:
              'Your details, KYC documents and status, each of your policies with the broker and agent looking after it, your advisors’ contact details and password reset now sit together under Settings. The separate My Contacts page is gone from the menu. Its contact cards now appear in your profile and on each policy, and old links still land in the right place.',
          },
          {
            title: 'Renewal tabs show which one you are on',
            detail:
              'Upcoming and Lapsed are now clearly highlighted when selected, in light and dark mode. The count numbers have gone from the tabs, as asked, on every portal.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Brokers',
        blurb: 'Pages that show what they hold, and records that find each other.',
        changes: [
          {
            title: 'API Credentials shows what is saved',
            detail:
              'Saved credentials were always kept, but the page reopened with empty boxes, so it looked as if nothing had saved. Each insurer now shows your webUserId, whether a password and API key are stored, when you last saved, and the last connection test. After you save, the page shows what the server now holds rather than just a message. If you manage more than one brokerage, the page now follows the one chosen in the Select Broker menu; before, it could not open at all. Editing a webhook’s event type now actually saves the change; before, it showed success and kept the old value.',
          },
          {
            title: 'Saved Reports, found and working',
            detail:
              'Every report tab now has a Save report button. From Saved Reports you can open a saved report with its filters, download it as CSV or Excel, rename, schedule or delete it. Report downloads now need you to be signed in.',
          },
          {
            title: 'Vehicles from policies reach your Vehicle Database',
            detail:
              'A vehicle number entered on a policy is now saved to that customer’s vehicle record, so it shows in your Vehicle Database and fills the next quote and renewal. It never creates a duplicate, and it never overwrites details already on file. An agent sees only the vehicles on their own policies.',
          },
          {
            title: 'Statement lines wait for their policy',
            detail:
              'A line in an insurer’s statement for a policy you have not issued yet is now kept as Pending. It is matched automatically once the policy is issued and its commission is calculated. A Re-check now button matches them straight away.',
          },
          {
            title: 'Filters stay while you move around',
            detail:
              'Filters on Policies, Quotes, Customers, Claims, Renewals, Leads, Servicing, Brokers, agents, Channels, the Vehicle Database and Reports now stay set when you go to another section and come back, until you clear them or sign out.',
          },
          {
            title: 'A tighter Targets page',
            detail:
              'Summary figures at the top, and a grid with row and month totals and each month’s achieved premium. Brokers with more than 20 agents now see all of them.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Everywhere',
        blurb: 'The insurer’s number, and dark mode that reads.',
        changes: [
          {
            title: 'The insurer’s policy number, including in agent reports',
            detail:
              'The agent’s Reports page, channel details, contacts, commissions, renewals, claims and servicing now show the number the insurer issued. On 20 September we said our internal reference no longer appeared anywhere a person could read it; these screens still showed it. When an insurer has not issued a number yet, our reference is now shown with an “internal ref” label, so it cannot be mistaken for theirs.',
          },
          {
            title: 'Dark mode on the pages that still washed out',
            detail:
              'Contacts, Quotes, Claims, Leads and their activity timelines, Insurance Companies, Webhooks, Channel details and Communications no longer have text, badges or panels that disappear into the dark background.',
          },
        ],
      },
    ],
    footnote:
      'Also fixed along the way: a sum insured typed with commas (5,00,000) was turning away the whole quote request; insurer eligibility rules for third-party-only and own-damage-only cover could never apply; an employee whose report access had been removed could still run a report they had saved earlier; and statement lines are now kept strictly to the brokerage\u2019s own staff. Not in this release, and why. Sixteen policies still hold our internal reference in the field meant for the insurer\u2019s number; they wait for a decision on each, because a wrong number is worse than a missing one. Downloaded spreadsheets still show our reference without a label when the insurer has not issued a number, so a pasted value keeps matching in search. A policy with no customer attached does not reach the Vehicle Database until a customer is linked. Filters come back when you return through the menu, but not when the link you follow already carries its own filter. A customer\u2019s quote request is priced by the broker; it does not yet carry the vehicle detail an insurer needs for a live motor price.',
  },
  {
    tag: 'RELEASE / 2026.09.20',
    date: '20 September 2026',
    headline: {
      lead: 'Your filters stay put. Your agents see their renewals.',
      accent: 'And a quote you can raise yourself.',
    },
    summary:
      'The 20 September list. Filters now live in the address bar, so a refresh keeps them and a link reproduces what you were looking at. Columns stop jumping when you filter, headers stay put as you scroll, and a column only offers to sort when the server can really sort it. Agents get the renewals for the policies they sold, so nobody has to be told a renewal is coming. Customers can request a quote for themselves, end to end. Dark mode reaches the pages that were still bleeding white. And the internal reference we use to join records together no longer appears anywhere a person can read it.',
    stats: [
      { value: '16', label: 'of the 20 reported items', note: 'three partly done and one deferred, all named at the foot of this entry' },
      { value: '8,200+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '1', label: 'database change', note: 'descriptions only; no amount or balance was touched' },
      { value: '12', label: 'unreported faults found', note: 'things nobody had filed, fixed while we were in there' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Lists that remember what you asked for',
        blurb: 'Filters survive a refresh, columns hold still, and a sort header really sorts.',
        changes: [
          {
            title: 'Your filters are in the address bar',
            detail:
              'Narrow a list, then reload, and it is still narrowed. Copy the link to a colleague and they open the same view. Clear Filters empties it properly, including anything you arrived with from another screen. This is live on Policies, Contacts, Quotes, My Quotes, Claims, Renewals, Servicing, Brokers, Agents, Channels, Leads and the Vehicle Database.',
          },
          {
            title: 'Columns stop moving when you filter',
            detail:
              'Every column now has a declared width and the header row stays visible as you scroll a long list. The reported problem was that columns resized the moment a filter was applied; they no longer do. Twelve of the busiest screens are covered by this release — the rest of the platform is unchanged and will follow.',
          },
          {
            title: 'A sort header that does nothing is not offered',
            detail:
              'Only the columns the server can genuinely order by are clickable, and the arrow shows which way. Where a screen cannot be sorted at all, no header pretends otherwise. Sorting applies to the whole filtered list, not just the rows on your page.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Renewals reach the person who sold the policy',
        blurb: 'Upcoming and Lapsed, with counts, for brokers, customers and now agents.',
        changes: [
          {
            title: 'Agents see the renewals on policies they sold',
            detail:
              'An agent now has a Renewals screen of their own, showing the policies they sold that are coming up for renewal or have lapsed, so they can chase them. It is read-only: recording a renewal stays with the brokerage. Every agent has this without anyone switching it on, and a brokerage can take it away.',
          },
          {
            title: 'Upcoming and Lapsed on your dashboard',
            detail:
              'The Renewals panel on the dashboard now has the same two tabs as the full screen, each with its own Month and Year filters and a count on the tab. Lapsed offers months that have passed; Upcoming offers months still to come. A count that cannot be loaded shows a dash rather than a zero — an unknown number is not the same as none.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'A customer can ask for a quote',
        blurb: 'A guided journey, honest about what it can price.',
        changes: [
          {
            title: 'Request a Quote, from the customer portal',
            detail:
              'A customer can now raise a quote request for themselves: choose the cover, give the details, add documents if they have them, review, and submit. It lands in the brokerage\u2019s own quote inbox. Where the brokerage is connected to an insurer that can price it live, the customer sees real prices; where it is not, the request goes to the brokerage to price by hand, and the screen says which of the two happened rather than implying a price that is not coming.',
          },
          {
            title: 'Documents can come with the request',
            detail:
              'Attaching a document is optional \u2014 a request can be submitted without one. A customer can only ever attach to their own request, and cannot remove, relabel or approve a document once it is in; that stays with the brokerage.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'The number you are shown is the insurer\u2019s',
        blurb: 'Our internal reference is for joining records, not for reading.',
        changes: [
          {
            title: 'Policy numbers, everywhere a person looks',
            detail:
              'The platform generates its own reference to tie records together. It was appearing in places people read it: commission ledgers, downloaded reports, and on the shared public policy page under a "Policy Number" label. Every one of those now shows the insurer\u2019s own policy number, or a dash where the insurer has not given one. The shared public page no longer carries our reference at all.',
          },
          {
            title: 'Renewal and issuance messages name the right number',
            detail:
              'A policy was issued with the insurer\u2019s number recorded, and the email and text that followed quoted our internal reference instead. Those now carry the insurer\u2019s number. A renewal message that used to read "Policy  is renewed" when no number existed now reads properly.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Things that were quietly wrong',
        blurb: 'Nobody reported these. They were live.',
        changes: [
          {
            title: 'An empty list that was really an error',
            detail:
              'Nine screens \u2014 Contacts, Quotes, Claims, Servicing, Brokers, Agents, Channels, Leads and more \u2014 showed "none found" when the server had actually failed. A broker with thousands of contacts could be told they had none, and offered an "Add" button as the remedy. Each of those screens now tells you plainly when something went wrong, and offers to try again.',
          },
          {
            title: 'Two filters that filtered nothing',
            detail:
              'The date range on Leads and the incident-date range on Claims sent values the server had no use for, so they silently changed nothing. Fixed rather than quietly dropped.',
          },
          {
            title: 'Agent links that lost their filter',
            detail:
              'Following an agent link into Channels arrived unfiltered, showing every channel rather than the agents. The filter is now applied where it should be.',
          },
          {
            title: 'Dark mode on the pages that were still white',
            detail:
              'Policy, Quotes, Claims and Settings kept bleeding light backgrounds in dark mode, and the theme flashed white on every hard refresh. Both fixed. Your theme choice also now follows you to another device \u2014 and a device you have told explicitly keeps what you told it.',
          },
        ],
      },
      {
        tag: '06',
        heading: 'Broker administration',
        blurb: 'One record, opened to read, edited on purpose.',
        changes: [
          {
            title: 'A broker opens to read, not to edit',
            detail:
              'Clicking a broker now opens their details in view mode with an Edit button beside the status switch, instead of dropping you straight into a form. The Add Broker form and the detail form now carry the same fields, and the fields that only matter to the system are no longer in your way.',
          },
          {
            title: 'Back goes back',
            detail:
              'Editing a broker\u2019s administrator and pressing Back landed on an Access Denied page. It had three separate causes, all fixed, and Back now returns to the broker you came from, on the tab you were on.',
          },
          {
            title: 'Commission rates show the product you mapped',
            detail:
              'The Product field on a commission rate showed the platform\u2019s own product name rather than the name set when the product was mapped to the insurance company. Every table on that screen now agrees.',
          },
        ],
      },
    ],
    footnote:
      'Not in this release, and why. Agent renewals are read-only, so recording a renewal stays with the brokerage. The customer quote journey does not yet carry a vehicle variant, so a live price on a motor quote may come back as a refusal from the insurer rather than a premium \u2014 the screen says so rather than hiding it. Fixed column widths and sorting cover twelve of the busiest screens, not every screen in the platform. And sixteen policies were found holding our internal reference in the field meant for the insurer\u2019s number; they are listed for correction rather than changed automatically, because a wrong number is worse than a missing one.',
  },
  {
    tag: 'RELEASE / 2026.09.18',
    date: '18 September 2026',
    headline: {
      lead: 'Find it, tick it, change it.',
      accent: 'Across every list in the platform.',
    },
    summary:
      'The 18 September list, in full. Filters and multi-select arrive on the master data screens \u2014 Products, Vehicles, Geography \u2014 so you can narrow a long list and act on several rows at once. Delete becomes safe: a record still in use is refused, and told you what is using it. Agents get their own private contact sync and a way to ask to be mapped to a brokerage. Customers get a dashboard that counts their own policies, filters scoped to what they actually hold, and a quote list they can search. Dark mode works on the controls themselves, not just the page around them.',
    stats: [
      { value: '44', label: 'of the 50 reported items', note: 'the rest are named at the foot of this entry' },
      { value: '10,000+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '4', label: 'database changes', note: 'all additive; nothing existing is rewritten' },
      { value: '0', label: 'records destroyed by Delete', note: 'every delete is reversible, and refused while in use' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Lists you can actually work with',
        blurb: 'Filters, tick boxes, and one action for many rows.',
        changes: [
          {
            title: 'Filters on every master data screen',
            detail:
              'Products, Product Groups, Subgroups, Policy Types and Insurance Types; Vehicle Makes, Models, Variants, Fuel Types and Body Types; States, Cities, Pin Codes and RTOs. Each gained the filters its screen was missing, on one line with the search box. Every filter is answered by the server, so what you pick is what you get \u2014 a control that could not really narrow the list was not added.',
          },
          {
            title: 'Tick several rows and act on them once',
            detail:
              'Select rows individually or a whole page at a time, then Activate, Deactivate or Delete the selection. A selection is remembered as you move between pages. If part of it cannot be done, the screen says which rows and why, and keeps them selected \u2014 it never reports a part-finished job as finished.',
          },
          {
            title: 'Delete asks what would break first',
            detail:
              'Deleting a record that something still points at is refused, naming what is in the way \u2014 "still in use by 3 policies and 12 pin codes". Nothing is destroyed: a deleted record is hidden, not erased, so a mistake can be undone. Six master data types that could not be deleted safely before can be now.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'What each person can see',
        blurb: 'Documents, dashboards and filters, scoped to their owner.',
        changes: [
          {
            title: 'Identity documents are behind a login',
            detail:
              'KYC documents are now served only to people entitled to them, through the application rather than as a plain web address. Someone without access gets the same answer as for a record that does not exist.',
          },
          {
            title: 'An agent\u2019s synced contacts belong to the agent',
            detail:
              'A new Contact Sync section under Agent Settings holds contacts uploaded from an agent\u2019s own device, through an API a mobile app can call. They are the agent\u2019s own: their brokerage cannot see them, nor can another agent, nor an administrator. Re-syncing the same phone book updates rather than duplicates.',
          },
          {
            title: 'Customer dashboard counts your own policies',
            detail:
              'Active Policies showed zero for every customer. It now counts the policies you hold. The filters on your Policies and Quotes lists offer only the insurers, types and products that appear on your own records.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Country Admin',
        blurb: 'Brokers and insurance companies, managed from their own page.',
        changes: [
          {
            title: 'A broker\u2019s details in one editable place',
            detail:
              'Broker Information and Contact Information are one form you can edit, with an Active/Inactive switch. The duplicate tab in Broker Settings is gone, Settings has a Back button, and a logo can be uploaded \u2014 including a favicon \u2014 instead of pasting a web address. A broker\u2019s performance and targets are on their own page.',
          },
          {
            title: 'Suspend, then block, then delete',
            detail:
              'The action button offers Suspend. Blocking is a deliberate second step, and only a blocked broker can be deleted \u2014 and only when no live business points at them. Deleting a brokerage is now a Country Admin decision; it was previously possible for a broker\u2019s own administrator.',
          },
          {
            title: 'Insurance company details, and Insurer Admins',
            detail:
              'An insurance company\u2019s page now shows everything held about it, including its logo and IRDAI registration. Only Insurer Admins are created here. Assigned brokers can be searched when assigning, removed or suspended afterwards, and clicked through to their own page. Key Features on a product mapping now carry a heading and a description, which the broker\u2019s public site shows as highlights.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Broker portal',
        blurb: 'Renewals, quotes and addresses.',
        changes: [
          {
            title: 'Renewals split into Upcoming and Lapsed',
            detail:
              'Two real tabs, each with Month and Year filters that the server honours, and each offering only the time windows that make sense for it \u2014 the Lapsed tab no longer offers future windows. A policy that has been renewed leaves the queue, and its "Record renewal policy" button goes with it.',
          },
          {
            title: 'Addresses follow the state you pick',
            detail:
              'City and PIN Code now offer only values belonging to the selected State, and changing the State clears a city that belonged to the previous one. A PIN can still be typed by hand, and is checked against the State.',
          },
          {
            title: 'Quotes and vehicles',
            detail:
              'Premium Selection lists only products matching the cover type chosen above. The Quotes search now covers the insurer as well as the quote number and customer. The Vehicle Database can be narrowed to your own book and sorted by registration date. Saving a policy with an unusually long term asks you to confirm first.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Reading the screen',
        blurb: 'Dark mode on the controls, not just around them.',
        changes: [
          {
            title: 'Dark theme reaches the controls',
            detail:
              'Inputs, dropdowns, buttons, badges, tables and dialogs had no dark styling of their own \u2014 they stayed light while the page around them went dark, and some text was unreadable. They are styled now, including while focused, disabled or showing an error, and contrast was measured rather than eyeballed.',
          },
          {
            title: 'A sortable column is one that really sorts',
            detail:
              'Column headings that could not actually sort used to look as though they could, and moved an arrow without changing the order. Only headings the server can sort by are clickable now.',
          },
        ],
      },
    ],
    footnote:
      'Six items are not in this release, and it is worth saying which. Some tables can still shift their column widths when a filter is applied \u2014 the admin screens were fixed, the rest are being worked through, as is putting every list\u2019s search and filters on a single line. Automatic archiving of expired and cancelled policies is built and switched off, awaiting a check of the renewals queue. Encryption of stored bank account numbers is scheduled for the next maintenance window. Commission percentages for a handful of agent channels need to be entered before those agents earn. The customer \u201cRequest a Quote\u201d journey is being scoped separately; its search and filters are in this release, the guided journey is not.',
  },
  {
    tag: 'RELEASE / 2026.09.17',
    date: '17 September 2026',
    headline: {
      lead: 'Decide exactly what each person can do.',
      accent: 'Page by page, and nobody loses what they had.',
    },
    summary:
      'Rights Management becomes a grid. For every employee and every agent you can now set Read, Create, Edit and Delete separately on each part of the platform \u2014 Contacts, Policies, Quotes, Leads, Renewals, Claims, Servicing, Commissions, Reports and Channels \u2014 instead of ticking a short list of blanket permissions. Departments can carry their own defaults, so a new joiner starts with the right access. Everyone keeps exactly the access they have today unless you change it, and the screen now tells you where each answer comes from.',
    stats: [
      { value: '40', label: 'permissions per person', note: 'ten areas, four levels each' },
      { value: '9,700+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '0', label: 'accounts lose access', note: 'existing access is carried over, not reset' },
      { value: '2', label: 'database changes', note: 'the new grid, and carrying today\u2019s access into it' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'A grid instead of a checklist',
        blurb: 'Ten areas, four levels, set per person.',
        changes: [
          {
            title: 'Read, Create, Edit and Delete, per area',
            detail:
              'Rights Management now shows a grid for each employee and each agent. You can let someone read Contacts but not change them, create Quotes but not delete them, and so on \u2014 each area set on its own. The old blanket permissions still work and are shown as \u201cLegacy\u201d until you replace them.',
          },
          {
            title: 'Defaults by department',
            detail:
              'A department can carry its own set of rights, so anyone in it starts from those instead of from nothing. A person\u2019s own settings always win over their department\u2019s.',
          },
          {
            title: 'The screen says where each answer came from',
            detail:
              'Every cell is labelled \u2014 Override, Legacy, Department default, Baseline, Locked, or Admin role \u2014 so it is clear whether you set it, your department did, or it was carried over. Agents cannot be granted the few things an agent may never do, and those cells say Locked rather than pretending to be editable.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Nobody is locked out by the change',
        blurb: 'Today\u2019s access is carried into the new grid.',
        changes: [
          {
            title: 'Existing access is carried over, not reset',
            detail:
              'Turning the grid on does not clear anyone\u2019s access. Whatever each person can do today, they can still do tomorrow, and you change it deliberately from the Rights Management screen. This includes staff and agent logins whose records were incomplete \u2014 they keep working exactly as before.',
          },
          {
            title: 'A refusal now reads as a refusal',
            detail:
              'When the platform cannot check your permissions \u2014 a brief connection problem, say \u2014 screens now say so and offer to try again, instead of showing you an empty page that looks like you have nothing.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'The same rule at every door',
        blurb: 'An action answers to its own permission, whichever screen it is started from.',
        changes: [
          {
            title: 'Actions answer to the right permission',
            detail:
              'A few actions could be reached from more than one screen and were not always checked against the same permission. Recording an issued policy, buying a vehicle registration search, and asking an insurer for a payment link now each answer to one rule wherever they are started from, so what you have granted is what applies.',
          },
          {
            title: 'Links no longer lead where you cannot go',
            detail:
              'Names that link to another record \u2014 an insurer, a contact, a quote, an agent \u2014 now appear as plain text when your own access would not open that page, instead of sending you to a \u201cnot authorised\u201d screen.',
          },
        ],
      },
    ],
    footnote:
      'Automatic archiving of expired and cancelled policies remains built and switched off, waiting on your check of the renewals queue. The customer \u201cRequest a Quote\u201d journey is being scoped separately and is not part of this release.',
  },
  {
    tag: 'RELEASE / 2026.09.16',
    date: '16 September 2026',
    headline: {
      lead: 'Renewals that follow the policies.',
      accent: 'And know which policy renewed which.',
    },
    summary:
      'The renewal and policy-lifecycle rework from the 15 September list. The renewals queue now reads straight from your policies, so it can no longer drift out of date or show a renewal that has already been done. Every policy has a clear status \u2014 Upcoming, Active, Expired or Cancelled \u2014 worked out from its dates. A renewal can now be linked to the policy it renews, all the way from a renewal quote to the issued policy, and entering a vehicle that already has a live policy offers to record the new one as its renewal.',
    stats: [
      { value: '1', label: 'reported item delivered in full', note: 'renewal and lifecycle, in six parts' },
      { value: '8,800+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '1', label: 'database change', note: 'a renewal quote can remember the policy it renews' },
      { value: 'Off', label: 'automatic archiving', note: 'built, and switched off until you have checked the new queue' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'A clear status for every policy',
        blurb: 'Upcoming, Active, Expired or Cancelled \u2014 never typed in by hand.',
        changes: [
          {
            title: 'Lifecycle status worked out from the dates',
            detail:
              'Each policy now shows Upcoming (cover has not started), Active (today is within cover), Expired (cover has ended) or Cancelled. It is calculated from the policy\u2019s dates and status, so it cannot be set wrongly or go stale. A policy in its grace period shows as Expired with \u201cin grace until\u201d beside it. The Policies list can be filtered by these four.',
          },
          {
            title: 'Expiry must come after the start date',
            detail:
              'Saving a policy whose expiry date is on or before its start date is now refused with a clear message. A handful of existing policies with impossible dates were corrected on 15 September.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Renewals linked to what they renew',
        blurb: 'From the renewal quote to the issued policy.',
        changes: [
          {
            title: 'A renewal remembers the policy it renews',
            detail:
              'Starting a renewal quote from a policy, recording a renewal policy, or using Renew now links the new policy to the old one and marks it as a renewal. The policy page shows \u201cRenewal of\u201d and \u201cRenewed by\u201d links. A policy that has already been renewed, belongs to another brokerage, or was never issued cannot be renewed again, and a renewal for a different customer asks you to confirm first.',
          },
          {
            title: 'A vehicle that already has a live policy',
            detail:
              'When you enter a vehicle number that already has an active or upcoming policy in your book, you are asked: \u201cThis vehicle already has an active policy expiring (month year). Create this as a renewal instead?\u201d \u2014 with Create as Renewal or Create as New Policy Anyway.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'A renewals queue built from your policies',
        blurb: 'No separate list to fall out of step.',
        changes: [
          {
            title: 'The queue reads your policies directly',
            detail:
              'Renewals now lists policies expiring in the window you choose, and those already past expiry, straight from your policy data. A policy leaves the queue only when a renewal is linked to it. Assigning, status updates and notes work as before and are kept against the policy. Where a later policy for the same vehicle looks like the renewal but is not yet linked, the queue shows it as a possible renewal so you can link it. The Fetch Pending button is gone because there is nothing to fetch.',
          },
          {
            title: 'Servicing requests wait for their documents',
            detail:
              'A servicing request waiting on documents can no longer be completed, sent to the insurer or moved to payment until the requested documents have been provided and approved, however the status is changed.',
          },
        ],
      },
    ],
    footnote:
      'Automatic archiving of renewed and cancelled policies is built but switched off; it will be turned on once you are happy the new renewals queue is right. Three older policies that were clearly renewed by later ones are being linked as renewals as part of this release.',
  },
  {
    tag: 'RELEASE / 2026.09.15-c',
    date: '15 September 2026',
    headline: {
      lead: 'Fewer duplicates, fewer dead ends.',
      accent: 'And the doors checked properly.',
    },
    summary:
      'The rest of the 15 September list, plus a set of access checks tightened along the way. Contacts can no longer be created twice with the same email or phone, every address form fills the state from the PIN code, channels and agents take the code you type, a missing insurer vehicle code now offers a choice instead of an error, reconciliation can be filtered by date and downloaded, payouts show each agent and policy, and a quote started from a contact opens already filled in. Separately, a review of who is allowed to do what closed a number of gaps that had existed for some time; none showed any sign of having been used.',
    stats: [
      { value: '8', label: 'reported items delivered', note: 'plus the access checks below' },
      { value: '8,500+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '4', label: 'database changes', note: 'agent codes unique per broker; three corrections to the state and city lists' },
      { value: '0', label: 'signs of misuse found', note: 'every gap was checked against real activity first' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Contacts and addresses',
        blurb: 'One record per person, and states that fill themselves.',
        changes: [
          {
            title: 'No duplicate email or phone when creating a contact',
            detail:
              'Creating a contact from the Contacts page, or a new customer from the Policy page, is now refused if another contact of yours already has the same email (ignoring capitals and spaces) or the same phone number, whatever the name. The message appears under the field and names the existing contact. Existing duplicates are left as they are.',
          },
          {
            title: 'State fills itself from the PIN code, and is always a list',
            detail:
              'On every address form, entering a six-digit PIN code now fills in the State (and City where the form has one). State is always a dropdown of all states and union territories, and you can still change it by hand. Andaman & Nicobar Islands and Dadra & Nagar Haveli and Daman & Diu are back in the list, and Uttarakhand PIN codes now resolve to the current Uttarakhand entry.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Channels and agents',
        blurb: 'Your codes, and checks that look in the right place.',
        changes: [
          {
            title: 'Channel duplicates are checked against channels only',
            detail:
              'Adding a channel no longer fails because the person\u2019s email or phone appears somewhere in your policy data. Duplicates are checked against your other channels. If the email or phone already belongs to a login elsewhere, the channel is created without a portal login and you are asked for a different login email; the agent\u2019s commission rate is kept and applied as soon as the login is added.',
          },
          {
            title: 'Channel and agent codes are entered by hand',
            detail:
              'The POSPs tab now asks for a code instead of generating one with a POSP prefix, matching the Channels tab. A code must be unique within your brokerage; another brokerage may use the same code.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Quotes',
        blurb: 'A choice instead of a dead end.',
        changes: [
          {
            title: 'A missing insurer vehicle code offers a choice',
            detail:
              'When a live premium cannot be fetched because the vehicle has no insurer code (for example a hand-entered two-wheeler), the quote now lists the insurer\u2019s matching coded variants to pick from and fetches again. The vehicle list marks variants without an insurer code, and country admins have a report of every uncoded variant so they can be fixed.',
          },
          {
            title: 'A quote from a contact opens already filled in',
            detail:
              'Create New Quote from a contact now opens at Business Type with the customer, insurance type, business type and vehicle taken from the contact\u2019s most recent converted lead, so you go straight on to products and premium.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Commissions',
        blurb: 'Reconciliation you can filter and keep, payouts you can read.',
        changes: [
          {
            title: 'Reconciliation by date, with a download',
            detail:
              'Commission Reconciliation can now be filtered by statement date or date range, and a Download Reconciliation button exports exactly those rows as CSV or Excel. Uploading the same statement again no longer creates duplicate lines; it tells you how many rows were already on file.',
          },
          {
            title: 'Payouts by agent, with the policies behind them',
            detail:
              'The payout list shows each agent\u2019s name, and View Policies opens the policy number, holder, product, insurance type and commission for every policy in that payout. Where a calculation\u2019s total includes shares beyond broker and agent, each share is shown so the total adds up on screen.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Access checks tightened',
        blurb: 'Actions now limited to the people who should take them.',
        changes: [
          {
            title: 'Approvals and money actions stay with the right people',
            detail:
              'Approving, reversing or calculating commission, approving and paying agent payouts, verifying agent documents, and setting agent targets now require the appropriate broker administrator or permission, and can never be done by the agent they concern. Employees can no longer change their own department or brokerage.',
          },
          {
            title: 'Records kept apart between brokers and countries',
            detail:
              'Renewal and servicing-request assignment and edits, linking a renewal to a previous policy, and submitting a quote are all checked against the brokerage of the person acting, and country administrators see renewal and commission records for their own country only.',
          },
          {
            title: 'Customers read their policies and renewals',
            detail:
              'In the customer portal, policies and renewals are read-only apart from filing a claim, matching what the portal has always offered.',
          },
        ],
      },
    ],
    footnote:
      'Not in this release: permission levels per page (Read, Create, Edit, Delete) and the renewal and policy-lifecycle rework are being built next. Seven agent channels have never had a commission percentage set, so their agents currently earn nothing on the policies they sell; that is a setup decision for each brokerage rather than a fault, and is being raised with them.',
  },
  {
    tag: 'RELEASE / 2026.09.15-b',
    date: '15 September 2026',
    headline: {
      lead: 'The right fields for the right policy.',
      accent: 'One door to each setting.',
    },
    summary:
      'Six items across the Broker, Agent and Customer portals, released the same afternoon as the commission fixes. Premium Paying Term now appears only where it means something, renewal pages show the policy number people actually recognise, a two-wheeler quote only offers two-wheeler products, agents can find and convert the leads assigned to them, and the duplicate password and profile sections are gone from Settings.',
    stats: [
      { value: '6', label: 'reported items delivered', note: 'plus gaps found by review alongside them' },
      { value: '7,600+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '0', label: 'database changes', note: 'nothing stored was altered' },
      { value: '0', label: 'known regressions', note: 'on the full backend suite' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Policies and renewals',
        blurb: 'Fields that fit the policy, numbers people recognise.',
        changes: [
          {
            title: 'Premium Paying Term only for Health, Term and Life',
            detail:
              'The Premium Paying Term and instalment fields now appear only for Health, Term, Life and investment-linked life policies, and are hidden for motor and every other type. The server applies the same rule, including when an edit changes a policy\u2019s type, so a motor policy can no longer carry an instalment schedule by accident.',
          },
          {
            title: 'Renewals show the insurer\u2019s policy number, as a link',
            detail:
              'The renewals list and renewal detail page now show the insurer\u2019s policy number (or ours where the insurer\u2019s is not recorded), and it opens the policy. The customer name opens the contact. Search finds a renewal by either number.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Quotes',
        blurb: 'Products that match the vehicle.',
        changes: [
          {
            title: 'Premium selection only offers products for the chosen vehicle',
            detail:
              'At the product and premium step, and when adding an option on a quote, the product list now shows only the selected insurer\u2019s products for the selected vehicle type \u2014 Two Wheeler products for a two-wheeler, and GCV or PCV where chosen. If the insurer has none, it says so instead of showing an empty list. Changing the vehicle type clears a product picked for the old one, and the server refuses a product that does not fit the quote.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Agents and customers',
        blurb: 'Less hunting, fewer duplicates.',
        changes: [
          {
            title: 'Agents see Leads in the sidebar and can convert their own leads',
            detail:
              'Leads now appears in the agent portal\u2019s menu, listing the leads assigned to that agent. An agent with the Add Customer permission can convert a lead assigned to them into a contact they own. If the broker already has that person as someone else\u2019s client, the agent is told, and no duplicate is created.',
          },
          {
            title: 'One place for the agent\u2019s password',
            detail:
              'The Reset Password section under Settings \u203a My Agent Profile has been removed; Settings \u203a Password is the one place to change it.',
          },
          {
            title: 'One place for the customer\u2019s profile and password',
            detail:
              'Customers no longer see separate Profile and Password sections in Settings. My Profile & KYC holds the profile, KYC details and password reset, and changing the photo there now updates the picture in the page header too.',
          },
        ],
      },
    ],
  },
  {
    tag: 'RELEASE / 2026.09.15',
    date: '15 September 2026',
    headline: {
      lead: 'Paid once means paid once.',
      accent: 'Edits that keep what you set.',
    },
    summary:
      'A short, urgent release about money and access. Recalculating commission on a policy that had already been paid out could pay the agent a second time. Editing a commission rate could open with its settings blank and quietly change what the rate applied to on save. New agents were joining with no permissions at all, so the buttons to add a contact or a quote simply never appeared for them. And the renewals table on the dashboard told some people there were no renewals when the truth was that they were not allowed to see them.',
    stats: [
      { value: '4', label: 'reported and urgent items delivered', note: 'plus access checks tightened alongside them' },
      { value: '7,500+', label: 'automated checks passing', note: 'both suites run in full before release' },
      { value: '1', label: 'database change', note: 'additive; nothing existing is rewritten' },
      { value: '0', label: 'commission recalculated in bulk', note: 'nothing was re-priced as part of this release' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Commission that cannot be paid twice',
        blurb: 'Recalculation now respects what has already been paid.',
        changes: [
          {
            title: 'Recalculating a paid policy could pay the agent again',
            detail:
              'Pressing Recalculate Commission on a policy replaced its commission with a fresh copy, even when that commission had already gone out in a payout, so the next payout batch paid the agent a second time. A paid commission now stays paid. If the recalculated figure is lower, the difference is recorded as a clawback against the agent\u2019s next payout; if it is higher, only the difference is added. Either way the entry is linked to the original so you can see why it exists.',
          },
          {
            title: 'Recalculating an unchanged commission no longer writes anything',
            detail:
              'Every click used to add a reversal and a new entry to the ledger even when nothing had changed. Now, if the result is the same, nothing is posted and the policy page says \u201cCommission unchanged \u2014 nothing was posted\u201d.',
          },
          {
            title: 'One current commission per policy, enforced',
            detail:
              'A policy can now only ever have one current commission calculation, and a payout batch will not pick up a commission that is already in another live payout. Issuing a policy also records its commission once, not twice.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Rates that keep their settings',
        blurb: 'Editing a commission rate shows what the rate actually is.',
        changes: [
          {
            title: 'Edit Rate opened blank for recent rates',
            detail:
              'Rates saved with both an insurance type and a product opened in an older form that showed none of their conditions, and saving it cleared the insurance type and product, changing which policies the rate paid on. The form now opens with every value filled in, and the server refuses a save that would silently remove a rate\u2019s insurance type or product.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Agents who can do their job',
        blurb: 'New agents start with the permissions agents have always had.',
        changes: [
          {
            title: 'New agents could not add contacts or quotes',
            detail:
              'Agents created since 10 August started with no permissions, so Add Contact and New Quote never appeared for them. The 28 affected agents were given both on 15 September, and every agent created from now on \u2014 by application, by channel or by an admin \u2014 starts with them. Brokers can still adjust each agent under Rights Management.',
          },
          {
            title: 'Approving and editing agents requires the right permission',
            detail:
              'Approving, rejecting and editing an agent now require the Manage POSPs permission, the same as creating or suspending one, and an agent\u2019s broker and login cannot be changed by an edit.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'A dashboard that does not guess',
        blurb: 'No more \u201cnone\u201d when the answer was \u201cnot allowed\u201d.',
        changes: [
          {
            title: 'Upcoming Renewals said there were none',
            detail:
              'The dashboard\u2019s Upcoming Renewals table was shown to people who are not allowed to read renewals, and the refusal appeared as \u201cNo upcoming renewals\u201d. It is no longer shown to them, and when renewals cannot be loaded for anyone else it now says so, with a Try again button.',
          },
        ],
      },
    ],
    footnote:
      'Past double payments are identified but not yet recovered. A review found a small number of agent payouts, almost all on demonstration accounts, where a policy was paid more than once before this fix. Nothing has been clawed back automatically: each one is being checked against what the payout actually paid before any adjustment is posted.',
  },
  {
    tag: 'RELEASE / 2026.09.14',
    date: '14 September 2026',
    headline: {
      lead: 'Filters that matched nothing.',
      accent: 'Doors that would not open.',
    },
    summary:
      'Seventeen items across the Broker, Customer and Agent portals. The theme this time is things that looked present and were not: a commission filter where typing an insurer\u2019s name could only ever return an empty list, a targets panel wired to an address that does not exist on the server, a Settings menu offered to people the page then turned away, and a city that was quietly discarded every time it did not exactly match a name we already held. None of these announced themselves \u2014 they just showed you nothing and let you conclude there was nothing to show.',
    stats: [
      { value: '17', label: 'reported items delivered', note: 'plus faults found alongside them' },
      { value: '7,400+', label: 'automated checks passing', note: 'about 430 written for this release' },
      { value: '2', label: 'database changes', note: 'both additive; nothing existing is rewritten' },
      { value: '0', label: 'known regressions', note: 'both suites run in full before release' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Looked empty, was not',
        blurb: 'Four places that showed you nothing and meant something else.',
        changes: [
          {
            title: 'The commission filter could not match an insurer by name',
            detail:
              'Typing an insurer\u2019s name into the Ledger filter returned an empty list every time \u2014 the box accepted text but the search behind it only ever understood an internal code. An empty result reads as \u201cno commission with this insurer\u201d, which was never what it meant. Both that filter and the identical one on the Reports tab are now dropdowns of the insurers you actually work with, and filtering no longer hides your page controls.',
          },
          {
            title: 'A failed load said you had no commission',
            detail:
              'When the commission figures could not be fetched, the page showed the same empty state it shows a broker with no commission at all. It now tells you it could not load them, which is a different sentence and a different problem.',
          },
          {
            title: 'An agent\u2019s targets panel was always empty',
            detail:
              'The Performance Targets card asked the server for something at an address that does not exist, so it quietly fell back to \u201cno targets set\u201d for everyone, always \u2014 including agents whose broker had set targets months ago. It now reads the real figures, and shows what has been achieved against them.',
          },
          {
            title: 'A city that did not exactly match was thrown away',
            detail:
              'Saving a contact with a city we did not already hold under that exact spelling discarded it silently \u2014 no warning, no error, the field simply blank again afterwards. City is now chosen from a list, an unrecognised one is refused rather than dropped, and where a city was stored it no longer appears on screen as an internal reference number.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Doors that said Access Denied',
        blurb: 'Offered in the menu, refused on arrival.',
        changes: [
          {
            title: 'Settings now opens for customers and agents',
            detail:
              'Both were shown a Settings link and both were turned away by it. Each now has a Settings page of their own carrying profile details, KYC status and documents, and a password change \u2014 moved off the contact screen, where they had been sitting among things that belong to the broker rather than to you.',
          },
          {
            title: 'An agent\u2019s portal is now Reports, and Settings holds the rest',
            detail:
              'Activation progress, documents, contact details, status, bank details and certifications have moved to Settings; what remains under Reports is the book, the payouts and the targets. Certifications had been appearing twice under two different headings and are now one section.',
          },
          {
            title: 'Agents can add a profile picture',
            detail:
              'Uploaded from their own Settings, and shown to their broker on the channel page.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Claims, and what a claim may be paid',
        blurb: 'Amounts recorded where the decision is actually made.',
        changes: [
          {
            title: 'Claimed, Approved and Settled amounts can be entered',
            detail:
              'The claim page had places to show all three and no way to record any of them. The amount claimed is captured when the claim is raised \u2014 by the customer, their agent, or the broker \u2014 and the approved and settled figures are entered at the point those decisions are taken. Agents can raise a claim at all for the first time.',
          },
          {
            title: 'Each amount can only be set where it belongs',
            detail:
              'The three figures are now recorded only on the screens where the corresponding decision is made, and only by the people entitled to make it. Sending one anywhere else is refused and says so, rather than being accepted and then ignored.',
          },
          {
            title: 'A settlement cannot exceed what was approved',
            detail:
              'Settling a claim is now checked against the approved amount, or against the amount claimed where nothing has been approved yet. A claim carrying no figure at all can no longer be settled for an arbitrary one. Nothing currently on the books is affected \u2014 we checked before changing it.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Insurers, and asking to be connected',
        blurb: 'Your own numbers, and a way to ask for more.',
        changes: [
          {
            title: 'Policy counts are now yours, not the catalogue\u2019s',
            detail:
              'The Insurance Companies page showed how many products each insurer sells \u2014 the same number for every broker on the platform. It now shows how many policies you have placed with them and how many are currently active. A policy still in draft no longer counts as business placed.',
          },
          {
            title: 'Insurers you are not connected to are visible, and can be requested',
            detail:
              'They are listed with a request button. Your country administrator reviews the request, and the connection exists once they approve it \u2014 approving is what creates it, rather than the request itself.',
          },
          {
            title: 'A quote from your public site carries what the visitor chose',
            detail:
              'The insurance type and company someone selects on your site now travel through to the lead, and on into any quote or policy raised from it, instead of being re-entered by hand.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Smaller things, asked for',
        blurb: 'Each reported, each done.',
        changes: [
          {
            title: 'Gross Premium is gone from the policy page',
            detail:
              'It showed the same figure as Total Premium on every policy entered by hand, which read as duplicate information. The figure is still recorded and still used where it matters \u2014 it is only off the screen.',
          },
          {
            title: 'Emailing a policy link now sends the link that works',
            detail:
              'The address in the email pointed at a page that does not exist, while the one behind Copy Public Link was correct. Both now come from the same place. Note that outgoing email is still not configured on this platform, so nothing is being delivered yet either way \u2014 this fixes the address for when it is.',
          },
          {
            title: 'Identity documents fill in the contact form as you attach them',
            detail:
              'Attaching a PAN or Aadhaar while creating a contact now reads what it can and offers it in the matching fields, alongside the alternatives it considered. Nothing is saved until you submit, and anything you have already typed is left alone.',
          },
          {
            title: 'Contacts can be deleted, and refuse to be when they should',
            detail:
              'Single or several at once. A contact with policies, quotes or claims against it is refused and tells you what is attached \u2014 previously the deletion appeared to succeed and left those records pointing at someone who could no longer be opened.',
          },
          {
            title: 'A customer who loses their password can be given a new one',
            detail:
              'Their broker, employee or agent can issue a fresh one, shown once to pass on. Previously a lost password had no route back at all.',
          },
          {
            title: 'Paying yourself is no longer offered when creating a payout',
            detail:
              'Removed as requested. Payouts already recorded are unaffected and can still be completed.',
          },
        ],
      },
    ],
    footnote:
      'Two things worth stating plainly. Outgoing email still does not work on this platform \u2014 several items in this release touch what an email would say, and none of them make one arrive. And the request-to-connect queue currently lives on the per-broker insurers screen, so an administrator reaches it by opening a broker rather than from a menu of its own; that is a shortcoming we know about rather than one we have hidden.',
  },
  {
    tag: 'RELEASE / 2026.09.09',
    date: '9 September 2026',
    headline: {
      lead: 'The data was right.',
      accent: 'The screen was reading the wrong thing.',
    },
    summary:
      'Thirteen reported items across the Broker and Agent portals. Four of them turned out not to be missing features at all \u2014 the information had been recorded correctly the whole time and the screen was reading the wrong field, so it showed a dash, or an empty list, or a row of zeros. That is a worse kind of fault than an error message, because nothing looks wrong. Alongside those: agents can now reach the leads assigned to them, customers can see their own quotes, and a scheduled health check that had been failing quietly since June was found and switched off rather than left to report good news it could not possibly know.',
    stats: [
      { value: '13', label: 'reported items delivered', note: 'plus two faults found on the way' },
      { value: '4', label: 'were reading the wrong field', note: 'the data was already correct' },
      { value: '7,000+', label: 'automated checks passing', note: 'about 330 written for this release' },
      { value: '2', label: 'database changes', note: 'both additive; nothing existing is rewritten' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Shown as empty, stored correctly',
        blurb: 'Four screens that were quietly reading the wrong field.',
        changes: [
          {
            title: 'Activities added to a lead never appeared',
            detail:
              'Adding an activity to a lead confirmed it had saved, and the timeline underneath carried on saying there was nothing recorded. Every one of those activities was stored correctly \u2014 the timeline was looking in the wrong place and always found nothing. It now shows them, and if the list genuinely fails to load it says so instead of claiming the lead has no history.',
          },
          {
            title: 'Product interest from your public site showed as a dash',
            detail:
              'When someone asked for a quote on a broker\u2019s own site and chose what they were interested in, that choice was saved against the lead but the lead page displayed a dash. It now shows what they asked about. A returning visitor who comes back asking about something different also has their new choice recorded, which previously was discarded.',
          },
          {
            title: 'Matching an insurer statement reported zero every time',
            detail:
              'Uploading a commission statement with a policy number and an amount reported nothing processed and nothing matched. The file was being refused for a missing column and every rejected row was being discarded without a word, so a completely failed upload looked exactly like an upload of an empty file. Statements now import with just the policy number and the amount, and any row that cannot be read is listed with the reason.',
          },
          {
            title: 'A follow-up date that could never be set',
            detail:
              'The next follow-up field on a lead was permanently blank because there was nowhere to enter one. There is now a date and time picker with a remarks box beside it.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Reaching work that was out of view',
        blurb: 'Two portals that were missing a section entirely.',
        changes: [
          {
            title: 'Agents can open the leads assigned to them',
            detail:
              'Assigning a lead to an agent recorded the assignment, but the agent was refused when they tried to open it \u2014 so the assignment did nothing they could act on. Agents now see the leads assigned to them, and only those, and can log activity against them. Converting, reassigning and deleting stay with the broker.',
          },
          {
            title: 'Customers can see their quotes',
            detail:
              'The customer portal had no quotes section at all. There is one now, read-only, listing the quotes that have been shared with them. Quotes raised for a contact before they were given a login now appear too, which previously they would not have.',
          },
          {
            title: 'A quote waiting to be sent is marked as such',
            detail:
              'A contact converted from a lead now carries a Quotes Pending marker until a quote has actually been shared with them, so nothing sits forgotten. It clears the moment the quote goes out.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Fewer things to retype',
        blurb: 'Details the system already held, now filled in for you.',
        changes: [
          {
            title: 'Vehicle details fill in when you make a policy for a contact',
            detail:
              'Creating a policy from a lead already filled in the vehicle. Doing the same from a contact did not, so the same registration, make, model and year had to be typed again. It now fills in from the vehicle already on that contact. Where a contact has more than one vehicle you are asked which, rather than the first being chosen for you.',
          },
          {
            title: 'Premium details for health, life, term and the rest',
            detail:
              'Every non-motor policy type now takes a net premium, taxes, and a sum assured, with the total worked out for you. Motor keeps the split it already had, unchanged.',
          },
          {
            title: 'Documents can be attached while you create',
            detail:
              'Identity documents for a contact, and documents for a new channel, can now be uploaded during creation instead of having to save first and come back. If one of several files fails to upload you are told which one and can retry it \u2014 the record itself is kept.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Errors that stopped work',
        blurb: 'One of these was reported. The other two were found while working nearby.',
        changes: [
          {
            title: 'Creating a quote failed outright for some brokers',
            detail:
              'Brokers whose own identifier is long could not create a motor, health or life quote at all \u2014 the reference the system builds for a new quote did not fit the space reserved for it. The reference is now built to fit whatever the broker is called, and the check that was meant to prevent this is applied on every path that creates one.',
          },
          {
            title: 'A policy history that would not open',
            detail:
              'The history view for any policy with a payment recorded against it failed to load. It had failed thirty-one times the previous day.',
          },
          {
            title: 'A health check that reported good news it could not know',
            detail:
              'A scheduled check on external services had been failing to run since June, unnoticed. On inspection it did not check anything \u2014 it reported everything healthy unconditionally. Rather than switch it on and start publishing an all-clear nobody had verified, it has been taken off the schedule until it does real work.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Agent bank details',
        blurb: 'Visible where they need to be, and nowhere else.',
        changes: [
          {
            title: 'Account numbers now show only their last four digits',
            detail:
              'An agent\u2019s bank account is shown as its last four digits everywhere it appears, and the full number is available only to the agent themselves. Agents can now view and update their own bank details from their portal, which previously had no screen for it at all; brokers see the masked number on the agent\u2019s channel page and can no longer change it.',
          },
        ],
      },
    ],
    footnote:
      'Deliberately not done, and worth stating: bank account numbers are shown to fewer people than before, but they are still stored in plain form. Changing that is a separate piece of work with its own risks and is not claimed here. Two known issues were also examined and left alone this time \u2014 a dashboard count that disagrees with the list beneath it for policies added between midnight and half past five in the morning, and a corrected commission statement re-uploaded on a later day being counted twice.',
  },
  {
    tag: 'RELEASE / 2026.09.08',
    date: '8 September 2026',
    headline: {
      lead: 'Things that reported success',
      accent: 'and had never once worked.',
    },
    summary:
      'Thirteen items across the Broker, Customer and Agent portals. The pattern running through this release is uncomfortable and worth stating plainly: several of these features had never worked for anybody, and nothing said so. Creating an agent channel failed outright for three brokers. Adding an activity to a lead was rejected every single time. An agent pressing “submit documents” had their files thrown away and was told it went through. Matching an insurer’s commission statement quietly failed for about a quarter of real policies. None of that was visible from the outside, which is exactly why it lasted.',
    stats: [
      { value: '13', label: 'reported items delivered', note: 'across four portals' },
      { value: '4', label: 'features that had never worked', note: 'for anyone, since they shipped' },
      { value: '5,300+', label: 'automated checks passing', note: 'about 190 written for this release' },
      { value: '5', label: 'database changes', note: 'all additive; nothing existing is rewritten' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Four things that had never worked',
        blurb: 'Not broken recently. Broken since they shipped.',
        changes: [
          {
            title: 'Three brokers could not create a channel at all',
            detail:
              'Adding an agent produced a server error every time, and only for brokers whose own code is long — the identifier the system builds for a new agent did not fit the space reserved for it. Eighteen failures are in the logs. The identifier is now built to fit whatever the broker is called.',
          },
          {
            title: 'Adding an activity to a lead was always rejected',
            detail:
              'The form sent an activity type the system does not have, so “Please select a valid Activity Type” appeared however carefully you filled it in — there was no Activity Type field to get wrong. There is one now, listing the real types, and the timeline icons match them (most rows had been drawing a blank circle).',
          },
          {
            title: 'An agent’s submitted documents were being discarded',
            detail:
              'Submitting KYC documents accepted the files, measured them, threw them away, moved the application to “submitted” and reported success. The documents never existed. Uploads are now stored, and submitting refuses — naming what is still missing — when there is nothing behind it.',
          },
          {
            title: 'A custom channel type was lost on the way in',
            detail:
              'A channel created under a custom type you had defined came out as a plain POSP, with no memory of the type. It now keeps the type it was created under while still behaving as the built-in type that type is based on.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Commission Reconciliation',
        blurb: 'Renamed, and made to actually match.',
        changes: [
          {
            title: 'A quarter of policies could never be matched',
            detail:
              'Statement lines were compared to your policies character by character. But an insurer’s monthly statement is produced by a different system than the one that issued the policy, and writes the same number differently — “2302 2088 8803 6000 000” against 2302208888036000000. Of the policies carrying an insurer’s own number, 180 of 722 are punctuated that way, concentrated in ICICI Lombard, Tata AIG, HDFC Ergo, Shriram and Magma HDI. Every one of their lines came back unmatched with no hint the policy was sitting right there. Matching now ignores spacing and punctuation, within the same insurer and your own book.',
          },
          {
            title: 'A failed load no longer reads as “no discrepancies”',
            detail:
              'The comparison now distinguishes loading, failed and genuinely empty. On money, those three are not the same answer.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'KYC documents, end to end',
        blurb: 'Upload, read, verify — with the reading never mistaken for a fact.',
        changes: [
          {
            title: 'Documents on contacts, channels and both portals',
            detail:
              'A contact, an agent and a customer can all upload PAN, Aadhaar (front and back), photo and IRDAI certificate. The agent’s and the broker’s screens read the same records, so what an agent uploads appears on the broker’s channel page, and the broker’s decision and remarks appear back on the agent’s own portal. A customer sees their status and remarks read-only; only the broker and its agents can verify.',
          },
          {
            title: 'Details are read off the document, and then confirmed by a person',
            detail:
              'Uploading reads the document and offers what it found — it does not fill your KYC fields behind your back. A phone photo confuses 0 with O and 8 with B, and an Aadhaar number has no way to check itself, so a misread would otherwise become a stored compliance fact nobody had looked at. Nothing reaches the contact record until somebody confirms it, and who confirmed it is recorded.',
          },
          {
            title: 'Uploaded files are no longer stored under their own names',
            detail:
              'A document kept the filename it was uploaded with, which made its address on the server guessable. New uploads are stored under an unguessable name; the original name is still shown to you.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Life, Health and Term policies pay in instalments',
        blurb: 'One policy, one number, for the whole premium-paying term.',
        changes: [
          {
            title: 'A payment schedule instead of a new policy each time',
            detail:
              'Set a frequency — monthly, quarterly, half-yearly or annually — and how many payments, and the schedule is created against the policy. Renewals now update the next payment on the existing policy rather than minting a new record, so the policy number never changes. The screen shows the end date the two settings actually add up to, so what you configured is visible before you save.',
          },
          {
            title: 'A policy no longer expires just because a date passed',
            detail:
              'For a policy still being paid, the end date is a projection of the schedule, not a fact. What ends the cover is a missed payment whose grace period has run out — nothing else. Once every payment is made the policy is marked Matured and, on a limited-pay plan, stays in force for the rest of its cover.',
          },
          {
            title: 'Grace period comes from the product, not from us',
            detail:
              'The number of days is set on the product, falls back to the insurance company, and only then to a statutory default. Whatever applied is recorded on each instalment, so re-pointing a product later cannot move a date a policy has already been judged on. Reinstating a lapsed policy is deliberately a separate, manual step.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Leads, contacts and the vehicle list',
        blurb: 'Smaller things, several of which were quietly wrong.',
        changes: [
          {
            title: 'Assign a lead to your team',
            detail:
              'From the lead itself or in bulk from the list. Before wiring this up we found the underlying endpoint would accept ANY user on the platform, so a lead could be handed to another broker’s employee; it is now restricted to your own team.',
          },
          {
            title: 'Leads is a right you can grant per employee',
            detail:
              'Off unless you turn it on, and a Broker Admin can never be locked out of their own pipeline. The switch that was supposed to make this general only ever worked for Reports — any other permission declared on a menu item was being silently ignored.',
          },
          {
            title: 'The KYC filter finds completed contacts',
            detail:
              'It offered “Verified”, which is a rarely-used manual state, while contacts whose KYC actually finishes are marked Completed — so the filter returned almost nothing however many were done. Renamed, and it now finds both.',
          },
          {
            title: 'The vehicle list shows more of the vehicle',
            detail:
              'Customer, manufacturing year, engine and chassis numbers and registration date, alongside the mapped make, model and variant.',
          },
          {
            title: 'Change Request has moved into Servicing Requests',
            detail:
              'It was a second way into the same screen, and three of its four shortcuts pointed at request types that do not exist — so those links could only ever show you an empty or unfiltered list. Every change you can ask for is a request type on the one screen.',
          },
        ],
      },
    ],
    footnote:
      'Two things we have not done, said plainly. Uploaded documents are served without a login check — the new unguessable filenames make them hard to find, but anyone holding a link can still open one, and closing that properly is separate work we would rather do deliberately. And the instalment schedule is new: no policy uses it yet, so its day-to-day behaviour has been proven by testing rather than by use.',
  },
  {
    tag: 'RELEASE / 2026.09.07',
    date: '7 September 2026',
    headline: {
      lead: 'A vehicle database that holds vehicles,',
      accent: 'and Suspend that actually suspends.',
    },
    summary:
      'Seven items from your list. Four of them turned out not to be the thing the report described, and those are the interesting ones. The Segment dropdown was already meant to follow the vehicle category — but every commercial category shared one list of words, so a goods carrier was always offered “Passenger Carrier”. “Vehicle Database” led to five catalogue lists, so a broker searching for a vehicle number could not find it anywhere. And the buttons that suspend or block a broker were, until now, close to decoration: nothing stopped a suspended broker’s team from signing straight back in through the main login form. That last one is fixed, along with two other ways the same control could be got around.',
    stats: [
      { value: '7', label: 'reported items delivered', note: 'from the list sent on 6 September' },
      { value: '3', label: 'ways round Suspend closed', note: 'found while making the buttons work' },
      { value: '5,146', label: 'automated checks passing', note: 'about 60 written for this release' },
      { value: '2', label: 'database changes', note: 'both additive; nothing existing is rewritten' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Suspending a broker now suspends them',
        blurb: 'The control existed. What it did was change a badge.',
        changes: [
          {
            title: 'A suspended broker’s team could still log in',
            detail:
              'The broker login checked whether the organisation was active. The main login form — the one almost everyone uses — never looked at the organisation at all. So suspending or blocking a broker changed the status shown on your list and stopped nobody: the admin, the employees and the agents all carried on. It now applies everywhere. A customer of that broker is deliberately unaffected: their cover is in force whatever has happened to the intermediary, and they can still open their own policy.',
          },
          {
            title: 'A suspended broker could switch themselves back on',
            detail:
              'A broker admin is allowed to edit their own organisation’s details, and the status was one of the fields that edit could write. It no longer is. Status changes through one route, which only a Country Admin can use, and every change is recorded with who made it and why.',
          },
          {
            title: 'Anyone signed in could suspend any broker',
            detail:
              'The suspend and activate endpoints carried no permission check of their own. They have been replaced. There was also no way to block a broker at all, although the status had always existed in the system.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Country Admin manages brokers, not people inside them',
        blurb: 'One section removed, and the actions it held moved to where they belong.',
        changes: [
          {
            title: 'Edit, Suspend and Block sit on the broker’s own row',
            detail:
              'All Brokers now carries them directly, with a confirmation that says what the action does rather than just asking whether you are sure — suspending an organisation takes its whole team offline, which is more than the button implies.',
          },
          {
            title: 'User Management is gone from the Country Admin panel',
            detail:
              'For a Country Admin that list was already the broker list wearing a different name, and it invited reaching past a broker into that broker’s own employees and agents — which are the Broker Admin’s to run. Insurer staff are unaffected and keep their existing screen.',
          },
          {
            title: 'A broker’s address was never shown',
            detail:
              'The broker page asked for a field the system does not send, so the address panel was invisible no matter how complete the record was. Noticed while working nearby, and fixed.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Vehicle Database now holds vehicles',
        blurb: 'It used to hold the catalogue.',
        changes: [
          {
            title: 'Your vehicles, by vehicle number',
            detail:
              'The menu led to five master lists — Makes, Models, Variants, Fuel Types, Body Types — which describe what vehicles exist in the world. Searching them for MH01AB1234 found nothing, because that is not what they are. Vehicle Database is now your own book: every vehicle you have insured or recorded against a customer, searchable by its number, with the make, model and variant already mapped.',
          },
          {
            title: 'The history behind one vehicle',
            detail:
              'Opening a vehicle shows the policies you have written on it, and — where a registration lookup has been bought — the ownership trail and the insurance trail behind it, including cover placed by somebody other than you. Where nothing has been bought, the page says so rather than implying the vehicle has no history.',
          },
          {
            title: 'One agent cannot see another agent’s customers',
            detail:
              'An agent sees the vehicles on policies they sold, matching the rule that already governs which policies they can open.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Renewals and leads stop asking twice',
        blurb: 'Both screens already held the answer and asked for it again.',
        changes: [
          {
            title: 'A renewal quote opens with the vehicle already in it',
            detail:
              '“Create renewal quote” has been on the policy list and the renewal page for a long time, and it opened an empty form: the link carried which policy was being renewed and the quote form never read it. It does now — customer, vehicle and the previous policy’s details all arrive filled in, from both screens.',
          },
          {
            title: 'A lead can hold the vehicle it is about',
            detail:
              'A motor lead now records the vehicle number, make, model, variant, fuel type and year, and the lead page shows them. The fields existed in the system and no screen had ever used them, so a motor lead reached whoever picked it up as a name and a phone number.',
          },
          {
            title: 'Creating a policy from a lead carries the lead across',
            detail:
              'Straight from the lead to Add Policy, with the customer and the vehicle already entered. Where a lead recorded the vehicle loosely — “a Swift, the diesel one”, taken down over the phone — the form fills in what it can match and leaves the rest blank with the lead’s own wording shown beside it, rather than guessing a variant. A guessed variant changes the premium.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Two things that were simply wrong on screen',
        blurb: 'Small, visible, and reported.',
        changes: [
          {
            title: 'The Segment list matches the vehicle category',
            detail:
              'Choosing “Goods Carrying Vehicle” offered “Passenger Carrier”, and the other way round. Every commercial category shared a single list of segments, and the form was also asking for the segments of everything the manufacturer makes rather than the vehicle in front of you. Each category now offers only its own words.',
          },
          {
            title: 'Paid is off the policy page',
            detail:
              'A policy is issued only once payment has gone through, so a second paid-or-unpaid state printed on the policy could only ever agree with that or contradict it. The badge, the “Mark Paid” button and the two payment stages are gone from the policy screens. Nothing is deleted: payments and their own statuses are unchanged on the Payments tab, and any older policy still sitting in a payment stage continues to show it.',
          },
          {
            title: 'A stray line of code was being printed on the Quotes page',
            detail:
              'The words “/* Quotes Table */” appeared above the list. They were a developer’s note that had been written in a way the page treated as text to display.',
          },
        ],
      },
    ],
    footnote:
      'Not done, and worth saying: the Vehicle Database shows what you already hold, and does not yet offer to buy a registration lookup for a vehicle you do not. The billing for that exists and works elsewhere; where the button belongs on this page is a decision rather than an oversight. Separately, someone already signed in when their broker is blocked keeps working until their session expires — blocking prevents the next sign-in, not the current one.',
  },
  {
    tag: 'RELEASE / 2026.09.04',
    date: '4 September 2026',
    headline: {
      lead: 'One place for a customer,',
      accent: 'and screens that stop saying “none” when they mean “I could not tell”.',
    },
    summary:
      'Ten items from your list, plus the agent page you reported as empty. Most of it is about one idea: everything you know about a customer should be on the customer, and everything a screen tells you should be something it actually knows. Leads from your website used to arrive stripped of the vehicle and the source the visitor gave you. Editing a lead confirmed the change and threw two fields away. An agent’s page showed no money at all, while the platform held every payment and every balance one screen out of reach. Along the way a review found nineteen further faults in this release before it shipped — including several screens that answered a failed request with a confident “there is none”, which on money and cover is the most expensive kind of wrong.',
    stats: [
      { value: '10', label: 'reported items delivered', note: 'from the list sent on 3 September' },
      { value: '19', label: 'faults caught before release', note: 'by an adversarial review of the work itself' },
      { value: '4,704', label: 'automated checks passing', note: 'about 220 written for this release' },
      { value: '0', label: 'database changes', note: 'nothing to migrate; the release is additive' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'A customer in one place',
        blurb: 'Contact Details is now the whole picture, not just the policies.',
        changes: [
          {
            title: 'Quotes, policies, claims and renewals on the contact',
            detail:
              'A contact’s page carries four tabs, each with its own count and its own paging, and every row opens the record it names. Before this, three of those four could not be asked for by contact at all — the information existed, but nothing could fetch one person’s share of it.',
          },
          {
            title: 'Start a quote, a policy or a claim from the contact',
            detail:
              'Four actions sit at the top of every contact, and the same four appear in the Contacts list once you tick a single row. Raising a claim asks which policy it is against before opening the form — a claim belongs to a policy, and a contact with several has several possible answers.',
          },
          {
            title: 'Converting a lead now carries what the lead knew',
            detail:
              'The visitor’s own description of what they wanted, their language preference, and — importantly — a decision not to be contacted. That last one was being dropped, so somebody who had asked not to be contacted arrived on your contact list contactable. Quotes already raised for that person are attached to the new contact instead of belonging to nobody.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Leads from your website arrive whole',
        blurb: 'What the visitor typed now reaches the person who picks the lead up.',
        changes: [
          {
            title: 'Source was empty on every lead your public site has ever captured',
            detail:
              'The form recorded where a lead came from and the record could never store it, so the Source column has been blank since the feature shipped — and the per-source counts every report is built on have been reading zero. Both work now.',
          },
          {
            title: 'The vehicle a visitor looked up now reaches the lead',
            detail:
              'Registration, make, model and year were being written into a note rather than the fields that filters, reports and quote forms read. The enquiry form also now asks what kind of cover the visitor wants; without it, a health enquiry reached your desk labelled Motor.',
          },
          {
            title: 'Editing a lead saves Source and Campaign',
            detail:
              'The edit form asked for both, said “Lead updated successfully”, and stored neither. It had worked on the day the lead was created and could never be corrected afterwards.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'The agent page you reported',
        blurb: 'It was empty, and for two separate reasons.',
        changes: [
          {
            title: 'Ten of your agents could not be opened at all',
            detail:
              'The page found an agent by looking through the first twenty on your list. You have thirty, so the last ten showed “Channel not found” — while everything behind the page worked perfectly. Any agent opens now, however long the list gets.',
          },
          {
            title: 'An agent’s money is on their page',
            detail:
              'The commission ledger — what was earned, what was paid, what tax was deducted, and the running balance — with what is still owed, the payments already made, and a download. None of this data is new; it was one screen away behind a filter nobody would think to set from an agent’s page.',
          },
          {
            title: 'Agents were being told they had never been paid',
            detail:
              'The payments section asked for a record that does not exist, and read the silence as “nothing has been paid”. One agent had been paid twice. This was also happening on the agents’ own portal, where it predates this release, and is fixed there too.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Screens that said “none” when they meant “I could not tell”',
        blurb: 'Found by reviewing this release before it shipped, not after.',
        changes: [
          {
            title: 'A failed request no longer reads as an empty result',
            detail:
              'Five places would answer a failed or unfinished request with a definite “there is none”: an agent’s outstanding balance shown as ₹0, their payments, a contact’s quotes, claims and renewals, the counts beside those tabs, and the list of policies to claim against. Not knowing something and knowing there is nothing are different facts, and on money and cover only one of them is safe to state.',
          },
          {
            title: 'Raising a claim showed only the first twenty policies',
            detail:
              'One of your contacts holds a hundred and seventy-three. The rest were not missing from the system, only from that list — so “my policy isn’t here” would have read as “it isn’t covered”.',
          },
          {
            title: 'Channel Types now refreshes when you change it',
            detail:
              'Creating or deleting a channel type confirmed the change and left the page exactly as it was. Each type also lists the channels that sit under it, including the built-in types — which is where all thirty of your agents live.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Two duplicates removed',
        blurb: 'Both asked for, both removing a second way to do one thing.',
        changes: [
          {
            title: 'One Add Channel button, in the Channels section',
            detail:
              'The second one lived under Manage Channel Types, which is a page about types rather than about creating channels.',
          },
          {
            title: 'Quote Requests is no longer a separate tab',
            detail:
              'Everything it listed is still reachable where it belongs: requests raised by your customers are on the Quotes list already, and enquiries from your public site are on Leads. Creating a quote from a lead now goes through converting them to a contact first, which is what gives the quote somebody to belong to.',
          },
        ],
      },
    ],
    footnote:
      'Two things deliberately not done. The customer portal that gathers a person’s insurance across several brokers is not part of this release — it needs each broker’s consent and a daily sync, and nothing here pretends to it. And a broker’s space stays sealed: a contact signing in to your site sees your records only, which is why the work to reach a customer’s records was written to be scoped to one broker from the start.',
  },
  {
    tag: 'RELEASE / 2026.09.03',
    date: '3 September 2026',
    headline: {
      lead: 'Twenty-three items from your list,',
      accent: 'and fifteen faults nobody had reported.',
    },
    summary:
      'This release started as a list of twenty-three things you asked for — leads in the sidebar, an archive for dead policies, a customer added from the policy form that actually reaches your contacts, product pages that keep what you type. All of those are here. What took the longest was what turned up on the way. Fifteen separate faults were found that nobody had reported, because every one of them looked like success from the outside: a screen that confirmed a save and stored nothing, a quote that priced without the discount the customer had earned, a policy that reported itself issued and was not. They are described below in plain terms, because each one has been quietly costing somebody something.',
    stats: [
      { value: '23', label: 'reported items delivered', note: 'from the two lists sent on 2 September' },
      { value: '15', label: 'faults found that were never reported', note: 'each one looked like it was working' },
      { value: '4,897', label: 'automated checks passing', note: 'about 400 of them written for this release' },
      { value: '5', label: 'database changes', note: 'all tested against a full copy of live data first' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Things that said yes and did nothing',
        blurb: 'The pattern behind most of this release. An action confirms, and nothing is saved or sent.',
        changes: [
          {
            title: 'Discounts customers had earned were being left out of quotes',
            detail:
              'Every quote started from the public site was sent to the insurer as first-time business, whatever the customer told us. Someone renewing with years of claim-free driving was priced as though they had none, and the quote came back higher than it should have. The quote itself looked perfectly normal, which is why this was never spotted.',
          },
          {
            title: 'Policy issuance reported failure and asked the insurer to try again',
            detail:
              'Issuing a policy through an insurer’s system failed every time, on a detail of how the premium figures were written to our own records. The failure was reported as the insurer’s, and the request was then retried — asking the insurer to issue the same policy a second time. Fixed, along with a related fault that was writing a customer’s identity number into our logs in plain text.',
          },
          {
            title: 'No-claim slabs entered against a product were never saved',
            detail:
              'Saving a product returned success without telling the page which product had been saved, so every no-claim slab entered alongside it was filed against nothing and lost. The product saved, the slabs vanished, and the screen showed an error while appearing to have worked.',
          },
          {
            title: 'Converting a lead to a customer would have failed on the first attempt',
            detail:
              'The conversion could not run at all, single or in bulk, because of how it locked the record it was reading. Creating a lead also returned success without an identifier, so opening the new lead led to a blank page — and a company lead could not be created at all, because a surname was demanded that a company does not have.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Records kept apart, and who may do what',
        blurb: 'Three boundaries that were not where they should have been.',
        changes: [
          {
            title: 'Insurance company staff lists are now visible only to those accountable for them',
            detail:
              'The list of an insurance company’s employees could be read by anyone signed in to the platform, including brokers and agents from elsewhere. It is now restricted to the people responsible for that company. Separately, seven staff logins existed while only one appeared in the list — all seven now show, so the person accountable can see them.',
          },
          {
            title: 'Removing an insurance company employee now actually removes their access',
            detail:
              'Deleting a staff member took them off the list and left their login working. Anyone doing this reasonably believed access had been withdrawn. It had not. Removal now suspends the login and detaches them from the company, in one step that cannot half-happen. Where the same person holds another role on the platform, only the company link is removed.',
          },
          {
            title: 'Policy issuance is limited to the people whose business it is',
            detail:
              'The route that asks an insurer to write a real policy accepted any signed-in account, including a customer holding a login on their own quote. It is now limited to the broker, their authorised staff, and the agent who sold the business — who is still tied to their own broker and cannot act for another.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'What you asked for',
        blurb: 'The twenty-three items from your list.',
        changes: [
          {
            title: 'A customer added while writing a policy now reaches your contacts',
            detail:
              'The policy form asked for a handful of details and the customer never appeared in Contacts. It now shows the same fields Contacts does, and the customer is created and linked to the policy as you save it.',
          },
          {
            title: 'Old policies can be archived instead of filling the list forever',
            detail:
              'Expired policies stayed in one list with everything else. They can now be archived, individually or in bulk, and brought back. Nothing is deleted. Alongside that, a policy whose cover has ended no longer counts as Active, and one whose cover has not started yet says Upcoming rather than pretending to be live — so your active count will drop, and it will be right.',
          },
          {
            title: 'Renewed policies no longer appear as lapsed',
            detail:
              'A policy you had already renewed still sat in the Lapsed list, because nothing recorded that the newer policy replaced it. The list now recognises a later policy for the same vehicle, so a renewed policy drops out of Lapsed and out of the count beside it.',
          },
          {
            title: 'The dashboard shows the two counts that were always zero',
            detail:
              'Policies added today and quotes awaiting a response both read zero for everyone, every day. One was asking the system a question that could never return a number; the other was answered correctly and never displayed. Both now show real figures, counted against your own calendar day rather than a clock in another country.',
          },
          {
            title: 'Leads have a place in the menu, and convert in bulk',
            detail:
              'The leads screen existed but could only be reached by typing the address. It is now in the menu, the form captures the same details as Contacts so nothing is retyped, and leads can be selected together and converted to customers in one action.',
          },
          {
            title: 'Reports open a policy without leaving the page',
            detail:
              'Clicking through from a report took you to the policies list and lost your filters. Policy details now open beside the report, and the Unknown group — policies with no product recorded — can be opened like any other, which is the only practical way to work through them.',
          },
          {
            title: 'Commission can be set by revenue band, and recovered when a policy is cancelled',
            detail:
              'Rates can now step by the business written with an insurer, optionally narrowed by claim history and by whether the vehicle is new. And where a policy is cancelled after an agent has already been paid, the amount is recorded against them and set off against their next payment, instead of quietly leaving the books short.',
          },
          {
            title: 'Volume bonuses now count policies that have gone live',
            detail:
              'Business counted towards a volume threshold previously included only policies at the point of issue. It now also counts policies already running, which is what most people assume it meant — so a threshold is reached sooner and the bonus above it starts earlier. This applies to existing bonus arrangements as well as new ones, and was a deliberate decision rather than a side effect.',
          },
          {
            title: 'Targets, and how the month is actually going',
            detail:
              'Targets can be set for each broker and each agent, month by month, and shown against what has actually been written. The achievement figure is the same premium total the dashboard and the reports already use, so the three agree rather than offering three answers.',
          },
          {
            title: 'The platform can send email',
            detail:
              'Nothing on this platform could send anything — no email, no message — whatever was configured. Email now works once your mail details are entered, and every send is recorded with what happened. A message that did not go is reported as not gone, with the reason, rather than shown as sent.',
          },
        ],
      },
    ],
    footnote:
      'Two things were deliberately left alone. Commission figures recorded in the past have not been recalculated: the rate cards on the system stop part-way through the year, so recalculating today would erase commission on roughly ninety policies that genuinely earned it. That is a rate-card review, and it comes first. And where a quote is shared or a policy sent by link, the link is generated for you to send — automatic sending follows once mail details are in place.',
  },
  {
    tag: 'RELEASE / 2026.08.28',
    date: '28 August 2026',
    headline: {
      lead: 'Motor quotes now price live,',
      accent: 'and the commission behind them is finally itemised.',
    },
    summary:
      'Online motor pricing has been dormant since it was built. The calls left our servers in France, and Go Digit\u2019s network answered them with a gateway error before their systems ever saw them \u2014 so nothing could be priced, and the reason was invisible from the outside. Routing those calls through a machine in Mumbai fixed the geography, and doing so exposed two faults in what we were sending that nothing but a real, answered call could have revealed. A live private-car quote now comes back priced in full. The other half of this release is the commission breakdown: the figures were being worked out and stored correctly all along, and the screen only ever showed one total. Both sides of that money \u2014 what the insurance company pays you, and what you pay your agent \u2014 are now itemised line by line.',
    stats: [
      { value: '\u20b919,526.64', label: 'first live motor quote priced end to end', note: 'a real private car, priced by Go Digit, with the sum insured we asked for' },
      { value: '2', label: 'faults in our request that only a live answer could show', note: 'both had been in place since the integration was written' },
      { value: '716', label: 'policies that can now be shared with their holder by link', note: 'none is shared until you choose to share it' },
      { value: '4,500+', label: 'automated checks passing', note: 'including 40 written for this release alone' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Motor quotes price live',
        blurb: 'Three things stood between a quote request and a premium. All three are gone.',
        changes: [
          {
            title: 'Quotes now reach the insurer from an Indian address',
            detail:
              'Our servers are in France. Go Digit\u2019s network refused those connections at the gateway, which looks identical to the insurer being down \u2014 the request never reached anything that could answer it. Quote traffic now goes out through a machine in Mumbai, and their systems answer it normally.',
          },
          {
            title: 'The sum insured was being sent in a form the insurer could not read',
            detail:
              'We sent the vehicle\u2019s insured value as a plain number where Go Digit expect a structured figure. Their system rejected the whole request over it, so every single motor quote failed \u2014 not just quotes with an unusual value. Nothing on our side could show this, because the rejection only exists once a request actually arrives.',
          },
          {
            title: 'Third-party premium was being recorded as zero on priced quotes',
            detail:
              'Go Digit return the own-damage and third-party halves of the premium under names we were not reading, so a quote that priced perfectly well was stored with a third-party premium of zero. Commission is calculated from exactly those two figures, so this would have quietly under-paid commission on every quote that became a policy.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Commission you can check',
        blurb: 'The numbers were always right. Nothing showed you how they were reached.',
        changes: [
          {
            title: 'See what the insurance company actually paid, line by line',
            detail:
              'A policy\u2019s commission was a single figure. It now breaks down into the parts of the insurer\u2019s own rule that earned it \u2014 own-damage commission, third-party commission, each reward \u2014 each shown with the rate applied and the premium it was applied to, so you can check a payment against the grid you agreed instead of taking the total on trust.',
          },
          {
            title: 'See what your agent is paid out of it, on the same lines',
            detail:
              'The agent\u2019s side is broken down across the same lines, so the two read against each other directly. The figures are apportioned from the payout actually being made \u2014 so where a cap or a fixed amount applies, the parts still add up to the amount that leaves your account, rather than to what an uncapped percentage would have suggested.',
          },
          {
            title: 'A volume bonus now says that it is a volume bonus',
            detail:
              'Bonuses earned by crossing a business volume were already being paid, folded into the total with nothing naming them. They now appear as their own line, which also means you can see one arrive when a threshold is crossed.',
          },
          {
            title: 'Rates can be set by vehicle category and segment',
            detail:
              'Commission rates could be narrowed to an insurer, a product, a make, a model or one exact variant \u2014 but not to a class of vehicle. A rule like "SUVs earn more" had to be entered model by model, or left out of the system entirely. Category and segment are now criteria in their own right, and both appear on the policy so you can see what a rate will match.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Things that reported success and did nothing',
        blurb: 'Three more of them, each accepted from you and then discarded.',
        changes: [
          {
            title: 'A comment on a policy status change is now kept',
            detail:
              'Changing a policy\u2019s status accepted an explanatory note and saved it nowhere. Status changes are now recorded with the reason you gave, who made them and when, and appear in a History panel on the policy \u2014 along with agent changes and other events that were already being recorded but shown nowhere.',
          },
          {
            title: 'Claims no longer ask you which day of the week it was',
            detail:
              'The claim form asked for the day of the week beside the date that already determines it, and then discarded the answer. It is now filled in from the date, and cannot disagree with it.',
          },
          {
            title: 'Adding a policy for an existing contact stops re-asking for what you have already given us',
            detail:
              'Date of birth, gender and pincode were left blank on the new policy even though the contact record held all three. They now carry across \u2014 and a company is not asked for a date of birth or a gender at all, nor shown blank ones on its policies.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Getting to work faster',
        blurb: 'Three places where the platform made you start again.',
        changes: [
          {
            title: 'A policy can be shared with its holder by link',
            detail:
              'Quotes could be shared with a customer by link; the policy the quote became could not. Every policy now has one, and it stays closed until you open it. The page shows the cover, the vehicle and the premium \u2014 deliberately not your commission, and not the holder\u2019s contact details, since a link can be forwarded. You can withdraw it at any time.',
          },
          {
            title: 'An enquiry from your website can be worked up into a quote',
            detail:
              'Enquiries from your public site could be read and nothing else, so quoting one meant starting a fresh quote and re-typing the name, email and phone number already on the screen. There is now a Create Quote button on the enquiry that carries all of it across.',
          },
          {
            title: 'Broker administrators can see the vehicle database',
            detail:
              'The list of makes, models, variants, fuel types and body types was visible only to platform administrators, although brokers are asked to name a make, model and variant on every motor policy. It is now available to broker staff to look up. Changing it remains with the platform administrators, as before.',
          },
        ],
      },
    ],
    footnote:
      'The bonus rules driven by business volume were reviewed and needed no change \u2014 they were already calculating and paying correctly, and what was missing was any sign of them on screen, which this release adds. Sharing a policy PDF over a public link has deliberately not been built: showing a policy\u2019s details and handing out its documents are different decisions, and only the first has been taken.',
  },
  {
    tag: 'RELEASE / 2026.08.26-II',
    date: '26 August 2026',
    headline: {
      lead: 'Agents were being paid nothing,',
      accent: 'and nothing on the screen said so.',
    },
    summary:
      'Set an agent\u2019s payout percentage, and the platform stamped that rate as starting today. Every policy the agent had already sold was suddenly older than their own rate, so it no longer applied \u2014 and the agent silently earned zero while the broker kept the whole commission. It showed up as a blank, which is indistinguishable from an agent who is genuinely owed nothing, so it ran unnoticed. Editing a percentage re-stamped the date again, so the more you maintained an agent, the further back the damage went. That is fixed, the affected commissions have been put right, and a rate saved without a start date now simply applies to everything. The same theme runs through this release: several things reported success while quietly doing nothing at all.',
    stats: [
      { value: '13', label: 'agent commissions restored', note: 'each had a live rate the whole time \u2014 it just could not reach the policy' },
      { value: '\u20b915,137', label: 'moved from broker margin back to agents', note: 'nothing had been paid out, so nobody is out of pocket' },
      { value: '103', label: 'two-wheeler policies on cover in a class we refused to quote online', note: 'a block written for commercial vehicles was catching them too' },
      { value: '4,471', label: 'automated checks passing', note: 'up from 4,396 at the last release' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Agent commission',
        blurb: 'A rate that existed, was correct, and could not reach a single policy.',
        changes: [
          {
            title: 'An agent\u2019s rate no longer expires backwards the moment you save it',
            detail:
              'Setting or editing an agent\u2019s payout percentage recorded that rate as beginning on the day you saved it. Commission is worked out against the policy\u2019s own date, so every policy already on the books fell outside the agent\u2019s rate and paid them nothing. Saving a percentage without a start date now means exactly what it looks like: it applies to that agent\u2019s whole book. You can still set a start date deliberately, and an end date still ends a rate.',
          },
          {
            title: 'A commission of zero is no longer written silently',
            detail:
              'Where an agent has a live rate that does not apply to a policy, the calculation now records that fact instead of quietly showing nothing. Previously a wrongly-zeroed commission and a correctly-empty one looked identical on screen, which is why this went unnoticed for weeks.',
          },
          {
            title: 'The commissions that were wrongly zeroed have been put right',
            detail:
              'Thirteen calculations across several brokers have been recalculated and \u20b915,137 has moved from broker margin to the agents who earned it. None of it had been paid out, so no payment needs reversing and nobody is out of pocket. Nine further policies were deliberately left alone: no commission rule applies to them at all under the rate conditions introduced in the previous release, so recalculating them would have replaced a real figure with a false zero. Those want a look at the rate grid rather than a recalculation.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Adding a policy',
        blurb: 'Every agent is reachable, and a company is no longer asked for its birthday.',
        changes: [
          {
            title: 'The agent list no longer stops at twenty',
            detail:
              'The Agent field loaded only the first twenty agents and searched within those, so a broker with twenty-seven active agents could not select seven of them at all \u2014 they simply were not in the list, whatever you typed. It now searches the full list as you type, by name, code or e-mail, on both the Add Policy form and when reassigning an agent on an existing policy. The agent already on a policy always stays visible, so opening a policy for editing can no longer make its agent look as though it had been cleared.',
          },
          {
            title: 'A corporate contact is treated as a company',
            detail:
              'Linking an existing contact to a policy carried across their name, phone and e-mail but not the fact that they were a company, so the form went on asking a limited company for its date of birth and gender. The contact\u2019s type now carries across, those fields are hidden for companies, trusts, partnerships, LLPs and HUFs, and the type is saved with the policy so it survives being reopened. A proprietorship still shows them, deliberately \u2014 in law a proprietorship is its proprietor, and the Contacts screen keeps those fields for that type too.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Contacts',
        blurb: 'Vehicle numbers, what is coming up for renewal, and a place for finished policies.',
        changes: [
          {
            title: 'The vehicle number appears beside each policy',
            detail:
              'A contact holding hundreds of motor policies could see policy numbers, premiums and expiry dates but not which vehicle each one was for, which made the list almost unusable for a fleet. The registration number is now a column.',
          },
          {
            title: 'Upcoming renewals now appear on the contact',
            detail:
              'There was no renewals view on a contact at all, and the underlying list had been filtering on a status that almost no policy in the system actually has \u2014 so it would have come back empty regardless. The contact now shows its soonest renewals with the days remaining on each, plus counts for the next fifteen and thirty days. It shows the nearest ones whatever the distance, rather than an empty panel on a book whose renewals are months out.',
          },
          {
            title: 'Live policies are separated from finished ones',
            detail:
              'Active and archived policies now sit on separate tabs, using the same date-based rule the Policies page already uses \u2014 including that a policy cancelled before its expiry date counts as finished rather than active. Policies held under duplicate records of the same contact stay visible rather than disappearing from the list.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Claims',
        blurb: 'Say what is happening, not just which stage it reached.',
        changes: [
          {
            title: 'A stage change can carry an explanation, and notes can be added between stages',
            detail:
              'Updating a claim offered a stage and nothing else, so the only way to record anything was to move the claim. You can now write what happened alongside a stage change, and add as many notes as you like without changing the stage at all. Everything appears on the claim\u2019s existing timeline with who wrote it and when.',
          },
          {
            title: 'You choose what the customer sees',
            detail:
              'Notes you type are internal unless you mark them as visible to the customer, so working remarks stay between colleagues. A rejection reason is always shared, because it is addressed to the claimant. The automatic progress entries a customer could already see on their own claim are unchanged and still visible \u2014 nothing that was visible has been taken away.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Quotes',
        blurb: 'Work the options before the customer sees them.',
        changes: [
          {
            title: 'Add and edit premium options on the quote itself',
            detail:
              'Options priced through an insurer\u2019s system and options typed in by hand have always sat together on the same quote, but there was no way to add or adjust one from the quote screen. You can now add an option with its document, edit it, and mark one as recommended before the quote goes anywhere.',
          },
          {
            title: 'Three buttons that reported success while doing nothing',
            detail:
              'Marking an option as recommended saved the choice and confirmed it, and nothing anywhere read it \u2014 the comparison screen still sorted purely by price. Recommended options now lead that screen and are marked. \u201cSend to Customer\u201d created the customer\u2019s link, threw the link away and said the quote had been sent; it now creates the link, shows it, and offers WhatsApp and e-mail, which are what actually send. And an option\u2019s expiry was displayed one day later than it was stored, so correcting it pushed it a further day each time you edited.',
          },
        ],
      },
      {
        tag: '06',
        heading: 'The public quote page',
        blurb: 'Correct your vehicle, and get a two-wheeler quoted.',
        changes: [
          {
            title: 'You can correct the vehicle we matched',
            detail:
              'When a registration matched the wrong variant, the page had already worked out every alternative and sent them to your browser \u2014 and then never showed them. There was no way to say \u201cthat is not my car\u201d. The choice is now offered whenever there is a real one to make, including when two variants share the same name.',
          },
          {
            title: 'Two-wheelers are no longer turned away',
            detail:
              'A restriction written for commercial vehicles \u2014 which our insurer genuinely does not cover on this account \u2014 was also blocking two-wheelers, which are the most-quoted class on the platform. They are no longer blocked, and the two situations now read differently: a class the insurer declines is described as exactly that, rather than as something we cannot handle.',
          },
        ],
      },
      {
        tag: '07',
        heading: 'Protecting the platform',
        blurb: 'Limits that count the right visitor.',
        changes: [
          {
            title: 'Rate limits and blocks now identify visitors reliably',
            detail:
              'Several protections \u2014 the pause between form submissions, request rate limits, and address blocking \u2014 worked out who was calling in a way that a caller could influence. They now use a value only our own servers set, so a limit applies to the visitor it was meant for and cannot be shifted onto somebody else. One consequence worth stating: a busy period can no longer cause one visitor\u2019s limit to lock out everyone else.',
          },
        ],
      },
    ],
    footnote:
      'Still not live, and worth saying plainly: online motor pricing through Go Digit remains switched off. Every step of the public quote journey now works \u2014 registration lookup, vehicle match, correcting the variant, creating the quote \u2014 but the connection that carries the request to the insurer is dormant and needs a key set on the India relay before any visitor sees a real premium. Two-wheelers being unblocked prepares for that; it does not price them today. Also examined and deliberately left alone: the insurer commission statement reconciliation, which already works end to end, and 192 stored commission calculations that disagree with the current rules \u2014 those are a rate-grid review, not something to recalculate in bulk.',
  },
  {
    tag: 'RELEASE / 2026.08.26',
    date: '26 August 2026',
    headline: {
      lead: 'Commission rules that were written down',
      accent: 'and never actually applied.',
    },
    summary:
      'If you set a commission rate for a weight band, an age range or a vehicle type, the platform recorded those conditions and then ignored them. A policy that failed them was not skipped — it quietly fell through to whatever broader rate sat beside it, and nothing on the screen said so. On one book that turned a 25% rate written for 3.5–7.5 tonne vehicles into a flat 10% paid on 14–40 tonne trucks, across 191 policies. Rates now mean what they say: every condition you fill in has to match, and where none does, the policy reads "no commission calculated" instead of inventing a number. Alongside that, quotes can now become policies — which, despite appearances, had never once happened.',
    stats: [
      { value: '191', label: 'policies priced by a rate that had rejected them', note: 'commission voided; none of it had been paid out' },
      { value: '₹7.16 lakh', label: 'of commission that was never really earned', note: 'the rate it was paid under excluded every one of these vehicles' },
      { value: '0 of 716', label: 'policies that had ever come from a quote', note: 'the conversion existed in code and nothing called it; it works now' },
      { value: '4,396', label: 'automated checks passing', note: 'up from 4,094 at the last release' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Commission',
        blurb: 'The rules you configure are now the rules that get applied.',
        changes: [
          {
            title: 'Rate conditions are enforced instead of recorded and ignored',
            detail:
              'A rate can carry a weight (GVW) band, an age range, a seating range, an engine capacity range, an NCB setting, a New/Old vehicle condition and a policy type. Until now, a policy that failed one of those was not refused — the rate was dropped and a broader one beside it paid instead, with no note anywhere. Every condition you fill in must now match before that rate pays, and a rate with no conditions filled in behaves exactly as it always has. Where nothing matches, the policy says so rather than being priced by a rule that excluded it.',
          },
          {
            title: 'A rate that rejects a policy now says so on the record',
            detail:
              'Previously the rejection was discarded the moment any rate matched, so you could not tell a mistyped weight band from a missing one. The rejected rate, the reason, the exact conditions that failed and the rate that got paid instead are all recorded against the calculation now. This moves no money on its own — it is what lets you review a rate grid before changing it.',
          },
          {
            title: 'National Insurance commercial-vehicle commissions have been unwound',
            detail:
              'For one broker, 191 Standalone TP policies on goods-carrying vehicles had been paid a flat 10% by a rate that excluded them on weight. Those calculations have been reversed and the policies now read "No commission calculated for this policy yet" and no longer appear on the Commissions page. None of this commission had been paid out, so nobody is owed or owing as a result. The three near-identical rate rows that produced it are still in the grid and want a look.',
          },
          {
            title: 'The commission breakdown is shown, not just the total',
            detail:
              'Where a rate sets OD Premium %, TP Premium %, OD Reward % or TP Reward %, the policy’s Commission section now lists each one separately as well as the total. Only the components actually configured on the rule are shown — an unset reward is absent rather than displayed as zero, which previously made "no reward configured" and "a reward of nothing" look identical.',
          },
          {
            title: 'Adding a rate is a shorter form that matches what is used',
            detail:
              'The Add Rate screen for an insurance company now lists the newest rate first, filters by Effective From and Effective To, and asks for a Product — showing only products belonging to the insurance type you picked — instead of the Group, Subgroup and Policy Type fields it used to ask for.',
          },
          {
            title: 'POSP payouts can be recorded as paid by cash',
            detail:
              'By Cash sits alongside the existing payment methods wherever a POSP payout is settled, and is kept with the payout record and in reports. Worth noting because the screen previously accepted a payment method it then discarded, reporting the payout as completed over an unchanged record.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Quotes becoming policies',
        blurb: 'A step everyone assumed was working, which had never run once.',
        changes: [
          {
            title: 'A quote can now actually become a policy',
            detail:
              'The conversion has existed in the software for a long time and nothing ever called it. Of 716 policies on the platform, none carried a link back to the quote it came from, and no quote had ever been marked converted — so the issued policy number never appeared on a quote, and anything that depended on it silently did nothing. Issuing now creates the policy, links it to the quote, and records the insurer’s own policy number and document against it. A quote with no customer on record still cannot convert, which is deliberate: that produced policies with no holder.',
          },
          {
            title: 'Premium options are no longer forced on every quote',
            detail:
              'The Premium Options step was mandatory even when the premium was going to come from the insurer. It is optional now, and where an insurer connection is configured the quote follows the full path — quote, proposal, KYC, payment, document — while still letting you add a premium option by hand alongside whatever the insurer returns.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'A quote can now start on the website',
        blurb: 'New, and honest about the parts that are still waiting on other people.',
        changes: [
          {
            title: 'Visitors can begin their own motor quote',
            detail:
              'A new public page lets someone arrive with a registration number, have the vehicle looked up, enter their details and start a real quote against a broker — captured as a lead rather than an anonymous enquiry. It is reachable from a broker’s public profile.',
          },
          {
            title: 'It tells the visitor the truth at each step',
            detail:
              'Three parts of this journey depend on things outside the platform: live pricing needs an insurer connection that is configured but not yet switched on, the vehicle lookup needs our address approved by the lookup provider, and a verification code cannot be sent until a message provider is connected. Each of those states is shown plainly to the visitor. Nothing invents a premium, and no step reports success it did not achieve.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Policies, renewals and channels',
        blurb: 'Smaller things that were reported, plus a few found next to them.',
        changes: [
          {
            title: 'Lapsed and Upcoming renewals are separate lists again',
            detail:
              'Choosing Lapsed now shows only policies already past expiry, and Upcoming only those still to come, instead of both arriving together. The Status list beside it also offered three values that are not renewal statuses at all — picking one returned an empty list with no explanation. It now offers the statuses that exist.',
          },
          {
            title: 'The dashboard’s renewal month filter was asking for days',
            detail:
              'Picking a month on the Upcoming Renewals panel sent the month’s number as a number of days — so "March" asked for renewals expiring within three days, and "December" within twelve. It always returned something, which is why it looked like it worked. It now filters by the month you picked.',
          },
          {
            title: 'POSP on the Policies list, and a guard on removing one',
            detail:
              'Policies now show which agent is assigned, or "Not Assigned", and can be filtered to either. Changing or removing an agent updates the policy everywhere it is referenced. Where the agent’s payout has already been completed, removal is refused with an explanation and a remark can be recorded instead.',
          },
          {
            title: 'Adding a policy for some life insurers returned an error',
            detail:
              'Saving a policy for an insurer with a long name failed outright. The generated policy number could exceed the space available for it, which ended the save with an error rather than a message. Policy numbers are now bounded and the shape stays valid however long the insurer name is.',
          },
          {
            title: 'Policy documents open in their own tab',
            detail:
              'Viewing a document from a policy opens a new page instead of a panel over the record. Download is unchanged.',
          },
          {
            title: 'Channel types you define yourself',
            detail:
              'Alongside POSP, Advertiser and Consolidator you can now create your own channel type and pick it when adding a channel. Because the built-in types carry real behaviour — how commission is paid and which report a channel appears in — a custom type must declare which of them it behaves as, and the form says what that choice means. Duplicate names are refused.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Records kept apart',
        blurb: 'Described by effect rather than mechanism, as always on this page.',
        changes: [
          {
            title: 'Issuing a policy is confined to your own records',
            detail:
              'The route used to issue a policy through an insurer connection did not confirm that the quote being issued, or the broker it was issued for, belonged to whoever was asking. Both are now checked against the account making the request. This was found and closed before release; the same review confirmed the equivalent path for channel types, where an existing channel could be pointed at another broker’s payout arrangement.',
          },
          {
            title: 'Removing an agent no longer disturbs the broker’s own commission',
            detail:
              'Detaching an agent from a policy that was expired, cancelled or still in draft reversed the broker’s own earned commission for that policy and left it with none at all. It now only unwinds what was genuinely created for that agent.',
          },
        ],
      },
    ],
    footnote:
      'Investigated and deliberately left alone: two items reported as outstanding were already fixed and are noted as such rather than reopened. A control on the Add Channel form offering to link an existing agent was removed rather than repaired — it had never saved anything, and restoring it properly needs a decision about what happens to an agent who already belongs to another channel. Changing an agent on a policy whose payout is already complete remains possible and is under review, because the three sensible answers differ in who ends up out of pocket. Roughly half the stored commission calculations on the platform would price differently if recalculated today; that predates this release, is being investigated, and has deliberately not been "fixed" by recalculating in bulk.',
  },
  {
    tag: 'RELEASE / 2026.08.25',
    date: '25 August 2026',
    headline: {
      lead: 'Buttons that said it worked',
      accent: 'when nothing had happened.',
    },
    summary:
      'This release is mostly one fault repeated in different places: an action that reported success and did nothing behind it. Saving a product cleared its images. Uploading a document to a claim returned a tick and stored no file. A report showed 0.0% whether it had calculated nothing or genuinely found nothing. A connection test said "ok" without contacting anything at all. None of these announced themselves — that is what made them expensive. Every one of them is now either doing the work or saying plainly that it cannot.',
    stats: [
      { value: '54 of 54', label: 'products whose images had been cleared', note: 'every save since a change in June wiped them; the cause is fixed' },
      { value: '19', label: 'reported items closed', note: 'across reports, claims, quotes, notifications and admin' },
      { value: '₹89,106', label: 'of business the channel report was discarding', note: 'it looked for a link that policies do not carry' },
      { value: '0.0% → —', label: 'rates that now admit when there is nothing to measure', note: 'a calculated zero and an uncalculated one looked identical' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Things that reported success and did nothing',
        blurb: 'Four places where the confirmation was real and the work was not.',
        changes: [
          {
            title: 'Saving a product cleared its images and description',
            detail:
              'Opening a product to edit it loaded only part of the record, then saved the whole thing back — so the card image, icon, banners and description were written away empty by the act of saving anything else. Every product on the platform had already been through this. The images themselves are still in your media library; the link from product to image is not, and has to be set again. Editing a product no longer touches a field you did not open it to change.',
          },
          {
            title: 'Documents attached to a claim were never stored',
            detail:
              'The upload reported success and created the row, but the file itself was read only for its size and then discarded. Nothing was kept. Uploads now store the file and it can be opened again afterwards, several documents can be attached to one claim, and the type and size are checked before the upload is accepted rather than after.',
          },
          {
            title: 'Marking a claim document as verified changed nothing',
            detail:
              'The confirmation appeared and the document stayed "pending" permanently, because the verification was being written to a field that does not exist on the record. It now sets the status you can see.',
          },
          {
            title: 'Test Connection reported "ok" without contacting anyone',
            detail:
              'On the insurer settings screen, testing a connection checked only that the software recognised the insurer — it made no call to them. It answered "ok" whether or not they were reachable, which is the least useful thing a diagnostic can do. It now makes a real request and tells you which of three different things happened: it could not reach them, it reached them and the credential was refused, or it worked.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Reports that were measuring the wrong thing',
        blurb: 'Numbers that were wrong, and numbers that were never calculated.',
        changes: [
          {
            title: 'Advertiser and consolidator business was reported as nil',
            detail:
              'Channel performance looked for a channel recorded directly on each policy. No policy carries one — the link runs through the POSP who wrote it — so every channel showed nothing. Following that link surfaces the business that was there all along, and advertisers and consolidators now have their own figures rather than sharing one line.',
          },
          {
            title: 'Conversion and renewal rates always read 0.0%',
            detail:
              'They were not being calculated. The difficulty with showing 0.0% is that it is also a perfectly good answer, so a rate that had never been worked out was indistinguishable from one that had. Both are now calculated, and both show the counts they were calculated from. Where there is genuinely nothing to divide by, the figure reads "—" instead of a zero that looks like a measurement.',
          },
          {
            title: 'A failed report looked the same as an empty one',
            detail:
              'Three places in the reports would catch any problem while gathering data and quietly return zeros, so a report that had broken looked exactly like a report on a quiet month. Problems now surface as problems.',
          },
          {
            title: 'Saved views did not save their filters',
            detail:
              'A saved view kept its name but not the filters you had set, so scheduling one silently sent the whole book instead of the slice you saved. Saved views now keep their filters, show you what they contain, and the scheduling window states what it is actually going to send.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Notifications, and states that were missing',
        blurb: 'Things the platform knew and never told you.',
        changes: [
          {
            title: 'Notifications now arrive for the events that matter',
            detail:
              'Contacts and policies being created, renewals falling due, service requests arriving, quotes being raised and shared, reports finishing — these now raise a notification for the people they concern, on the broker, customer and company sides. They can be filtered by date, marked read individually or all at once, and are not raised twice for the same thing.',
          },
          {
            title: 'Suspending and blocking a user are now different things',
            detail:
              'Previously an account was either active or not, so suspending someone and blocking them left no trace of which you had meant. They are now distinct states, set by the people entitled to set them, and an account that cannot sign in is told why rather than being shown the same message as someone who mistyped their password.',
          },
          {
            title: 'Country administrators can keep notes on a broker',
            detail:
              'Notes and remarks can be kept against a broker and read back later. They are visible to country administrators only — a broker cannot read or edit what has been written about them.',
          },
          {
            title: 'Support tickets have a category and a customer',
            detail:
              'A ticket can be filed under a category and mapped to the customer it concerns, and both appear in the list and on the ticket. Customer service requests are no longer mixed into the support ticket list; they continue in their own section.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Records, rates and repairs',
        blurb: 'Quieter work, some of it structural.',
        changes: [
          {
            title: 'Records kept apart between brokers',
            detail:
              'A check intended to limit certain lists to the broker they belong to had never taken effect, in nineteen places. In some it meant a list was not being narrowed; in others it meant the opposite — a broker administrator was shown nothing at all where they should have seen their own records. Both are corrected.',
          },
          {
            title: 'Commission rates can be set by vehicle detail',
            detail:
              'A motor commission rate can now carry engine capacity, gross vehicle weight, vehicle age, seating capacity, condition, no-claim bonus and location, all optional. A rate with none of them set behaves exactly as it did before. Three of these criteria could previously be entered on a rate and were never checked against the policy.',
          },
          {
            title: 'Fiscal years can be created again',
            detail:
              'Creating or managing a fiscal year failed outright with a "404" error. It works.',
          },
          {
            title: 'Setup wizards keep what they create',
            detail:
              'The onboarding wizard reported that it had created accounts and did not keep them. It now saves them and links them together, and the insurer employee form validates the way the standard user form does — it had been accepting passwords a single character long.',
          },
          {
            title: 'Communication gateways can be configured',
            detail:
              'There was no screen for setting up an email or WhatsApp gateway at all, which is why anything depending on one appeared not to work. Gateways can now be configured, and message templates follow the channel they are written for. Sending is not enabled yet: no provider is connected, and the platform says so plainly rather than accepting a message it cannot deliver.',
          },
        ],
      },
    ],
    footnote:
      'Two things were looked at and deliberately left alone. Popular-plan images were reported as stretched and were already being cropped correctly — no change was needed. And quotes to Go Digit are not yet live: the groundwork is in place, but a step outside the platform remains outstanding, and until it is done the platform will tell you a quote failed rather than appear to succeed.',
  },
  {
    tag: 'RELEASE / 2026.08.19',
    date: '19 August 2026',
    headline: {
      lead: 'Screens that were showing you',
      accent: 'less than they knew.',
    },
    summary:
      'Most of this release is information the platform already held and was not putting on the screen. A claim knew its customer\u2019s name and showed the claim type in its place. The ledger knew which account each line belonged to and printed none of them, so one policy read as the same record three times over. A quote knew who it was for and said "no customer". None of it was lost data \u2014 it was data that never reached the page. Alongside that, calculating commission across a large book no longer times out, and several filters that looked like they worked now actually do.',
    stats: [
      { value: '3', label: 'columns the claims list was leaving blank', note: 'customer, incident date and priority' },
      { value: '30s \u2192 none', label: 'time limit on calculating commission', note: 'it now runs in the background and reports progress' },
      { value: '23 of 28', label: 'quotes that now name their customer', note: 'was 16; the rest are named below' },
      { value: '0', label: 'filters that quietly narrow nothing', note: 'four that did have been removed or fixed' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Lists that were hiding what they knew',
        blurb: 'Three screens where the record was right and the page was not.',
        changes: [
          {
            title: 'The Customer column on a claim was showing the claim type',
            detail:
              'On the claims list, the Customer column was empty for every claim, and the claim type sat directly beneath it \u2014 so "own_damage" appeared where a person\u2019s name belonged. Incident Date and Priority were blank in the same way. The claim detail page had always shown all three correctly, because it asked for them and the list did not. The list now asks, and the claim type reads as "Own Damage" rather than as a stored value.',
          },
          {
            title: 'A claim and a policy show the same policy number',
            detail:
              'The Policies module leads with the number printed on the insurer\u2019s document; claims were showing our internal reference instead. The same policy therefore appeared under two different-looking identifiers depending on which screen you were on. Claims now follows the same rule. The claims themselves were correctly linked throughout \u2014 only the number on display differed.',
          },
          {
            title: 'The ledger repeated a policy, and now says why',
            detail:
              'One policy could appear on the ledger three times, with the same number, date and description, differing only in amount \u2014 which reads as duplication. It is not: a policy pays into more than one account, and each line is the broker\u2019s commission, an agent\u2019s share, or a channel\u2019s payout. Those lines have not been merged, because each carries its own running balance and merging them would break the trail. The ledger now names the account each line belongs to, on screen and in the CSV.',
          },
          {
            title: 'Advertiser and consolidator payouts were missing from the ledger',
            detail:
              'Payouts owed to a non-agent channel were being counted in your balance but were not appearing in the ledger you read it from, so the total and the lines under it could not be reconciled. They are shown now. Nothing about the amounts changed.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Quotes now know who they are for',
        blurb: 'A link that was being made and then dropped.',
        changes: [
          {
            title: '"No customer linked to this quote"',
            detail:
              'When a quote was raised, the platform created the contact in your directory \u2014 which is why those people were findable under Contacts \u2014 and then failed to attach that contact to the quote. So the quote showed no customer while the customer plainly existed. Twelve of twenty-eight quotes were in this state. New quotes are attached at the point they are created, and existing ones have been reconnected where the match was unambiguous.',
          },
          {
            title: 'Five quotes were deliberately left unlinked',
            detail:
              'Reconnecting the rest meant matching on name together with a phone number or email address. Five quotes share a test phone or email with a different person\u2019s contact record, so a match on contact details alone would have attached the quote to the wrong person \u2014 and that name would then have gone onto a policy. Those five still read "no customer", which is accurate, rather than being guessed at.',
          },
          {
            title: 'A quote with no customer cannot be turned into a policy',
            detail:
              'Convert to Policy was available on a quote with nobody attached, and the policy it produced had no customer on record \u2014 nothing for renewals, claims or a customer login to attach to later. That button is now withheld until a customer is linked, and the quote shows the proposer\u2019s name and number so you can tell whose it is.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Calculating commission on a real book',
        blurb: 'The button that reported failure while it was succeeding.',
        changes: [
          {
            title: 'Calculate Commission timed out after thirty seconds',
            detail:
              'On a book of any size the calculation took longer than the thirty seconds your browser was willing to wait, and reported "Timeout of 30000ms exceeded". The work usually carried on and finished on the server \u2014 which is the worst version of this: the button reported an error at the moment it was succeeding, and the natural response was to press it again.',
          },
          {
            title: 'It now runs in the background and tells you where it is',
            detail:
              'Pressing Calculate Commission starts the run and returns immediately. The page shows how many policies it has worked through of how many, and you can leave the page and come back to it. If someone else has already started a run, you are shown theirs rather than starting a second one over the same policies.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Filters that were not filtering',
        blurb: 'Four controls that narrowed nothing, and the ones worth having instead.',
        changes: [
          {
            title: 'Month and Year on Renewals did nothing at all',
            detail:
              'Choosing a month or a year on the Renewals page changed the list in no way. Both have been removed, along with two date boxes that duplicated the Expiry Window beside them.',
          },
          {
            title: 'The Expiry Window could not reach a lapsed policy',
            detail:
              'Every option on that filter started from today, so a policy that expired last month could not be selected \u2014 which is why "Fetch Pending Renewals" appeared to do nothing: it finds exactly those policies, and the filter then hid all of them. There is now an "Already lapsed" option, and counts above the list showing how many have lapsed against how many are still ahead. A renewal you have completed drops off the list.',
          },
          {
            title: 'Policies can be filtered by insurance type, product and commission status',
            detail:
              'The Policies page could be narrowed by insurance company and not by what kind of insurance it was or which product it was sold under. Both are there now. So is a Commission Status filter, for the question that previously meant opening records one at a time: which policies have not had commission calculated yet.',
          },
          {
            title: 'Raw Status and Payment Status have gone from the Policies filters',
            detail:
              'Both repeated information already shown on every row, and Raw Status listed internal storage values next to a Status filter that speaks in Active and Inactive. Nothing about what you can find has been lost.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Smaller things you asked for',
        blurb: '',
        changes: [
          {
            title: 'A contact cannot be created twice',
            detail:
              'Adding a contact who already exists \u2014 same name, with the same phone number or email \u2014 is now refused, and points you at the existing record. Matching ignores capitals and stray spaces. Two people who genuinely share a name, or a family sharing one phone number, are still both allowed.',
          },
          {
            title: 'A contact\u2019s photo can be set while creating them',
            detail:
              'The photo could only be added by saving the contact, reopening it and editing it \u2014 a picture chosen on the create form was discarded without a word. It is now kept and saved with the contact.',
          },
          {
            title: 'The Actions column on Policies had nothing in it',
            detail:
              'Every row showed a dash. There are now View, Download and Renew actions on each row, so common jobs no longer require opening the record first.',
          },
          {
            title: 'Engine CC and GVW on a motor policy',
            detail:
              'Both are on the Add Policy form now, and neither is compulsory. GVW appears only for commercial vehicles \u2014 it was previously offered on two-wheelers, which have no gross vehicle weight to enter. Policy Number is now required when adding a policy; policies already on the system that have none can still be edited.',
          },
          {
            title: 'Quote documents open where they belong',
            detail:
              'A document attached to one premium option was shown in a single list at the bottom of the shared quote page, so with several options priced there was no way to tell which file went with which. Each now sits with its own option, and opens in a new tab rather than downloading straight away.',
          },
          {
            title: 'Today\u2019s policies, and reports you can open',
            detail:
              'The dashboard shows how many policies were added today and opens that list when you click it. On the Reports page, the Policy view\u2019s rows are now links into the policies behind each number, carrying the dates you were looking at.',
          },
          {
            title: 'The customer list on a new quote stopped at twenty',
            detail:
              'Choosing an existing customer on a new quote offered the first twenty on your book and no way to reach the rest. The same limit had already been fixed on Add Policy and was never carried across. Typing now searches all of them.',
          },
        ],
      },
    ],
    footnote:
      'Records that are kept apart between brokers were reviewed again as part of this work, and two places where that separation was thinner than intended have been closed. Neither is known to have been used, and the data involved was not exposed outside the platform.',
  },
  {
    tag: 'RELEASE / 2026.08.13',
    date: '13 August 2026',
    headline: {
      lead: 'Two things this platform told you',
      accent: 'that were not true.',
    },
    summary:
      'This release is mostly corrections, and two of them are corrections to things we published here. Messages you sent from the platform — a shared quote, an acknowledgement — were recorded as sent when nothing had gone out; earlier notes on this page said otherwise, and they were wrong. And on a motor quote, the list of manufacturers you were choosing from held four names where it should have held ninety-six. Alongside those: the statutory returns brokers file with the IRDAI can now be generated from your own book.',
    stats: [
      { value: '4 → 96', label: 'car manufacturers on a motor quote', note: 'commercial vehicles went from 2 to 674' },
      { value: '20 → 30', label: 'insurers you can pick from', note: 'ten were never reachable, by scrolling or searching' },
      { value: '5', label: 'IRDAI returns generated from your book', note: 'Form F and four Form S variants' },
      { value: '0', label: 'messages we now claim to have sent', note: 'until a provider is connected — see below' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Messages were not being delivered',
        blurb: 'Correcting something this page told you twice.',
        changes: [
          {
            title: 'Nothing was being sent, and we said it was',
            detail:
              'When you shared a quote by email or WhatsApp, the platform recorded that it had gone and told you so. It had not. The same was true of service-request acknowledgements and account alerts. Two earlier entries on this page described both as going out through your configured channels — that was not correct, and we are saying so here rather than quietly changing what we published. No message sent from this platform has reached a customer.',
          },
          {
            title: 'It now says so plainly instead',
            detail:
              'Sharing a quote tells you the message could not be sent, and gives you the link so you can send it yourself. That is not a fix for delivery — nothing is delivered until a messaging provider is connected to your account, which is a separate piece of setup. What it fixes is being told something happened when it did not, which is worse than being told it failed.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Choosing a vehicle, and choosing an insurer',
        blurb: 'Two lists that were quietly short.',
        changes: [
          {
            title: 'The manufacturer list on a motor quote was almost empty',
            detail:
              'Starting a private car quote offered four manufacturers. There are ninety-six. A commercial vehicle quote offered two, out of six hundred and seventy-four. Two-wheelers were unaffected, which is most of why this went unnoticed for so long — the first list anyone tries was the one that worked. If a make you needed was missing and you worked around it, that is why.',
          },
          {
            title: 'The insurer list stopped at twenty',
            detail:
              'When adding an insurer employee, the insurance company picker showed the first twenty companies of thirty. The ten after that could not be selected by scrolling, and typing a name did not find them either — so a company that was in fact registered looked as though it was not. Every list of this kind now loads in full.',
          },
          {
            title: 'Vehicle body types show their real category',
            detail:
              'Every body type displayed as a four-wheeler regardless of what it actually was, and the category column on the list was blank. Editing one appeared to let you correct the category and then discarded the change. The stored data was never affected — the change simply never reached it.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Statutory returns',
        blurb: 'Form F and Form S, generated from your own book.',
        changes: [
          {
            title: 'The IRDAI returns can be produced from the platform',
            detail:
              'Form F and the four Form S variants — insurer-wise business, general business by line, and life business split single and non-single premium — now generate from your policies and brokerage, in the regulator\'s own layouts, ready to download. Your offices are recorded on the platform for Form F, including the qualified-staff counts the form asks for.',
          },
          {
            title: 'Business we cannot classify is reported as unclassified',
            detail:
              'The regulator counts business under its own lines, which are not the categories you sell under. Where a category has not yet been mapped to one of those lines, the return says so explicitly and names it, rather than folding it into "Miscellaneous". An under-reported line is a filing error; a visible gap is something you can fix before you file.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Screens that were not doing what they appeared to',
        blurb: 'Several places where a success message meant nothing had happened.',
        changes: [
          {
            title: 'The setup wizards have been withdrawn',
            detail:
              'Onboarding, Product Setup and User Setup each ended with "created successfully" and created nothing at all. They have been removed rather than left in place while they are rebuilt. Everything they covered has a working form elsewhere, so nothing you could do before has become impossible.',
          },
          {
            title: 'Your broker username saves',
            detail:
              'Changing it reported success and left the old value in place. It now saves, and the field explains what it affects: it begins every new policy number, and policies already issued keep the username they were issued under.',
          },
          {
            title: 'Roles opens, and says what it can',
            detail:
              'The Roles screen offered to create your first role over a list that had failed to load. It now shows the roles the platform defines and how many people hold each. Permissions themselves are set per broker under your own settings, which is where they have always been.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Smaller things you asked for',
        blurb: '',
        changes: [
          {
            title: 'Search shows the policy number on the document',
            detail:
              'Searching by the number printed on your customer\'s policy found the right record and then displayed our internal reference instead. It now shows the insurer\'s number, with ours underneath for support.',
          },
          {
            title: 'One Policy Date filter, with Today',
            detail:
              'Issued From and Issued To were two controls for one decision. They are now a single Policy Date filter offering Today or a custom range, and the filters sit on one row on a wide screen.',
          },
          {
            title: 'Users can be edited from the list',
            detail:
              'User Management had no way to open a user for editing. It does now.',
          },
          {
            title: 'A contact with no email address has no email address',
            detail:
              'Contacts created automatically while adding a policy, quote or lead were given an invented address, which then appeared on the contact as though the customer had provided it. Those thirty addresses have been cleared, and no new ones are made up.',
          },
          {
            title: 'Change Request moved into the sidebar',
            detail:
              'It was a block on your dashboard duplicating links already in the navigation. It now sits in the sidebar, where the rest of the navigation lives.',
          },
        ],
      },
    ],
    footnote:
      'Also in this release, described by effect as we always do with these: records that should stay separate between brokers now do in two more places, and internal release notes are no longer readable by accounts that were never meant to see them.',
  },
  {
    tag: 'RELEASE / 2026.08.08',
    date: '8 August 2026',
    headline: {
      lead: 'A message that said "already exists"',
      accent: 'when nothing did.',
    },
    summary:
      'Adding an insurance company had been failing for every company, not just the one you happened to try. The form reported that the company already existed; what had actually happened was that the record could not be written at all, and the error was being relabelled on its way back to you. Alongside that fix: your public website was showing an empty space where its list of insurance partners should have been, and the commission reports were showing our own internal reference where the policy number belongs.',
    stats: [
      { value: 'Every', label: 'insurance company can now be added', note: 'the failure was not specific to one company' },
      { value: '0 → 7', label: 'partners on the public site', note: 'measured on a real broker site; the grid had been empty' },
      { value: '578', label: 'policies now showing their real number', note: 'reports had been showing our internal reference' },
      { value: '30', label: 'insurers listed under API Credentials', note: 'previously one, with no way to see the rest' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Adding an insurance company',
        blurb: 'The message was wrong, and so was the thing it was hiding.',
        changes: [
          {
            title: 'Companies can be added again',
            detail:
              'The form answered "An insurance company with these details already exists" for companies that did not exist. The record was in fact being rejected before it could be saved, for a reason that had nothing to do with duplicates, and the rejection was being dressed up as a duplicate on the way back to the screen. Both halves are fixed: the record saves, and a failure that is not a duplicate is no longer described as one.',
          },
          {
            title: 'A genuine duplicate still says so, clearly',
            detail:
              'Adding a company that really is already registered names the company and the category it sits under, and reminds you that the same company can be added under a different category. That message was always correct — it was simply never the one you saw.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Your public website',
        blurb: 'The partner list was empty, and there are now pages per type of cover.',
        changes: [
          {
            title: 'Your insurance partners appear',
            detail:
              '"Our Trusted Insurance Partners" had been a heading with nothing under it. The site was looking for insurers by a short name while your insurers are stored under their full registered names, so it matched none of them and said nothing about it. On a real broker site the same section now lists seven partners.',
          },
          {
            title: 'A page for each type of cover',
            detail:
              'Motor, Health, Term, Travel and the rest each have their own page now, listing only the plans you can actually sell for that cover, with a short form at the bottom that reaches you as an enquiry with the cover already recorded. Which pages exist follows what you sell — a cover you have no product for does not get a page.',
          },
          {
            title: 'Seven new building blocks for the page',
            detail:
              'A navigation bar, a trust and figures band, cover tiles, customer testimonials, a claim-support section, an about block and a full footer with your IRDAI licence. All editable from the site builder, like the sections you already had, and the design has been reworked throughout.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Commissions, contacts and quotes',
        blurb: 'Three places where the screen was showing the wrong thing, or too little of it.',
        changes: [
          {
            title: 'Reports show the insurer’s policy number',
            detail:
              'Opening an insurance company or an agent in the commission reports listed policies under a column headed "Policy" that carried our internal reference. It now leads with the number printed on the customer’s policy, and keeps our reference underneath in smaller type for looking records up. The exports carry both as well.',
          },
          {
            title: 'Duplicate contacts can be merged',
            detail:
              'Contacts has a Duplicates tab listing records that look like the same person, showing what is attached to each so you can see what a merge would move before choosing which record to keep. Nothing merges on its own: the system only ever proposes, and you confirm. Merging carries every policy, quote, claim, document and lead across.',
          },
          {
            title: 'Quote requests open as quote requests',
            detail:
              'A request that came in through your public website used to open as a lead under Contacts, so reading one meant a detour. Both kinds of request now open on the same screen, with a link to the underlying lead for anyone who wants it.',
          },
          {
            title: 'Servicing Requests is its own section',
            detail:
              'It had been presenting itself as though it lived under Renewals. It does not, and no longer says it does.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Smaller things',
        blurb: 'Two that had been quietly getting in the way.',
        changes: [
          {
            title: 'Profile photos upload',
            detail:
              'Uploading a photo in Settings had been giving up after thirty seconds. Photos taken on a phone are large, and the upload was being cut off before it finished rather than being slow at the far end. Large images are now resized in your browser before they are sent, with a progress bar while they go.',
          },
          {
            title: 'API Credentials shows every insurer you are linked to',
            detail:
              'The page listed only insurers that had already been switched on, which for most brokers meant one card or an empty screen, and no way to tell what else was possible. It now lists every insurer you hold a relationship with and says, for each, whether it is ready for your key, waiting on your administrator — with a button to ask them — or not yet supported by the platform.',
          },
        ],
      },
    ],
    footnote:
      'Verified against a copy of the live database rather than test data alone, which is how the empty partner list and the "already exists" message were both found to be worse than reported. 4,053 automated tests cover this release: the full suite, re-run against the released code.',
  },
  {
    tag: 'RELEASE / 2026.08.06',
    date: '6 August 2026',
    headline: { lead: 'Fewer screens to get through,', accent: 'and the ones that never worked now do.' },
    summary:
      'Raising a motor quote used to take fifteen screens, each asking for a single thing. It now takes six, and the vehicle details arrive together so you can see the registration lookup fill them in rather than pressing Next through pages that were quietly completed behind you. Alongside that, a run of things that looked finished but had never actually worked: number-plate scanning that failed only on the live site, six administration screens that had never once loaded, and product tabs that accepted everything you typed and saved none of it.',
    stats: [
      { value: '6 steps', label: 'to raise a motor quote', note: 'previously fifteen, one field per screen' },
      { value: '6', label: 'admin screens brought back', note: 'they had never loaded since the day they were written' },
      { value: '5 of 6', label: 'product tabs now save', note: 'they had been accepting entries and discarding them' },
      { value: '3,744', label: 'automated tests passing', note: 'the full suite re-run against the released code' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Raising and sending a quote',
        blurb: 'Six screens instead of fifteen, and a link the customer can be sent directly.',
        changes: [
          {
            title: 'The quote form is six steps',
            detail:
              'Customer details, business type, insurance type, vehicle details, product and premium, then review. The questions are the same ones; what changed is how many times you press Next. Vehicle details now share a screen, which is what lets you watch a registration number fill in the make, model, variant, dates and RTO — previously those were filled in on screens you then walked through one at a time.',
          },
          {
            title: 'Scanning a number plate works again',
            detail:
              'Photographing a number plate had been answering “reading images is unavailable right now”. It worked everywhere it was tested and failed only on the live site, which is why it survived so long. Fixed, and pinned so a future change to the server setup cannot quietly take it down again.',
          },
          {
            title: 'Send the quote by WhatsApp or email',
            detail:
              'A quote link used to be something you copied and pasted somewhere yourself. There are now Share via WhatsApp and Share via Email buttons beside it, which send through your own configured channel and templates, and publish the link as they go — so the customer never receives a link that does not open yet.',
          },
          {
            title: 'Convert to Policy appears when there is something to convert',
            detail:
              'The button used to appear only once a quote was complete — but a quote becomes complete BY uploading the issued policy, so it arrived after the work it was meant to start. It now appears once payment has been recorded, and disappears once a policy actually exists.',
          },
          {
            title: 'Quote Requests empties as you deal with it',
            detail:
              'A request left the list only in the sense that it moved further down it. Sharing a quote, or converting an enquirer into a customer, now takes the row off the tab — it is an inbox, and an inbox that never empties stops being read.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Finding things',
        blurb: 'Filters you can see, and a claim page that answers the question first.',
        changes: [
          {
            title: 'Search and filters sit on one row',
            detail:
              'On Contacts, Policies and Renewals. On two of those, several filters had been hidden behind a Filters button, which meant a filter could stay applied while being invisible — the list looked wrong and the reason was one click out of sight.',
          },
          {
            title: 'A claim leads with where it has got to',
            detail:
              'Claim Progress and the full status history now sit at the top together, ahead of the reference details. Two duplicate panels were removed, including a second one confusingly headed “Claim Tracking” that showed something different from the first.',
          },
          {
            title: 'A converted lead stops offering to be converted',
            detail:
              'The Convert to Customer button never went away after conversion, so a lead could be offered up a second time. The Leads status filter was also offering two states the system cannot produce while missing four that it does.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Administration',
        blurb: 'Screens that had never loaded, and forms that had never saved.',
        changes: [
          {
            title: 'Six administration screens now open',
            detail:
              'NCB Slabs, Add-ons, Insurer Employees, API Configs, Fiscal Years and Account Types were each requesting an address that does not exist, so all six had been blank since the day they were written. Two of them were also reading and writing the wrong field names, so they could not have worked even once the address was right.',
          },
          {
            title: 'Product mapping keeps what you enter',
            detail:
              'The Add Product Mapping form has six tabs; five of them were being accepted and discarded. USPs and features, premiums, eligibility, documents, links and tags now save, come back when you re-open the product, and can be cleared as well as added. Editing one field no longer risks the rest.',
          },
          {
            title: 'Organisation details can be maintained',
            detail:
              'Licence number, type and validity, the organisation username, and the tax identifiers are editable in one place and shown on the organisation page. Only a country administrator can change them.',
          },
          {
            title: 'Sub-groups, NCB slabs and insurer employees',
            detail:
              'Product groups now show and take their sub-groups in place instead of sending you to a separate screen, No Claim Bonus slabs are edited beside the product that owns them, and creating an insurer employee is called that — and no longer assigns a role the system does not recognise.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Being told when something happens',
        blurb: 'Quotes and new leads reach you, and acknowledgements actually go out.',
        changes: [
          {
            title: 'Quotes and new leads raise a notification',
            detail:
              'A quote request raised by a customer, and a lead arriving from your public enquiry form, now notify you — the assigned advisor where there is one, and the administrators otherwise. Both are categories you can switch on or off per person, alongside payments, claims, renewals and policies.',
          },
          {
            title: 'Service acknowledgements are sent',
            detail:
              'Acknowledgements for service requests, and alerts raised against your account, had never been delivered — the platform recorded the attempt and moved on. They now go out through your configured channels and templates.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Your public site',
        blurb: 'Insurance companies can be listed publicly, with what they sell.',
        changes: [
          {
            title: 'The insurance companies directory can be published',
            detail:
              'The public directory could not previously show anything at all: listing a company required a setting that existed but appeared nowhere in the platform, so none had ever been turned on. Country administrators can now publish a company, and each card shows the products it actually offers rather than only how many there are.',
          },
        ],
      },
    ],
    footnote:
      'Two things worth being straight about. Publishing insurance companies is a switch, and it is off — nothing has been put on the public site by this release; a country administrator decides that company by company. And the registration lookup described in the previous release is still waiting on the records provider to authorise our servers, so vehicle details filled in from official records remain ready rather than working; lookups against your own issued policies and customer records are unaffected.',
  },
  {
    tag: 'RELEASE / 2026.08.03',
    date: '3 August 2026',
    headline: { lead: 'Recognising the right vehicle,', accent: 'and letting you say when we have not.' },
    summary:
      'A registration number only saves you work if we can tell which car it is. This release is about that — recognising a manufacturer whatever way the record spells it, telling a Ciaz from a Swift when the catalogue lists both under three slightly different Marutis, and reading the previous insurer’s name correctly so a renewal arrives already filled in. And where we still cannot be certain, saying so: a wrong match is now one tap to correct instead of a vehicle typed out by hand.',
    stats: [
      { value: '100%', label: 'of manufacturers recognised', note: 'measured on a 600-vehicle sample from the catalogue; previously 87.5%' },
      { value: '97%', label: 'of models recognised', note: 'previously 86%' },
      { value: '1 tap', label: 'to correct a wrong match', note: 'in the quote wizard and on your public enquiry form' },
      { value: '2,651', label: 'automated tests passing', note: 'the full suite re-run against the released code' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Getting the vehicle right',
        blurb: 'Records spell a manufacturer a dozen ways. The catalogue spells it one.',
        changes: [
          {
            title: 'A manufacturer is recognised however it is written',
            detail:
              'Official records name a manufacturer with whatever corporate wording was current when the vehicle was registered — “MARUTI SUZUKI INDIA LTD”, “EICHER MOTORS”, “ESCORTS LTD”. We were comparing the tidied-up version of that against the untidied catalogue name, so a manufacturer could fail to be recognised as itself. Recognition rose from under nine in ten to essentially all of them.',
          },
          {
            title: 'The model decides which listing was meant',
            detail:
              'The vehicle catalogue carries the same manufacturer more than once — a Maruti range split across two listings, a Toyota across two more. We used to pick one and search only there, so roughly half a manufacturer’s models were invisible depending on which listing we happened to choose. Now every listing is searched and the one that actually contains your vehicle wins.',
          },
          {
            title: 'The previous insurer is read correctly',
            detail:
              'A record naming the previous insurer as one word where our own list holds it as two meant the insurer simply did not come through, and a renewal quote arrived without it. Fixed — including for the insurer we are most closely integrated with, where it had been failing quietly.',
          },
          {
            title: 'Fuel and vehicle type arrive filled in',
            detail:
              'A record describes fuel as “PETROL/HYBRID” and a vehicle as “LMV”, which are not the words the quote form uses. They are now translated: a hybrid is treated on its main fuel, a factory CNG car on its gas kit, and a light motor vehicle is a private car unless the record says it is used commercially.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'When we are not sure',
        blurb: 'The closest match is applied, and the alternatives stay within reach.',
        changes: [
          {
            title: '“Not this vehicle?”',
            detail:
              'Where more than one vehicle plausibly fits, the closest match is still filled in — waiting for a decision you almost always agree with would only slow you down — but the alternatives are now offered beside it, ordered by how well each fits the record rather than alphabetically. Choosing a different model brings its own variants with it and clears any variant picked for the car it replaced.',
          },
          {
            title: 'Your correction is remembered for everyone',
            detail:
              'When you correct a match, that answer is kept against the exact wording the record used, so the next person on the platform to meet a vehicle described the same way gets it resolved without asking. Every correction makes the next lookup a little less work.',
          },
          {
            title: 'Visitors can correct it too — for their own enquiry only',
            detail:
              'Someone using your public enquiry form can pick the right vehicle if we guessed wrong, so a mismatch does not cost you the enquiry. What they choose applies to their own request and is not remembered for anyone else: what the platform learns comes from advisors, not from anonymous visitors.',
          },
        ],
      },
    ],
    footnote:
      'One thing to be clear about, because it decides when you will actually see any of this. Everything above applies to vehicles identified from official registration records, and our connection to the records provider is not yet live — they must authorise our servers before it can return anything. Until then these improvements sit ready rather than working, and you will not see the “Not this vehicle?” prompt because there is nothing yet for it to be unsure about. Lookups against your own issued policies and your customers’ own vehicle records are unaffected and continue to work exactly as before.',
  },
  {
    tag: 'RELEASE / 2026.08.02',
    date: '2 August 2026',
    headline: { lead: 'Start with the number plate,', accent: 'and let the form fill itself.' },
    summary:
      'A motor quote used to begin with a dozen fields about the vehicle. It can now begin with the registration number. We check what we already hold — a policy you issued, a vehicle on your customer’s record, a vehicle any broker on the platform has already looked up — and fill in make, model, year, RTO and the previous policy from that. Where the make and model are recognised but the exact variant is not, the likely variant is put at the top of the list and labelled, and whichever one you pick is remembered so the next person to meet the same vehicle does not have to decide again. And if typing a plate on a phone is the awkward part, photograph it instead.',
    stats: [
      { value: '1', label: 'field to start a motor quote', note: 'the registration number; everything else is prefill you confirm' },
      { value: '0', label: 'charge for anything we already hold', note: 'a vehicle bought once is free for every broker afterwards, forever' },
      { value: '116', label: 'automated tests added', note: 'covering the new work, what it must never expose, and what it must never charge for' },
      { value: '2,588', label: 'automated tests passing', note: 'the full suite re-run against the released code' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Starting a motor quote',
        blurb: 'Type the plate; confirm the rest.',
        changes: [
          {
            title: 'The registration number fills in the vehicle',
            detail:
              'Enter a registration on the first step of a motor quote and we look through what we already have — first any policy you have issued for that vehicle, then the vehicle on your customer’s own record, then the shared registry of vehicles the platform has looked up before. A match fills in make, model, variant, year, registration date and RTO, and on a renewal or rollover it fills in the previous insurer, policy number, expiry and no-claim bonus too. Nothing is locked: every field stays yours to correct, and a miss simply leaves you typing as before. The lookup speeds the journey up; it never stands in the way of it.',
          },
          {
            title: 'The likely variant, offered rather than assumed',
            detail:
              'Vehicle records name a make and model but almost never the exact variant, and the variant is what sets the premium. So the variant list now arrives sorted by how well each one matches the wording on the record, with the most likely one marked. It is never selected for you. A variant chosen on your behalf and not noticed would quote a different car at a different price, and you would not find out until the policy was issued.',
          },
          {
            title: 'Your choice is remembered for everyone',
            detail:
              'When you tell us which variant a vehicle actually is, that answer is kept against the exact wording the record used. The next time anyone on the platform meets a vehicle described the same way, it resolves on its own. Every decision made makes the next lookup a little less work, for you and for every other broker.',
          },
          {
            title: 'Photograph the number plate',
            detail:
              'A Scan the number plate button reads the registration off a photo of the plate or the registration certificate — useful standing next to the car with a phone. It offers what it read for you to confirm rather than filling it in silently, because cameras confuse 0 with O and 8 with B, and a registration number has no built-in check to catch it. Reading a photo is free and involves no outside service.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'What a search costs',
        blurb: 'Priced per broker, visible before you spend, and never charged twice.',
        changes: [
          {
            title: 'Our own records are always free',
            detail:
              'Looking through your policies, your customers’ vehicles, or the platform’s shared registry costs nothing and always will. A vehicle only has to be bought from an official records provider once; from then on it is free for every broker on the platform, including you. Searching the same vehicle twice never costs twice.',
          },
          {
            title: 'Official records search, priced and controlled',
            detail:
              'Buying a record we do not already hold is a paid search, and your account now carries explicit terms for it: whether it is switched on, the price per search, and either a prepaid balance or a monthly account. You can see the price and what is left before you spend anything. On a prepaid account a search that the balance will not cover is declined, with the shortfall named, rather than quietly putting you into debt. The button is only offered when a search is actually available to you — it will not be dangled and then refuse.',
          },
          {
            title: 'Every charge appears in your ledger',
            detail:
              'A paid search posts a line to your account ledger naming the vehicle it was for, so what you were charged and what you searched can always be reconciled against each other.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'On your public website',
        blurb: 'Off by default, capped by you, and it never shows a stranger someone’s details.',
        changes: [
          {
            title: 'Visitors can look up a vehicle before asking for a quote',
            detail:
              'Your public site can offer a visitor the same registration lookup, so they arrive at an enquiry with the vehicle already identified instead of a blank form. It is switched off until you turn it on, in Settings beside your domain.',
          },
          {
            title: 'You set the daily limit',
            detail:
              'Because a visitor’s search can spend your balance, you set how many paid searches your site may make in a day, and the count resets each day. The limit starts at zero, so turning the feature on is not on its own enough to start spending — you choose the number deliberately. Visitors are also rate-limited, and searches of vehicles already in our records are free and unlimited as everywhere else.',
          },
          {
            title: 'Nothing personal is shown to the public',
            detail:
              'Anyone can type any registration number, including one that is not theirs. So the public lookup returns only what a quote needs — make, model, variant options, year, fuel, RTO. The registered owner’s name, address and phone number, the chassis and engine numbers, and any existing insurance are never returned to an unidentified visitor, and the page is built to return a fixed list of permitted fields rather than to hide the rest, so nothing new can start appearing there by oversight.',
          },
        ],
      },
    ],
    footnote:
      'Two honest notes. First, the paid official-records search arrives switched off for every broker: the price and the account terms are set by your administrator, and until they do that, only the free lookups through our own records are available to you. Nothing can be charged in the meantime. Second, our connection to the official vehicle-records provider is not yet live — they must authorise our servers before any paid search can return anything, and we are waiting on them. Everything described above that draws on records we already hold, including the variant ranking, the remembered choices and reading a plate from a photo, works today and does not depend on that connection.',
  },
  {
    tag: 'RELEASE / 2026.07.30',
    date: '30 July 2026',
    headline: { lead: 'Search everything,', accent: 'and see the whole record.' },
    summary:
      'One search box in the header now reaches across your whole portfolio — policies, contacts, quotes, claims, agents, companies — and takes you straight to whatever you click. Alongside it, the pages that were showing you part of a record now show all of it: the full premium breakdown as you entered it, the complete vehicle details, and a Quote Requests tab that finally puts customer enquiries and website enquiries in one place instead of leaving them scattered.',
    stats: [
      { value: '1', label: 'search box, every module', note: 'policies, contacts, quotes, claims, agents and companies' },
      { value: '286', label: 'automated tests added', note: 'covering the new work and what it must never expose' },
      { value: '4', label: 'reported bugs fixed', note: 'each traced to a root cause, not worked around' },
      { value: '0', label: 'known regressions', note: '2,682 automated tests re-run and passing' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Finding things',
        blurb: 'A search bar that stays with you, and a sidebar that gets out of the way.',
        changes: [
          {
            title: 'Search across everything at once',
            detail:
              'The search box sits in the header on every screen. Type a policy number, a customer name, a registration number, a claim reference — results come back grouped by what they are, and clicking one takes you straight there. Arrow keys and Enter work if you would rather not reach for the mouse. You only ever see records you already have access to; search does not widen what you can reach, it just makes it quicker to get to.',
          },
          {
            title: 'Collapse the sidebar',
            detail:
              'Fold the navigation down to icons when you want the screen for your work, and it stays that way as you move between pages and across sessions.',
          },
          {
            title: 'Quick actions from anywhere',
            detail:
              'A quick-action button beside the notification bell starts a new contact, policy, claim or quote from whatever page you are on. It only offers what your role can actually do.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Quotes',
        blurb: 'Enquiries stop falling between two places.',
        changes: [
          {
            title: 'A Quote Requests tab',
            detail:
              'Quotes opens on All Quotes as before, with a new Quote Requests tab beside it. It brings together requests raised by your customers from their own dashboard and enquiries submitted through your public website, each row labelled with where it came from, so you are not checking two screens to see who is waiting. Website enquiries still appear in Contacts exactly as they do today — this shows them, it does not move them.',
          },
          {
            title: 'New Quote and Batch Quotes moved',
            detail:
              'Both now sit as buttons in the top-right corner, where the other page actions live.',
          },
          {
            title: 'The previous insurer reads as a name',
            detail:
              'On a renewal quote, Previous Policy Information was showing an internal reference instead of the insurer’s name. It now shows the name.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Policies',
        blurb: 'Filters where you can see them, and records shown in full.',
        changes: [
          {
            title: 'Filters on the page, not behind a button',
            detail:
              'Search and filters sit inline on the policy list, with new filters for issue-date range and insurer.',
          },
          {
            title: 'The full premium breakdown',
            detail:
              'The policy page was showing three totals. It now shows the breakdown as you entered it, including the own-damage and third-party split and the tax on each.',
          },
          {
            title: 'The complete vehicle record',
            detail:
              'Vehicle details showed make, model, variant and a few fields. Registration date, RTO, colour, engine capacity, seating, gross weight, financing and accessories were all stored but never displayed — they are now. A motor policy entered with only a registration number also used to show no vehicle section at all; it does now.',
          },
          {
            title: 'Endorsement documents upload',
            detail:
              'Attaching an Endorsement Copy silently failed and only Policy Copy would save. The two halves of the system disagreed on what that document type was called. Fixed, with all three types covered by tests on both sides so they cannot drift apart again.',
          },
          {
            title: 'Policy Validation under Reports',
            detail:
              'Checking a spreadsheet of policy numbers against your book now lives in Reports, where you would look for it.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Contacts and your customers',
        blurb: 'Faces on the records, and a shorter route to buying.',
        changes: [
          {
            title: 'Profile pictures',
            detail:
              'Contacts show a profile picture on both the list and the detail page, with a default icon when none is set. Customers can set their own from their dashboard.',
          },
          {
            title: 'Buy New Policy goes to your website',
            detail:
              'When a customer picks an insurance type, they now land on their broker’s public product page. Brokers without a public site keep the previous in-app view, so nobody is sent to a dead end.',
          },
          {
            title: 'A tidier customer dashboard',
            detail:
              'The Recent Quotes panel has been removed; quotes remain on the Quotes page.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Privacy and safety',
        blurb:
          'Every module the new search touches was reviewed for whether it could reach records it should not. Two ways it could were found and closed before release.',
        changes: [
          {
            title: 'Records stay apart between brokers',
            detail:
              'Two ways one broker’s enquiry records could be reached by someone at another broker, or in another country, were found during review and closed before this went out. Both predate this release and neither was reported by anyone — they were found by looking. Both are now covered by tests that fail if they ever reopen.',
          },
          {
            title: 'Search shows only what you could already open',
            detail:
              'Every module the search covers is filtered by the same rules that govern the module itself, so an agent sees their own business and a customer sees their own records. Results also only point at pages your role can actually open.',
          },
        ],
      },
    ],
    footnote:
      'One honest note on the Quote Requests tab. A website enquiry from somebody already in your contacts is recorded against that existing contact rather than as a fresh enquiry, so it appears in Contacts but may not appear in this tab. That is existing behaviour we chose not to change quietly in this release; if you rely on the tab as your only inbox, keep an eye on Contacts too.',
  },
  {
    tag: 'RELEASE / 2026.07.29',
    date: '29 July 2026',
    headline: { lead: 'Your own website,', accent: 'and a quote for every kind of cover.' },
    summary:
      'Two big changes. Your public site becomes something you design rather than something you inherit — real images, drag-and-drop sections, your own colours, and a preview of how it looks on a phone before anyone else sees it. And the quote flow, which only ever worked for motor, health and life, now works for every kind of insurance you sell, with room to put several insurers’ premiums side by side on one link for your customer.',
    stats: [
      { value: '9', label: 'image formats accepted', note: 'JPG, JPEG, PNG, SVG, WEBP, AVIF, GIF, ICO and BMP' },
      { value: 'All', label: 'insurance types quotable', note: 'previously motor, health and life only' },
      { value: '550', label: 'automated tests added', note: 'covering the new work and what it must never expose' },
      { value: '0', label: 'known regressions', note: '2,396 automated tests re-run and passing' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Your public site',
        blurb:
          'The landing page builder stops being a fixed list of blocks and becomes something you can actually lay out.',
        changes: [
          {
            title: 'Real images, not pasted links',
            detail:
              'Upload logos, banners and product images directly — JPG, PNG, SVG, WEBP, AVIF, GIF, ICO and BMP all work. Every image is checked, optimised, and re-rendered at several sizes so it stays sharp on a retina screen without making the page slow to load. They live in one library you can search, organise into folders and reuse anywhere, and the library tells you where an image is already in use before you delete it.',
          },
          {
            title: 'Crop it here, not somewhere else',
            detail:
              'Zoom, crop, rotate, flip, reposition and lock an aspect ratio without leaving the page. Drag a pile of files in at once rather than one at a time.',
          },
          {
            title: 'Arrange the page by dragging it',
            detail:
              'Drag sections into the order you want, duplicate one you have already set up, or hide a section without deleting it. You can also show a section on desktop but not on mobile — and it is genuinely left out on a phone rather than merely hidden, so visitors are not paying to download something they will never see. The up and down buttons are still there for anyone who prefers the keyboard.',
          },
          {
            title: 'See it before anyone else does',
            detail:
              'Preview desktop, tablet and mobile inside the builder, and set your own colours, typography, spacing, backgrounds and shadows. Save a section you have configured and reuse it on another page.',
          },
          {
            title: 'Fuller company and product cards',
            detail:
              'Product cards can carry an image, badges, features, premium, ratings and a call to action; company cards can carry a logo, banner, description, claim ratio and network. Search engines see the text, and images carry alt text so screen readers do too.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Quotes',
        blurb: 'One flow for everything you sell, and a link that is ready the moment the quote is.',
        changes: [
          {
            title: 'Every insurance type, not three',
            detail:
              'The guided quote flow previously existed only for motor, health and life. Every other type now gets a standard flow built from that type’s own fields, so you are not pushed into a form designed for a car when you are quoting something else.',
          },
          {
            title: 'Several insurers on one quote',
            detail:
              'After choosing the type of insurance you can add as many companies as you like, each with its product, premium, expiry date and an attached file. Your customer sees them side by side on one link and picks the one they want.',
          },
          {
            title: 'The link exists from the start',
            detail:
              'A shareable link is now created with the quote rather than at the moment you share it, so it is never a step you can forget. It stays private until you deliberately share it, and you can withdraw it later — if you share it again afterwards, your customer’s original link still works.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Commission',
        blurb: 'Checking what an insurer actually paid you, without doing it by hand.',
        changes: [
          {
            title: 'Compare what you were paid against what was due',
            detail:
              'Commission Comparison puts the amount an insurer states against the amount your own calculation approved, with the difference in green when it is in your favour and red when it is not. Upload the insurer’s statement as CSV or Excel and each line is matched to its policy for you.',
          },
          {
            title: 'Commission grids read for you',
            detail:
              'Rate cards can be uploaded as Excel or as a text PDF and the grid is read into the same fields as the Add Rate form. Nothing is saved from that reading alone — you always see and confirm the rows first, because a commission percentage that is wrong by a decimal point is wrong on every policy it touches.',
          },
          {
            title: 'Pay several agents at once',
            detail:
              'A payout can now be created for multiple agents in one go instead of one at a time.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Things that were quietly wrong',
        blurb: 'Reported by brokers, and fixed.',
        changes: [
          {
            title: 'The renewal countdown now counts down',
            detail:
              'The days remaining shown in red under an expiry date was only recalculated when something else changed the record, so it could sit still for weeks and quietly understate how urgent a renewal was. It is now worked out fresh every time you look at it.',
          },
          {
            title: 'The agent field starts empty',
            detail:
              'Adding a policy pre-filled the Agent (POSP) field with whoever was on the last policy you created, which was easy to miss and attributed business to the wrong person. It now starts blank. Converting a quote into a policy still carries that quote’s agent across, which is intended.',
          },
          {
            title: 'Service requests can be opened',
            detail:
              'A service request raised from the customer portal could be seen in the list but not opened. You can now open one and see its details, documents and full history — as the customer who raised it, the agent it belongs to, or the broker.',
          },
          {
            title: 'Check a list of policy numbers at once',
            detail:
              'Upload a spreadsheet of policy numbers and get back a report of which ones are not in the system, rather than searching for them one by one.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Privacy and safety',
        blurb:
          'This release was reviewed specifically for whether one broker’s records could ever reach another. Three ways they could were found and closed.',
        changes: [
          {
            title: 'Records stay apart between brokers',
            detail:
              'Three separate places were found where information belonging to one broker could be reached by another — including documents attached to service requests, which is where customers upload identity paperwork. All three predate this release, all three were found by that review rather than by anyone encountering them, and all three are now closed and covered by tests that fail if they ever reopen.',
          },
          {
            title: 'A rate that cannot be read is refused, not guessed',
            detail:
              'A spreadsheet can describe a commission rate in a way that makes 5% and 500% look identical to software. That case is now handled correctly, and any percentage outside 0–100 is rejected before it can be saved rather than after it has been used.',
          },
        ],
      },
    ],
    footnote:
      'One thing worth stating plainly about reading commission grids. Excel files and PDFs containing real text are read exactly. Scans and images are read too, but how well depends on the picture: a clean export or screenshot reads reliably, while a cramped or photographed rate card can run neighbouring columns together. Nothing is saved from a reading alone — every row is checked and shown to you first, and a value that came out malformed is refused at that point rather than stored. If a grid will not read cleanly, type it in; a commission percentage that is wrong by a decimal point is wrong on every policy it touches.',
  },
  {
    tag: 'RELEASE / 2026.07.28',
    date: '28 July 2026',
    headline: { lead: 'Choose your cover,', accent: 'and tell us once you have paid.' },
    summary:
      'The shared quote link stops being a document and starts being a conversation. Your advisor can attach the insurer’s quote for you to download, you pick the option you want, and once you have paid them directly you enter the payment reference on the same link. Your advisor sees the choice and the reference straight away, confirms the money has arrived, and issues the policy.',
    stats: [
      { value: '2', label: 'steps for the customer', note: 'choose an option, then confirm payment when you have made it' },
      { value: '0', label: 'card details collected', note: 'you pay your advisor directly — nothing is charged on the page' },
      { value: '42', label: 'automated tests added', note: 'covering the new flow and what it must never expose' },
      { value: '0', label: 'known regressions', note: '1,688 automated tests re-run and passing' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'For the customer',
        blurb:
          'Everything happens on the one link your advisor sends you. No login, no account, no card.',
        changes: [
          {
            title: 'Download the quote document',
            detail:
              'Your advisor can attach the insurer’s quote, or a comparison sheet, to the link. You download it from the same page you see the options on. Only these documents appear there — anything else held against your quote, such as identity or KYC paperwork, stays inside your advisor’s portal.',
          },
          {
            title: 'Choose the option you want',
            detail:
              'Each option shows the insurer, the product and the premium. Pick one and it is recorded against your quote. You can change your mind as many times as you like, right up until you tell us you have paid.',
          },
          {
            title: 'Tell your advisor once you have paid',
            detail:
              'You pay your advisor directly, as you do today. Then come back to the same link and enter the payment reference, the date and how you paid. Nothing is charged on the page and no card details are collected — this only tells your advisor where to look for the money.',
          },
          {
            title: 'Then it is with your advisor',
            detail:
              'Once you have confirmed payment your choice is locked, so what you paid for and what you chose cannot drift apart. Your advisor confirms the money has arrived, buys the policy, and uploads it — and you can download it from your portal when it is ready.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'For the broker',
        blurb:
          'A customer who has chosen and paid should not have to phone you to say so.',
        changes: [
          {
            title: 'Attach the quote before you share it',
            detail:
              'Upload the insurer’s quote PDF or a comparison sheet to any quote, and it travels with the link you send. Remove it just as easily.',
          },
          {
            title: 'See who is waiting on you',
            detail:
              'Quotes where the customer has reported paying are flagged in the Quotes list, and your dashboard shows how many are waiting so nothing sits unnoticed. The payment reference, date and method appear on the quote itself, ready to match against your bank statement.',
          },
          {
            title: 'A record of who accepted',
            detail:
              'Because the link needs no login, every acceptance is recorded with a timestamp and the connection it came from, visible to you on the quote. If a customer ever disputes a choice, there is something to check rather than an argument to have.',
          },
        ],
      },
    ],
    footnote:
      'No payment is taken on this platform and no card or bank credentials are collected or stored at any point — the customer pays their advisor directly and only tells us the reference afterwards. The public quote link continues to show the customer-facing quote only; identity and KYC documents held against the same quote are never listed or downloadable through it.',
  },
  {
    tag: 'RELEASE / 2026.07.27',
    date: '27 July 2026',
    headline: { lead: 'Shared quotes that open,', accent: 'and records that find their people.' },
    summary:
      'A round about links that lead somewhere and names that actually appear. The quote link you send a customer now opens — and can carry the insurer’s quote document with it. Renewals show who the policy belongs to instead of a blank column. Agents see their own customers’ quotes, brokers can create their own staff, and a claim now walks through its stages one step at a time rather than offering every status at once.',
    stats: [
      { value: '18', label: 'fixes & features shipped', note: 'across the Country Admin, Broker, POSP and Customer portals' },
      { value: '4', label: 'portals improved', note: 'Country Admin, Broker Admin, POSP Agent, Customer' },
      { value: '3', label: 'access issues closed before release', note: 'found while building, none reachable in normal use' },
      { value: '0', label: 'known regressions', note: '1,655 automated tests re-run and passing' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'The shared quote link',
        blurb:
          'A quote could be shared, but the link went nowhere. It now opens for the customer, and can carry the paperwork with it.',
        changes: [
          {
            title: 'The link finally opens',
            detail:
              'Sharing a quote produced a link that showed "page not found" when the customer clicked it. The page behind that link now exists: it shows the quote, the vehicle or cover it is for, and every option with its premium — no login needed.',
          },
          {
            title: 'Send the quote document with the link',
            detail:
              'A Broker Admin can attach the insurer’s quote PDF, or a comparison sheet, to a quote. The customer downloads it straight from the shared link. Only these customer-facing documents ever appear there — identity and KYC paperwork held against the same quote stays inside the portal.',
          },
          {
            title: 'A quote shows who it is for',
            detail:
              'The customer panel on a quote said "no customer linked" for most quotes, because it was reading the wrong record. It now shows the customer’s name, email and phone on every quote that has one.',
          },
          {
            title: 'Turning a quote into a policy carries the details over',
            detail:
              'Choosing "Convert to Policy" on a completed quote opened an empty form, so everything just entered had to be typed again. The form now arrives filled in with the customer, cover and vehicle details from the quote.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Renewals and claims',
        blurb:
          'Two screens that were technically working but not telling you anything useful.',
        changes: [
          {
            title: 'Renewals show the customer',
            detail:
              'The customer column on Upcoming Renewals and the Renewals list was blank for most rows. It now reads the policy’s own record, so the name appears — and the policy column shows the insurer’s policy number, the one you would quote to an insurer, rather than an internal reference.',
          },
          {
            title: 'Claims move one step at a time',
            detail:
              'The claim status list offered all fifteen statuses at once, including one that was never valid and always failed. It now offers only the steps that legitimately come next, and shows the claim’s full path with the completed, current and pending stages marked.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Agents and staff',
        blurb:
          'POSP agents could not see their own work, and brokers could not add their own people.',
        changes: [
          {
            title: 'Agents see quotes for their customers',
            detail:
              'The POSP Quotes section was empty for every agent, because it only counted quotes formally assigned to them. It now shows quotes raised for their customers, whether the agent or the Broker Admin created them.',
          },
          {
            title: 'Agents and customers can open the policy document',
            detail:
              'The policy screen offered View and Download to POSP agents and customers, but the file would not open for either. Both can now open the document the Broker Admin uploaded, on policies they are party to.',
          },
          {
            title: 'Brokers create their own employees',
            detail:
              'Adding a Broker Employee required the Country Admin. A Broker Admin can now create employees directly from Rights Management, and set rights for both employees and POSP agents from the same screen.',
          },
          {
            title: 'Staff changes reach the Country Admin',
            detail:
              'When a Broker or Insurer Employee updated their name, email or mobile number, the change never showed in the Country Admin’s Users list — employees were not listed there at all. They now are, within their own country.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'The customer portal',
        blurb:
          'Raising a service request failed outright. Buying was a guessing game.',
        changes: [
          {
            title: 'Service requests can be raised',
            detail:
              'Submitting a service request always failed with a validation error, because the form never collected the requester’s name, email and phone that the system required. Those fields are now on the form, filled in from your profile and editable if you are raising it for someone else.',
          },
          {
            title: 'Pick your policy from a list',
            detail:
              'The service request form asked customers to type a policy reference by hand. It now offers a searchable list of your own policies.',
          },
          {
            title: 'See the products before you ask',
            detail:
              '"Buy New Policy" showed insurance categories with nothing behind them. Each category now opens to show the products your broker actually offers, and who underwrites them.',
          },
          {
            title: 'A tidier policy view',
            detail:
              'The policy screen offered customers a "Create Renewal Quote" action that led to a page they could not access. It has been removed from the customer view.',
          },
        ],
      },
    ],
    footnote:
      'Records continue to be kept apart between brokers, and between customers of the same broker. Three places where that separation was thinner than intended were found and closed during this work; none of them were reachable through normal use of the portal, and no customer document or record was exposed.',
  },
  {
    tag: 'RELEASE / 2026.07.25',
    date: '25 July 2026',
    headline: { lead: 'Quotes that finish,', accent: 'and portals that know who you are.' },
    summary:
      'A focused round across the Broker, POSP and Customer portals. Quote comparison now answers immediately instead of spinning, every quote keeps its customer attached, and agents finally see their own clients. Customers get a place to browse what their broker actually sells, their own servicing requests section, and a claim view that shows the real numbers.',
    stats: [
      { value: '25', label: 'fixes & features shipped', note: 'across the Broker, POSP and Customer portals' },
      { value: '3', label: 'portals improved', note: 'Broker Admin, POSP Agent, Customer' },
      { value: '2', label: 'security issues fixed before release', note: 'both caught by automated review during the work' },
      { value: '0', label: 'known regressions', note: 'existing automated tests re-run and passing' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Quotes end to end',
        blurb:
          'The quote journey had gaps at both ends — comparison never settled, and the customer link quietly went missing. Both are closed.',
        changes: [
          {
            title: 'Comparison answers straight away',
            detail:
              'The comparison screen used to sit on "Fetching quotes…" and then show a bare "No quotes available". Prices are worked out the moment a request is sent, so the result now appears immediately — and when there are no quotes, it says why: whether no insurer is enabled for that product line, or insurers were asked and none priced it.',
          },
          {
            title: 'The customer stays attached to the quote',
            detail:
              'Choosing an existing customer, or adding a new one, now links them to the quote on save. Previously a newly added customer could be dropped, leaving the quote with nobody attached and "View Customer" leading nowhere. That link now opens the right record.',
          },
          {
            title: 'A quote has a life cycle you control',
            detail:
              'Every quote saves as a draft. From there a Broker Admin can edit it, assign a POSP agent, and move it through Submitted, Processing, Completed, Expired or Cancelled. Submitted quotes appear for the assigned agent.',
          },
          {
            title: 'A link you can send to a customer',
            detail:
              'A quote can be shared with a customer through a private link that needs no login. It shows only the quote itself, is created only when you choose to share, and stops working if the quote is pulled back to draft or cancelled.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Broker workspace',
        blurb: 'Claims, renewals, documents, rights and support — the screens brokers work in every day.',
        changes: [
          {
            title: 'Claim status updates work',
            detail:
              'Changing the status of a raised claim returned a server error every time. It now saves, and an out-of-order status change explains what is allowed instead of failing.',
          },
          {
            title: 'Pending renewals can be pulled in on demand',
            detail:
              'A new "Fetch Pending Renewals" button finds policies that have already lapsed or expired without being renewed. Previously the list only looked ahead, so those policies never appeared.',
          },
          {
            title: 'View opens a document, Download saves it',
            detail:
              'Choosing View now opens the policy document in the page. Download continues to save the file, as before.',
          },
          {
            title: 'Rights Management lists your employees',
            detail:
              'The screen reported "No employees found for this broker" even when employees existed, because their staff record was never created. Employees now appear — including everyone already added — with working permission toggles.',
          },
          {
            title: 'Customer requests reach your support queue',
            detail:
              'Servicing requests raised by customers now appear in the Support Tickets screen alongside your tickets, each linking through to where it is handled. Nothing is duplicated: a request keeps one status and one owner wherever you open it.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'POSP agents',
        blurb: 'Agents were blocked from routine work and could not see their own clients. Both are fixed.',
        changes: [
          {
            title: 'Your own customers, finally visible',
            detail:
              'Contacts now shows every customer whose policy was issued through you, as well as anyone your broker has assigned to you. Previously only assigned customers appeared, so an agent\'s own clients were missing.',
          },
          {
            title: 'Creating quotes works again',
            detail:
              'Raising a new quote returned a permission error, and creating a renewal quote from a policy said "Access Denied". Agents can now do both.',
          },
          {
            title: 'My Portal opens on your profile',
            detail:
              'My Portal used to show a list containing only yourself, which you then had to click through. It now opens straight onto your profile — policies, commission for the year, targets, payouts, documents and certifications — with activation still managed by your broker.',
          },
          {
            title: 'Policies are read-only, as intended',
            detail:
              'Editing a policy, recalculating commission and uploading documents have been removed for agents. Policy documents uploaded by your broker remain available to view and download.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Customer portal',
        blurb: 'Customers hit dead ends on buying, servicing and passwords. Each one now leads somewhere.',
        changes: [
          {
            title: 'Browse what your broker offers',
            detail:
              'Choosing a policy type from the dashboard used to say "Access Denied". It now opens the insurers and plans your own broker offers for that type, with a Request a Quote action that sends the request to your broker to price.',
          },
          {
            title: 'Servicing requests have their own section',
            detail:
              'Address, vehicle, mobile and email changes now live in a dedicated Servicing Requests section rather than under Renewals — and submitting one works, where it previously failed outright. Your broker and assigned agent see it and can respond.',
          },
          {
            title: 'Your advisor is shown correctly',
            detail:
              'The dashboard and My Contacts showed your own name as your advisor, or "Not Assigned". Both now show the agent looking after you, with their contact details.',
          },
          {
            title: 'Password reset works',
            detail:
              'Resetting your password failed with a confirmation error even when both fields matched. It now completes.',
          },
          {
            title: 'Claims show the full picture',
            detail:
              'Opening a claim now shows the customer, priority and current status, along with claimed, approved and settled amounts as your broker updates them.',
          },
        ],
      },
    ],
    footnote:
      'Two security issues were found by automated review during this work and fixed before release: uploaded documents are now opened only in formats that cannot run code, and customers can raise a servicing request only against their own policies. Policies and claims across different brokers remain kept apart.',
  },
  {
    tag: 'RELEASE / 2026.07.24',
    date: '24 July 2026',
    headline: { lead: 'Every dashboard sharper,', accent: 'and a public site of your own.' },
    summary:
      'A broad round of fixes across all four dashboards — Broker, Customer, POSP and Country Admin — lands alongside the biggest new capability in a while: every broker now has its own public website. It lists the insurers and products you offer, takes enquiries straight into your contacts, and comes with a shareable link that works the moment it is switched on.',
    stats: [
      { value: '25', label: 'fixes & features shipped', note: 'across four dashboards and a new public site' },
      { value: '43', label: 'new automated tests', note: 'all passing, on top of the existing suite' },
      { value: '4', label: 'dashboards improved', note: 'Broker, Customer, POSP, Country Admin' },
      { value: '1', label: 'security issue fixed before release', note: 'flagged by automated review on the new public pages' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Your own public website',
        blurb:
          'The largest new capability: a public, shareable site for every broker, built to be found by search engines.',
        changes: [
          {
            title: 'A public site per broker, live from day one',
            detail:
              'Each broker gets a shareable public page listing the insurance companies and products they offer, with a page for every company and every product. It works immediately on a ready-made link — no domain setup required — so you can share it the moment it is enabled.',
          },
          {
            title: 'Enquiries land in your contacts',
            detail:
              'A "Request for Premium" form on the public pages captures a visitor\'s details and adds them straight into your contacts as a new lead, tied to your broker automatically.',
          },
          {
            title: 'Design it yourself, from a shared starting point',
            detail:
              'Country Admin sets a base design; each broker then customises their own site with a section-based builder — add, reorder and configure sections, or reset back to the base at any time.',
          },
          {
            title: 'Bring your own domain',
            detail:
              'A broker can map a custom domain from their settings, which the Country Admin approves. Until a domain is connected, the ready-made link keeps working, so the site is never offline.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Broker workspace',
        blurb: 'The busiest screens got the most attention — dashboards, adding policies, claims, renewals and search.',
        changes: [
          {
            title: 'Commission dashboard filters by real periods',
            detail:
              'The Total Commission filter is now 7 days, 30 days, 90 days or a custom range, measured by the policy issue date, instead of the older All Time / Monthly / Annually options.',
          },
          {
            title: 'Adding a policy is smoother',
            detail:
              'The customer selector now shows every one of your contacts and searches as you type, rather than stopping at the first twenty. The policy term in months fills in on its own from the start and expiry dates.',
          },
          {
            title: 'Claims register against the number you know',
            detail:
              'Registering a claim now finds the policy whether you enter its policy number or the insurer\'s own number, instead of failing to find it.',
          },
          {
            title: 'Renewals show everything due',
            detail:
              'The Renewals page now lists every policy expiring within the next 30 days, and a newly added policy expiring soon appears straight away. A nightly check keeps the list current.',
          },
          {
            title: 'Search as you type across policies',
            detail:
              'The Policies search now matches on policy number, holder name and vehicle number as you type.',
          },
          {
            title: 'Rights, channels and insurer search tidied up',
            detail:
              'Granting a right to an employee now saves reliably; unused sections were removed from Rights Management and Channels; and the insurance companies list has a search box.',
          },
          {
            title: 'Quotes show the fuel type by name',
            detail:
              'Saving a quotation no longer shows a technical code where the fuel type should be — Petrol, Diesel, CNG or Electric now appear by name and save without error.',
          },
          {
            title: 'Names and vehicle numbers format themselves',
            detail:
              'As you type or paste, names are tidied to a consistent capitalisation and vehicle registration numbers to a consistent upper-case form, with characters that do not belong blocked at entry.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Customer portal',
        blurb: 'Trimmed to what a customer actually needs, with a profile and password control added.',
        changes: [
          {
            title: 'A dashboard that fits the customer',
            detail:
              'Figures meant for brokers — total commission and pending quotes — no longer appear on the customer dashboard.',
          },
          {
            title: 'Your profile, your policy agent, your password',
            detail:
              'The contacts area now shows your profile and the status of your KYC, names your assigned Policy Agent clearly, and lets you change your own password.',
          },
          {
            title: 'Claims you can raise but not overrule',
            detail:
              'You can register a claim, which goes to your broker, and follow its progress. Only the broker changes a claim\'s status, so the status control no longer appears on your side.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'POSP portal & Country Admin',
        blurb: 'Correct figures for POSPs, and a form fix for administrators.',
        changes: [
          {
            title: 'POSP figures are their own',
            detail:
              'The active-policy count now reflects only the POSP\'s own policies, and the commission shown is only what the POSP has earned from the broker — not the broker\'s wider total.',
          },
          {
            title: 'POSP contacts load correctly',
            detail:
              'The contacts section, which was failing to load, now shows the POSP\'s broker contact details. Quote creation, which is not a POSP task, has been removed.',
          },
          {
            title: 'Vehicle variant form accepts blanks',
            detail:
              'Adding a vehicle variant without an engine CC, ex-showroom price, cubic capacity or GVW no longer fails — those fields are optional, and can now also be cleared once set.',
          },
        ],
      },
    ],
    footnote:
      'The public site is served in a search-engine-friendly way and every page keeps records strictly to the broker they belong to. Custom-domain certificates activate once a broker points a real domain at the platform; the ready-made shareable link works in the meantime.',
  },
  {
    tag: 'RELEASE / 2026.07.23',
    date: '23 July 2026',
    headline: { lead: 'Claims that work,', accent: 'records kept apart.' },
    summary:
      'This release repairs claim registration from end to end — the types you can choose, the step that files the claim, the reference that lets you open it again, and a broker approval before anything reaches the insurer. It also closes the largest remaining gap in how customer records are separated between brokers, and rewrites the messages you see when something is wrong.',
    stats: [
      { value: '14', label: 'changes shipped', note: 'across claims, privacy and everyday use' },
      { value: '5', label: 'faults found by their own tests', note: 'caught before release, not after' },
      { value: '1,556', label: 'automated tests passing', note: 'run against every change' },
      { value: '0', label: 'database changes required', note: 'no migration, no downtime' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Claims',
        blurb:
          'Raising a claim was effectively broken. Three separate faults combined to make it look like claims vanished after being filed.',
        changes: [
          {
            title: 'Every claim type on the form can actually be used',
            detail:
              'The claim form offered eight types, but only two of them were accepted — choosing any of the other six failed with a validation error. The form now takes its list from the system itself, so the two cannot drift apart again.',
          },
          {
            title: 'Filed claims no longer sit as drafts',
            detail:
              'A claim raised by a customer or POSP stayed in Draft indefinitely, because nothing ever moved it on. It is now submitted properly, recorded in the claim history, and the notification is sent.',
          },
          {
            title: '"Claim not found" after filing a claim',
            detail:
              'The claim was being created successfully — the confirmation simply did not include its reference, so opening it led nowhere. The claim you just filed now opens as expected.',
          },
          {
            title: 'Claims can be edited again',
            detail:
              'Saving a change to any claim failed outright. Editing works, and the Claim Amount and Priority fields — which were never stored anywhere — have been removed from the form rather than left to mislead.',
          },
          {
            title: 'Surveyors can be assigned',
            detail:
              'Assigning a surveyor to a claim failed every time. It now works, and is restricted to your own claims.',
          },
          {
            title: 'Broker approval before the insurer',
            detail:
              'A claim raised by a customer or POSP now goes to the broker for approval or rejection first, and only approved claims go forward to the insurer. A rejection is final and cannot be quietly reversed.',
          },
          {
            title: 'Claim history opens reliably',
            detail:
              'Viewing a claim that had any recorded activity could fail to load. Fixed as part of the above.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Privacy & data protection',
        blurb: 'Described by effect rather than mechanism, as always on this page.',
        changes: [
          {
            title: 'Customer records are strictly separated between brokers',
            detail:
              'Family details, vehicles, identity documents, interaction history and notes are now checked against the broker they belong to on both reading and writing. This closes the largest remaining gap of its kind.',
          },
          {
            title: 'Claim actions are limited to your own claims',
            detail:
              'Assigning a surveyor, and approving or rejecting a claim, now apply the same ownership rule as viewing one.',
          },
          {
            title: 'Approval cannot be self-granted',
            detail:
              'The person who raises a claim cannot approve it. Approval is limited to broker administrators.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Clearer messages',
        blurb: 'Error messages were showing internal technical detail to people who could do nothing with it.',
        changes: [
          {
            title: 'Errors are written in plain English',
            detail:
              'Messages such as "Invalid UUID" or "accident_type is not a valid choice" now read as "Please choose a fuel type" and "Please select a valid Claim Type". Technical detail is still recorded for support, just not shown to you.',
          },
          {
            title: 'Consistent phone numbers',
            detail:
              'A number typed with spaces or dashes is accepted and saved as ten clean digits. Previously it passed the check but was stored exactly as typed, so the same person could appear under two different numbers.',
          },
        ],
      },
    ],
    footnote:
      'The redesigned quotation flow, the renewals page and the public product catalogue are in progress and will follow in a later release.',
  },
  {
    tag: 'RELEASE / 2026.07.22',
    date: '22 July 2026',
    headline: { lead: 'Reporting you can', accent: 'actually trust.' },
    summary:
      'This release corrected a set of figures that were wrong in ways the screen did not reveal — most importantly the commission totals brokers price and pay against. It also unblocked claim registration, added logins for POSPs, customers and channels, and tightened how records are kept apart between brokers.',
    stats: [
      { value: '26', label: 'changes shipped', note: 'across 16 areas of the platform' },
      { value: '₹1.40L', label: 'restored to reporting', note: 'dashboard commission was reading zero' },
      { value: '1,480', label: 'automated tests passing', note: 'run against every change' },
      { value: '5', label: 'data-isolation fixes', note: 'tenant boundaries tightened' },
    ],
    sections: [
      {
        tag: '01',
        heading: 'Commissions & payouts',
        blurb:
          'The largest cluster of fixes. Several reporting figures were wrong in ways that were hard to see from the screen alone.',
        changes: [
          {
            title: 'Total Commission now reports the real figure',
            detail:
              'The dashboard tile was returning zero for every filter selection, including the default. It now reports correctly, and the Motor / Health / Other breakdown adds up to the total.',
          },
          {
            title: 'Recalculated policies are no longer counted twice',
            detail:
              'Recalculating a policy keeps the previous figure as an audit record. Those superseded records were being added into commission totals alongside the current one.',
          },
          {
            title: 'Directly-entered policies are classified correctly',
            detail:
              'Policies captured without a linked product were all falling into "Other". They now classify by their own insurance type, so Motor and Health totals are complete.',
          },
          {
            title: 'Commission rates can vary by NCB',
            detail:
              'Commission Rate forms take an optional No Claim Bonus value. A rate set for a specific NCB now takes precedence over a general one; leave it blank and the rate applies at any NCB.',
          },
          {
            title: 'Commission can be calculated from the policy page',
            detail:
              'The action only appeared once a commission already existed, which hid it on exactly the policies that needed it.',
          },
          {
            title: 'Payouts work across multiple brokers',
            detail:
              'Users who belong to more than one broker could not generate a payout — the request was checked against the wrong broker. Payouts now run for any broker you are an active member of.',
          },
        ],
      },
      {
        tag: '02',
        heading: 'Claims',
        blurb: 'Claim registration was blocked outright for most policies. That is resolved.',
        changes: [
          {
            title: 'Claims can be raised on live policies',
            detail:
              'Registration rejected policies in the "issued" state — the normal state for an in-force policy — so almost no real policy could be claimed against.',
          },
          {
            title: 'Claims open from both entry points',
            detail:
              'Both "New Claim" and "Raise Claim from Policy" now work, and the policy can be identified by either its policy number or its record ID.',
          },
          {
            title: 'Deleted policies are no longer claimable',
            detail: 'Removed policies were still reachable from the self-service claim flow.',
          },
        ],
      },
      {
        tag: '03',
        heading: 'Policies & data quality',
        blurb: 'Entry rules that keep records consistent as the book grows.',
        changes: [
          {
            title: 'Vehicle numbers are stored consistently',
            detail:
              'Spaces and separators are removed and the value is upper-cased on save, so DL14-FZ-2034 and dl14 fz 2034 become the same record.',
          },
          {
            title: 'Duplicate policy numbers are blocked per insurer',
            detail:
              'Two insurers may legitimately issue the same policy number, so the check is scoped to the insurer. A repeat customer can still hold as many policies as they like.',
          },
          {
            title: 'Larger documents upload without failing',
            detail:
              'The server-side limit now matches the 25 MB gateway ceiling instead of cutting off well below it.',
          },
          {
            title: 'Policies group under a single contact',
            detail: 'Policies belonging to one contact were being listed as separate contacts.',
          },
          {
            title: 'Product mapping corrected',
            detail:
              'Passenger-carrying vehicle products were mapped to the goods-carrying subgroup, which affected the commission rate applied.',
          },
        ],
      },
      {
        tag: '04',
        heading: 'Accounts & access',
        blurb: 'Logins for the people around the broker, and clearer control over who sees what.',
        changes: [
          {
            title: 'POSP and customer portals',
            detail:
              'Separate sign-in for POSPs and end customers. A POSP sees only their own policies, commissions and payouts; a customer sees only their own policies, claims and documents.',
          },
          {
            title: 'Rights Management lists everyone',
            detail:
              'POSP, Advertiser, Employee and Consolidator users all appear, with their rights manageable in one place.',
          },
          {
            title: 'Channel users can sign in',
            detail:
              'Channels are created with a login and are required to set their own password the first time they sign in, so the starting password cannot stay in use.',
          },
          {
            title: 'Channel type can be edited',
            detail:
              'Changing the type of an existing channel now saves, without disturbing any POSP record already attached to it.',
          },
          {
            title: 'Data isolation strengthened',
            detail:
              'Several areas checked permissions when reading records but not when writing them. Claims, claim documents and customer records now apply one shared rule in both directions.',
          },
        ],
      },
      {
        tag: '05',
        heading: 'Everyday use',
        blurb: 'Smaller changes that show up constantly.',
        changes: [
          {
            title: 'One busy screen no longer locks you out',
            detail:
              'Rate limiting is split into a short burst allowance and a sustained one, so hitting the limit on a single screen stops that request rather than the whole application.',
          },
          {
            title: 'Consistent contact details',
            detail:
              'Email addresses are saved in lower case, phone numbers are corrected to ten digits (a leading +91, 91 or 0 is removed automatically), and stray spaces are trimmed before saving.',
          },
          {
            title: 'Dropdowns are searchable',
            detail: 'Long lists — insurers, products, RTOs — can be typed into instead of scrolled.',
          },
          {
            title: 'Back navigation',
            detail: 'Detail and form pages have a Back control that returns you to the list you came from.',
          },
        ],
      },
    ],
    footnote:
      'Two items from this round were investigated and needed no change: policies with future expiry dates were already showing correctly, and the Total Premium column was already displaying the premium amount.',
  },
];

const LATEST = RELEASES[0];

function ReleaseSections({ sections }: { sections: Section[] }) {
  return (
    <>
      {sections.map((section) => (
        <section key={section.tag} className="mb-16 last:mb-0">
          <div className="grid gap-10 lg:grid-cols-[15rem_1fr] lg:gap-16">
            {/* sticky section label */}
            <div className="lg:sticky lg:top-12 lg:self-start">
              <span className="font-mono text-xs tracking-[0.22em] text-primary-600 dark:text-primary-400">
                {section.tag}
              </span>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight">{section.heading}</h3>
              <p className="mt-3 text-sm leading-relaxed text-secondary-600 dark:text-secondary-400">
                {section.blurb}
              </p>
            </div>

            {/* entries, hung off a single rule */}
            <ul className="border-l border-secondary-200 dark:border-secondary-800">
              {section.changes.map((c) => (
                <li key={c.title} className="group relative pb-9 pl-7 last:pb-0">
                  <span
                    aria-hidden="true"
                    className="absolute -left-[4.5px] top-2 h-2 w-2 rounded-full bg-secondary-300 transition-colors duration-200 group-hover:bg-primary-500 dark:bg-secondary-700 dark:group-hover:bg-primary-400"
                  />
                  <h4 className="text-base font-semibold leading-snug">{c.title}</h4>
                  <p className="mt-2 text-[0.9375rem] leading-relaxed text-secondary-600 dark:text-secondary-400">
                    {c.detail}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}
    </>
  );
}

export function WhatsNew() {
  return (
    <div className="min-h-screen bg-white dark:bg-secondary-950 text-secondary-900 dark:text-secondary-100">
      <style>{`
        @keyframes wn-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .wn-rise { animation: wn-rise .7s cubic-bezier(.16,.84,.44,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .wn-rise { animation: none; }
        }
      `}</style>

      {/* ---------------------------------------------------------------- hero */}
      <header className="relative overflow-hidden bg-secondary-950 text-white">
        {/* blueprint grid — records and ledgers, not generic SaaS gradient */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgb(148 163 184 / .4) 1px, transparent 1px), linear-gradient(to bottom, rgb(148 163 184 / .4) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 30% 0%, black 40%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 30% 0%, black 40%, transparent 100%)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/4 h-[32rem] w-[32rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgb(37 99 235 / .35), transparent 65%)' }}
        />

        <div className="relative mx-auto max-w-5xl px-6 pb-24 pt-20 sm:pt-28 lg:px-8">
          <p
            className="wn-rise font-mono text-xs uppercase tracking-[0.28em] text-primary-300"
            style={{ animationDelay: '0ms' }}
          >
            {LATEST.tag} · latest
          </p>

          <h1
            className="wn-rise mt-7 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight sm:text-6xl"
            style={{ animationDelay: '90ms' }}
          >
            {LATEST.headline.lead}
            <br />
            <span className="text-primary-400">{LATEST.headline.accent}</span>
          </h1>

          <p
            className="wn-rise mt-7 max-w-2xl text-lg leading-relaxed text-secondary-300"
            style={{ animationDelay: '180ms' }}
          >
            {LATEST.summary}
          </p>

          <p
            className="wn-rise mt-8 font-mono text-sm text-secondary-400"
            style={{ animationDelay: '250ms' }}
          >
            {LATEST.date} · base.Policy.online
          </p>
        </div>

        {/* stat band — real numbers are the point of a changelog for a money product */}
        <div className="relative border-t border-white/10 bg-black/25 backdrop-blur-sm">
          <dl className="mx-auto grid max-w-5xl grid-cols-2 gap-px px-6 lg:grid-cols-4 lg:px-8">
            {LATEST.stats.map((s, i) => (
              <div key={s.label} className="wn-rise py-8 pr-6" style={{ animationDelay: `${320 + i * 70}ms` }}>
                <dt className="font-mono text-3xl font-medium tabular-nums text-white sm:text-4xl">
                  {s.value}
                </dt>
                <dd className="mt-2 text-sm font-medium text-secondary-200">{s.label}</dd>
                <dd className="mt-1 text-xs leading-relaxed text-secondary-400">{s.note}</dd>
              </div>
            ))}
          </dl>
        </div>
      </header>

      {/* ------------------------------------------------------------ changelog */}
      <main className="mx-auto max-w-5xl px-6 py-20 lg:px-8">
        {RELEASES.map((release, index) => (
          <article
            key={release.tag}
            className={
              index === 0
                ? ''
                : 'mt-24 border-t border-secondary-200 pt-16 dark:border-secondary-800'
            }
          >
            {/* release header — every entry is dated, so the history is self-describing */}
            <div className="mb-12">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="font-mono text-xs uppercase tracking-[0.22em] text-primary-600 dark:text-primary-400">
                  {release.tag}
                </span>
                <span className="font-mono text-xs text-secondary-500 dark:text-secondary-500">
                  {release.date}
                </span>
                {index === 0 && (
                  <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-primary-700 dark:bg-primary-950 dark:text-primary-300">
                    Latest
                  </span>
                )}
              </div>
              {index > 0 && (
                <p className="mt-4 max-w-2xl text-[0.9375rem] leading-relaxed text-secondary-600 dark:text-secondary-400">
                  {release.summary}
                </p>
              )}
            </div>

            <ReleaseSections sections={release.sections} />

            {release.footnote && (
              <p className="mt-14 max-w-2xl border-l-2 border-secondary-200 pl-5 text-sm leading-relaxed text-secondary-500 dark:border-secondary-800 dark:text-secondary-400">
                {release.footnote}
              </p>
            )}
          </article>
        ))}

        {/* ------------------------------------------------------------ footer */}
        <footer className="mt-24 border-t border-secondary-200 pt-10 dark:border-secondary-800">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-sm">
            <Link
              to="/brokers-directory"
              className="text-primary-600 underline-offset-4 transition-colors hover:underline dark:text-primary-400"
            >
              Brokers directory
            </Link>
            <Link
              to="/insurers"
              className="text-primary-600 underline-offset-4 transition-colors hover:underline dark:text-primary-400"
            >
              Insurance companies
            </Link>
            <Link
              to="/login"
              className="text-primary-600 underline-offset-4 transition-colors hover:underline dark:text-primary-400"
            >
              Sign in
            </Link>
          </div>

          <p className="mt-10 text-xs text-secondary-500 dark:text-secondary-500">
            base.Policy.online · {RELEASES.length} releases · updated {LATEST.date}
          </p>
        </footer>
      </main>
    </div>
  );
}
